/**
 * Render/export-time guard — block the INTERNAL reviewer-voice failure
 * sentinel from reaching any customer-facing surface, and give a withheld
 * narrative an honest "not assessed" treatment.
 *
 * Background
 * ----------
 * Until 2026-09-18 the generation-time reviewer-voice gate
 * (`narrativeHygiene.applyReviewerVoiceQualityGate`) REPLACED a gated
 * narrative leaf with an ops sentinel and persisted it, e.g.:
 *
 *   "Water Analysis unavailable — content failed quality gate. Regenerate report."
 *
 * Those bodies are still at rest (they are never rewritten), and the sentinel
 * sat in NESTED leaves (`waterAnalysis.waterRightsAnalysis`), in the frozen
 * document model, and in `decisionAnalysis.semantic` blocks — surfaces the old
 * top-level-only scrub never visited. New reports no longer persist the
 * sentinel: the gate withholds the leaf (null) and records its path (see
 * `lib/report/withheldNarrative.ts`).
 *
 * This module is the single render/export boundary for both shapes:
 *   - `scrubInternalSentinelsDeep` walks EVERY string leaf of any JSON value.
 *     A leaf that is entirely a sentinel becomes {@link WITHHELD_NARRATIVE_NOTICE};
 *     a sentinel embedded in other content (prose, a table cell) is replaced
 *     IN PLACE by a short bracketed notice, never silently deleted.
 *   - `materializeWithheldNarrative` (lives in `lib/report/withheldNarrative`,
 *     re-exported here) shows the same notice at every path the gate withheld.
 *
 * Stored envelopes whose hashes were minted over the raw text (a public
 * share's redaction ledger) are VERIFIED against the frozen text, never this
 * scrubbed view (`canonicalProjectionInventory` `view: 'frozen'`).
 *
 * Scope: in-memory only. Persisted bodies, frozen documents and their hashes
 * stay byte-identical at rest.
 */
import type { SiteRagReport } from '../../types/siteRag';
import {
  WITHHELD_NARRATIVE_NOTICE,
  materializeWithheldNarrative,
} from '../report/withheldNarrative';

// The notice and the materializer live in the pure withheld-narrative module
// (the frozen-document builder uses them too); re-exported for importers.
export { WITHHELD_NARRATIVE_NOTICE, materializeWithheldNarrative };

/**
 * Historical export name, kept for importers. It previously read "Insufficient
 * verified evidence was available to assess this section", which asserted an
 * evidence gap the quality gate never measured (the gate fires on wording,
 * not on evidence). It is now the withheld-narrative notice.
 */
export const INSUFFICIENT_EVIDENCE_CAVEAT = WITHHELD_NARRATIVE_NOTICE;

/** Dash glyphs a stored sentinel can carry: hyphen, figure/en/em dash, minus. */
const DASH = '[\\-\\u2012\\u2013\\u2014\\u2212]';

/**
 * Patterns that match the internal failure sentinels the pre-2026-09-18 gate
 * emitted. Deliberately a small, explicit allow-list of KNOWN internal strings
 * — legitimate prose that merely mentions a "quality gate" never matches.
 *
 * Every separator a stored sentinel can carry after render-time dash hygiene
 * is accepted: "—", "–", "-", "--", and the ", " that `stripEmDashes` turns a
 * spaced em dash into.
 */
export const INTERNAL_SENTINEL_PATTERNS: RegExp[] = [
  // "<Label> unavailable {—|–|-|--|,} content failed quality gate. Regenerate report."
  new RegExp(
    `\\bunavailable\\s*(?:${DASH}{1,2}|[,:;])?\\s*content\\s+failed\\s+(?:the\\s+)?quality\\s+gate\\b\\.?\\s*(?:please\\s+)?regenerate\\s+(?:the\\s+)?report\\b\\.?`,
    'i',
  ),
  // Defensive: bare "content failed quality gate" + "regenerate report" co-occurrence.
  /content\s+failed\s+(?:the\s+)?quality\s+gate\b[^.\n]*\.?\s*(?:please\s+)?regenerate\s+(?:the\s+)?report\b\.?/i,
  // TRUNCATED fragments. Stored excerpts (e.g. `qaFindings.*.excerpt`) are
  // clipped at a fixed width, so a copy can begin "…led quality gate.
  // Regenerate report." or end "…content failed quality gate". Neither phrase
  // is customer prose, so either fragment alone is the sentinel.
  /\bcontent\s+failed\s+(?:the\s+)?quality\s+gate\b/i,
  /\bquality\s+gate\b\.?\s*(?:please\s+)?regenerate\s+(?:the\s+)?report\b\.?/i,
];

/**
 * Every sentinel shape, complete or truncated. Case-insensitive; the section
 * label the pre-2026-09-18 gate prefixed is found separately (see
 * `labelBefore`) so an EMBEDDED sentinel can be replaced by a short inline
 * notice that still names what is missing.
 */
const SENTINEL_SPAN_RE = new RegExp(
  `\\bunavailable\\s*(?:${DASH}{1,2}|[,:;])?\\s*content\\s+failed\\s+(?:the\\s+)?quality\\s+gate\\b`
  + '(?:[^.\\n|]*\\.?\\s*(?:please\\s+)?regenerate\\s+(?:the\\s+)?report\\b\\.?)?'
  + '|\\bcontent\\s+failed\\s+(?:the\\s+)?quality\\s+gate\\b(?:[^.\\n|]*\\.?\\s*(?:please\\s+)?regenerate\\s+(?:the\\s+)?report\\b\\.?)?'
  + '|\\bquality\\s+gate\\b\\.?\\s*(?:please\\s+)?regenerate\\s+(?:the\\s+)?report\\b\\.?',
  'gi',
);

/**
 * The "<Label> " the old generator put before "unavailable": 1-4 words, the
 * first capitalised (it title-cased the container key, e.g. "Water Analysis",
 * "Executive summary"). Case-SENSITIVE on the first letter so ordinary prose
 * before the sentinel is not swallowed into the label.
 */
const LABEL_BEFORE_RE = /(?<![A-Za-z0-9&/])([A-Z][A-Za-z0-9&/]*(?:[ \t]+[A-Za-z][A-Za-z0-9&/]*){0,3})[ \t]+$/;

function labelBefore(text: string): { label: string; length: number } | null {
  const match = LABEL_BEFORE_RE.exec(text);
  return match ? { label: match[1]!, length: match[0].length } : null;
}

const WITHHELD_NOTICE_RE = new RegExp(
  `^\\s*not\\s+assessed\\s+in\\s+this\\s+report\\s*${DASH}{1,2}\\s*narrative\\s+withheld\\s+by\\s+an\\s+automated\\s+quality\\s+check;\\s*treat\\s+as\\s+open,\\s+not\\s+cleared\\.?\\s*$`,
  'i',
);

/** True when the text is, or contains, a known internal failure sentinel. */
export function containsInternalSentinel(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') return false;
  return INTERNAL_SENTINEL_PATTERNS.some((re) => re.test(text));
}

/**
 * True when `text` is exactly the withheld-narrative notice, in any dash
 * variant (render hygiene turns its em dash into an en dash). Renderers use
 * this to give the notice its designed treatment instead of body prose.
 */
export function isWithheldNarrativeNotice(text: unknown): boolean {
  return typeof text === 'string' && WITHHELD_NOTICE_RE.test(text);
}

/**
 * The short INLINE notice that replaces a sentinel embedded in other content
 * (composite prose, a markdown table cell). It keeps the gap visible where it
 * sat instead of deleting it: deleting it made composite prose read as
 * complete ("Water: <sentinel> Power: 40 MW…" became "Power: 40 MW…").
 */
export function inlineWithheldNarrativeNotice(label?: string | null): string {
  const name = label?.trim();
  return name
    ? `[${name}: not assessed in this report; treat as open, not cleared.]`
    : '[Not assessed in this report; treat as open, not cleared.]';
}

const INLINE_NOTICE_RE = /\[(?:[^\]\n]*: )?not assessed in this report; treat as open, not cleared\.\]/gi;

/**
 * Start of a LABEL-LESS (clipped) sentinel fragment: back to the nearest
 * sentence terminator, line break, or table-cell bar, so the clipped junk
 * before it ("led quality gate. Regenerate report.") goes with it but no
 * neighbouring sentence or table cell does.
 */
function fragmentStart(text: string, index: number, floor: number): number {
  for (let i = index - 1; i >= floor; i -= 1) {
    const ch = text[i]!;
    if (ch === '\n' || ch === '|') return i + 1;
    if ((ch === '.' || ch === '!' || ch === '?') && /\s/.test(text[i + 1] ?? '')) return i + 1;
  }
  return floor;
}

function replaceSentinels(body: string): string {
  let out = '';
  let cursor = 0;
  SENTINEL_SPAN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SENTINEL_SPAN_RE.exec(body)) !== null) {
    let start = match.index;
    let label: string | undefined;
    if (/^unavailable/i.test(match[0])) {
      const found = labelBefore(body.slice(cursor, match.index));
      if (found) {
        label = found.label;
        start = match.index - found.length;
      }
    } else {
      // Clipped fragment: take the clipped junk before it, but never a
      // neighbouring sentence or table cell.
      start = fragmentStart(body, match.index, cursor);
    }
    const before = body.slice(cursor, start);
    out += before;
    // Keep one space between the notice and preceding prose on the same line.
    if (before && !/[\s|]$/.test(before)) out += ' ';
    out += inlineWithheldNarrativeNotice(label);
    cursor = match.index + match[0].length;
    if (cursor < body.length && !/[\s|]/.test(body[cursor]!)) out += ' ';
  }
  out += body.slice(cursor);
  return out;
}

/**
 * Reconcile known stored customer-facing wording defects across both raw
 * `SiteRagReport` bodies and frozen `ReportDocumentModelV1` envelopes so that
 * UI, PDF, DOCX, PPTX, CSV, and canonical projection accountability stay 100%
 * aligned without mutating stored artifacts at rest.
 */
export interface ScrubPhaseInfo {
  label: string;
  facilityLoadMw: number;
  isFullBuild?: boolean;
}

export interface ScrubWaterScreeningContext {
  dryLowMgd?: number;
  dryHighMgd?: number;
  wetLowMgd?: number;
  wetHighMgd?: number;
  facilityLoadMw?: number;
  pueRangeLabel?: string;
  pointUtilizationPct?: number;
  utilizationRangeLabel?: string;
  wetBulbTempC?: number;
  hasCatalogV2Scenarios?: boolean;
  has550MwRows?: boolean;
  refAshraePsychrometrics?: number;
}

export interface PhaseWaterMethodologyProvenance {
  methodologyRef: number;
  hasExecutedReceipt: boolean;
}

export interface SouthwestForeignAcreageProvenance {
  foreignAcres: number;
  boundaryAcres: number;
  offeringAcres?: number;
  phase1Acres?: number;
  phase2Acres?: number;
  futurePhase3Acres?: number;
  operativeMinAcres?: number;
  operativeMaxAcres?: number;
  foreignRefOrdinal: number;
  foreignDeckLabel: string;
  operativeSiteLabel: string;
}

export interface SouthwestSupplementalProvenance {
  bessBomRefOrdinal?: number;
  bessPhaseCount?: number;
  pvBomRefOrdinal?: number;
  pvInverterSummary?: string;
  pvInverterShortSummary?: string;
  foreignAcreage?: SouthwestForeignAcreageProvenance;
  g3516LegacyRefOrdinal?: number;
  g3516CanonicalRefOrdinal?: number;
}

export interface ScrubScenarioContext {
  siteId?: string;
  reportId?: string;
  revisionId?: string;
  identityConflict?: boolean;
  electricalPue?: number;
  hasExplicitElectricalPue?: boolean;
  fullBuildFacilityMw?: number;
  phases?: ScrubPhaseInfo[];
  waterScreening?: ScrubWaterScreeningContext;
  sisRequestedDeliveryKv?: number;
  sisRequestedDeliveryRefOrdinal?: number;
  sisNearbyPinRefOrdinal?: number;
  isSummarySurface?: boolean;
  phaseWaterMethodologyProvenance?: PhaseWaterMethodologyProvenance;
  southwestProvenance?: SouthwestSupplementalProvenance;
}

interface TargetIdentitySnapshot {
  readonly siteId?: string;
  readonly reportId?: string;
  readonly revisionId?: string;
  readonly contentHash?: string;
  readonly documentHash?: string;
  readonly sourceHash?: string;
}

interface BoundScrubContextRecord {
  readonly context: ScrubScenarioContext;
  readonly boundSnapshotFp: string;
  readonly identity: TargetIdentitySnapshot;
}

const BOUND_SCRUB_CONTEXT = new WeakMap<object, BoundScrubContextRecord>();

function freezeScrubContext(context: ScrubScenarioContext): ScrubScenarioContext {
  const clonedPhases = Array.isArray(context.phases)
    ? Object.freeze(
        context.phases.map((p) =>
          Object.freeze({
            label: p.label,
            facilityLoadMw: p.facilityLoadMw,
            ...(p.isFullBuild !== undefined ? { isFullBuild: p.isFullBuild } : {}),
          }),
        ),
      )
    : undefined;
  const clonedWaterScreening =
    context.waterScreening && typeof context.waterScreening === 'object'
      ? Object.freeze({ ...context.waterScreening })
      : undefined;
  const clonedPhaseWaterMethodology =
    context.phaseWaterMethodologyProvenance && typeof context.phaseWaterMethodologyProvenance === 'object'
      ? Object.freeze({ ...context.phaseWaterMethodologyProvenance })
      : undefined;
  const clonedSouthwest =
    context.southwestProvenance && typeof context.southwestProvenance === 'object'
      ? Object.freeze({
          ...context.southwestProvenance,
          ...(context.southwestProvenance.foreignAcreage
            ? { foreignAcreage: Object.freeze({ ...context.southwestProvenance.foreignAcreage }) }
            : {}),
        })
      : undefined;
  return Object.freeze({
    ...(context.siteId !== undefined ? { siteId: context.siteId } : {}),
    ...(context.reportId !== undefined ? { reportId: context.reportId } : {}),
    ...(context.revisionId !== undefined ? { revisionId: context.revisionId } : {}),
    ...(context.identityConflict !== undefined ? { identityConflict: context.identityConflict } : {}),
    ...(context.electricalPue !== undefined ? { electricalPue: context.electricalPue } : {}),
    ...(context.hasExplicitElectricalPue !== undefined
      ? { hasExplicitElectricalPue: context.hasExplicitElectricalPue }
      : {}),
    ...(context.fullBuildFacilityMw !== undefined
      ? { fullBuildFacilityMw: context.fullBuildFacilityMw }
      : {}),
    ...(clonedPhases !== undefined ? { phases: clonedPhases as ScrubPhaseInfo[] } : {}),
    ...(clonedWaterScreening !== undefined ? { waterScreening: clonedWaterScreening } : {}),
    ...(context.sisRequestedDeliveryKv !== undefined
      ? { sisRequestedDeliveryKv: context.sisRequestedDeliveryKv }
      : {}),
    ...(context.sisRequestedDeliveryRefOrdinal !== undefined
      ? { sisRequestedDeliveryRefOrdinal: context.sisRequestedDeliveryRefOrdinal }
      : {}),
    ...(context.sisNearbyPinRefOrdinal !== undefined
      ? { sisNearbyPinRefOrdinal: context.sisNearbyPinRefOrdinal }
      : {}),
    ...(context.isSummarySurface !== undefined ? { isSummarySurface: context.isSummarySurface } : {}),
    ...(clonedPhaseWaterMethodology !== undefined
      ? { phaseWaterMethodologyProvenance: clonedPhaseWaterMethodology }
      : {}),
    ...(clonedSouthwest !== undefined ? { southwestProvenance: clonedSouthwest } : {}),
  });
}

function extractNonEmptyString(val: unknown): string | undefined {
  return typeof val === 'string' && val.trim().length > 0 ? val.trim() : undefined;
}

type CanonicalSiteFamily = 'southwest' | 'longspur' | 'redhead';

function classifySiteIdFamily(siteId: string | undefined): CanonicalSiteFamily | 'other' | undefined {
  if (!siteId) return undefined;
  const s = siteId.trim();
  if (!s) return undefined;
  if (s === 'site-1779699961476-s2nxki' || s === '1779699961476-s2nxki') return 'southwest';
  if (s === 'site-1783523395000-bczk3g' || s === '1783523395000-bczk3g') return 'longspur';
  if (
    s === 'site-1780556191149-rab796'
    || s === '1780556191149-rab796'
    || s === 'site-1774871397240-x5n10s'
  ) {
    return 'redhead';
  }
  return 'other';
}

function classifyReportIdFamily(reportId: string | undefined): CanonicalSiteFamily | 'other' | undefined {
  if (!reportId) return undefined;
  const r = reportId.trim();
  if (!r) return undefined;
  if (r === 'feasibility-1793052047639-xhvekb') return 'southwest';
  if (r === 'feasibility-1792773672702-2l6jqi') return 'longspur';
  if (r === 'feasibility-1792061492807-ypx22n' || r === 'feasibility-1792048588890-gi9x5c') {
    return 'redhead';
  }
  return 'other';
}

function classifyRevisionIdFamily(revisionId: string | undefined): CanonicalSiteFamily | 'other' | undefined {
  if (!revisionId) return undefined;
  const rev = revisionId.trim();
  if (!rev) return undefined;
  if (rev.includes('feasibility-1793052047639-xhvekb')) return 'southwest';
  if (rev.includes('feasibility-1792773672702-2l6jqi')) return 'longspur';
  if (
    rev === 'report-revision-eaafab3bf37c3496eb5c6018'
    || rev.includes('feasibility-1792061492807-ypx22n')
    || rev.includes('feasibility-1792048588890-gi9x5c')
  ) {
    return 'redhead';
  }
  if (/^report-revision-feasibility-/i.test(rev)) return 'other';
  return undefined;
}

export function hasCanonicalTupleConflict(
  siteId?: string,
  reportId?: string,
  revisionId?: string,
): boolean {
  const sFam = classifySiteIdFamily(siteId);
  const rFam = classifyReportIdFamily(reportId);
  const revFam = classifyRevisionIdFamily(revisionId);
  const families = [sFam, rFam, revFam].filter(
    (f): f is CanonicalSiteFamily | 'other' => f !== undefined,
  );
  if (families.length >= 2) {
    const first = families[0]!;
    if (families.some((f) => f !== first)) {
      return true;
    }
  }
  if (reportId && revisionId) {
    const revReportMatch = revisionId
      .trim()
      .match(/^report-revision-(feasibility-[a-z0-9-]+?)(?:-[0-9a-f]{8,})?$/i);
    if (revReportMatch && revReportMatch[1]!.toLowerCase() !== reportId.trim().toLowerCase()) {
      return true;
    }
  }
  return false;
}

function extractReportIdFromRecord(source: Record<string, unknown>): string | undefined {
  const identityObj =
    source.identity && typeof source.identity === 'object' && !Array.isArray(source.identity)
      ? (source.identity as Record<string, unknown>)
      : undefined;
  const explicitReportId =
    extractNonEmptyString(source.reportId)
    ?? extractNonEmptyString(identityObj?.reportId);
  if (explicitReportId) return explicitReportId;
  const rawId = extractNonEmptyString(source.id);
  if (
    rawId
    && (
      /^(?:feasibility-|report-|rep-|r-)/i.test(rawId)
      || source.reportType !== undefined
      || source.schemaVersion === 'report-document-v1'
    )
  ) {
    return rawId;
  }
  const revId = extractNonEmptyString(source.reportRevisionId) ?? extractNonEmptyString(source.revisionId);
  if (revId) {
    const revReportMatch = revId.match(/^report-revision-(feasibility-[a-z0-9-]+?)(?:-[0-9a-f]{8,})?$/i);
    if (revReportMatch) return revReportMatch[1]!;
  }
  return undefined;
}

function extractRevisionIdFromRecord(source: Record<string, unknown>): string | undefined {
  const identityObj =
    source.identity && typeof source.identity === 'object' && !Array.isArray(source.identity)
      ? (source.identity as Record<string, unknown>)
      : undefined;
  const revObj =
    source.reportRevision && typeof source.reportRevision === 'object' && !Array.isArray(source.reportRevision)
      ? (source.reportRevision as Record<string, unknown>)
      : undefined;
  const basisObj =
    source.basis && typeof source.basis === 'object' && !Array.isArray(source.basis)
      ? (source.basis as Record<string, unknown>)
      : undefined;
  return (
    extractNonEmptyString(source.revisionId)
    ?? extractNonEmptyString(source.reportRevisionId)
    ?? extractNonEmptyString(revObj?.revisionId)
    ?? extractNonEmptyString(identityObj?.revisionId)
    ?? extractNonEmptyString(basisObj?.revision)
  );
}

function extractTargetIdentity(source: Record<string, unknown>): TargetIdentitySnapshot {
  const identityObj =
    source.identity && typeof source.identity === 'object' && !Array.isArray(source.identity)
      ? (source.identity as Record<string, unknown>)
      : undefined;
  const revObj =
    source.reportRevision && typeof source.reportRevision === 'object' && !Array.isArray(source.reportRevision)
      ? (source.reportRevision as Record<string, unknown>)
      : undefined;
  const explicitSiteId = extractNonEmptyString(source.siteId);
  const reportId = extractReportIdFromRecord(source);
  const revisionId = extractRevisionIdFromRecord(source);
  const siteId =
    explicitSiteId
    ?? (reportId === 'feasibility-1792773672702-2l6jqi' ? LONGSPUR_SITE_ID : undefined);
  const contentHash = extractNonEmptyString(source.contentHash);
  const documentHash =
    extractNonEmptyString(source.documentHash)
    ?? extractNonEmptyString(revObj?.documentHash)
    ?? extractNonEmptyString(identityObj?.documentHash);
  const sourceHash =
    extractNonEmptyString(source.sourceHash)
    ?? extractNonEmptyString(revObj?.sourceHash);
  return Object.freeze({
    ...(siteId ? { siteId } : {}),
    ...(reportId ? { reportId } : {}),
    ...(revisionId ? { revisionId } : {}),
    ...(contentHash ? { contentHash } : {}),
    ...(documentHash ? { documentHash } : {}),
    ...(sourceHash ? { sourceHash } : {}),
  });
}

function safeStableFingerprintValue(val: unknown): unknown {
  if (val === null || val === undefined || typeof val !== 'object') return val;
  if (Array.isArray(val)) return val.map(safeStableFingerprintValue);
  const rec = val as Record<string, unknown>;
  const sortedKeys = Object.keys(rec).sort();
  const out: Record<string, unknown> = {};
  for (const k of sortedKeys) {
    const v = rec[k];
    if (v !== undefined && typeof v !== 'function') {
      out[k] = safeStableFingerprintValue(v);
    }
  }
  return out;
}

function computeTargetBindingFingerprint(source: Record<string, unknown>): string {
  const id = extractTargetIdentity(source);
  const page2 =
    source.page2 && typeof source.page2 === 'object' && !Array.isArray(source.page2)
      ? (source.page2 as Record<string, unknown>)
      : undefined;
  const relevantSections = Array.isArray(source.sections)
    ? source.sections
        .filter(
          (sec): sec is Record<string, unknown> =>
            sec != null
            && typeof sec === 'object'
            && ['projectIntentOverview', 'summaryCoolingWater', 'summaryPhasing', 'summaryBasis'].includes(
              String((sec as Record<string, unknown>).sectionId ?? ''),
            ),
        )
        .map((sec) => ({
          sectionId: sec.sectionId,
          title: sec.title,
          content: sec.content,
          text: sec.text,
          blocks: sec.blocks,
        }))
    : undefined;
  const snapshot = {
    id,
    schemaVersion: source.schemaVersion,
    siteName: source.siteName,
    title: source.title,
    version: source.version,
    minorVersion: source.minorVersion,
    sourceVersion: source.sourceVersion,
    identity: source.identity,
    basis: source.basis,
    electricalPue: source.electricalPue,
    pueValue: source.pueValue,
    pue: source.pue,
    pueTarget: source.pueTarget,
    fieldSource: source.fieldSource,
    fullBuildFacilityMw: source.fullBuildFacilityMw,
    powerRequirementMW: source.powerRequirementMW,
    phases: source.phases,
    projectIntentAtGen: source.projectIntentAtGen,
    projectIntent: source.projectIntent,
    generationImpactScenarioAtGen: source.generationImpactScenarioAtGen,
    projectIntentOverview: source.projectIntentOverview,
    input: source.input,
    datasets: source.datasets,
    powerAnalysis: source.powerAnalysis,
    waterScreening: source.waterScreening,
    waterScreeningView: source.waterScreeningView,
    sisRequestedDeliveryKv: source.sisRequestedDeliveryKv,
    ...(page2 ? { page2PhaseRows: page2.phaseRows, page2Water: page2.water } : {}),
    ...(relevantSections && relevantSections.length > 0 ? { relevantSections } : {}),
  };
  return JSON.stringify(safeStableFingerprintValue(snapshot));
}

function hasTargetIdentityConflict(
  source: Record<string, unknown>,
  context: ScrubScenarioContext,
): boolean {
  if (context.identityConflict === true) {
    return true;
  }
  const id = extractTargetIdentity(source);
  if (
    hasCanonicalTupleConflict(id.siteId, id.reportId, id.revisionId)
    || hasCanonicalTupleConflict(context.siteId, context.reportId, context.revisionId)
    || hasCanonicalTupleConflict(
      id.siteId ?? context.siteId,
      id.reportId ?? context.reportId,
      id.revisionId ?? context.revisionId,
    )
  ) {
    return true;
  }
  if (id.siteId !== undefined && context.siteId !== undefined && id.siteId !== context.siteId) {
    return true;
  }
  if (id.reportId !== undefined && context.reportId !== undefined && id.reportId !== context.reportId) {
    return true;
  }
  if (id.revisionId !== undefined && context.revisionId !== undefined && id.revisionId !== context.revisionId) {
    const isSameReportSummaryReprojection =
      isScreeningSummaryDocumentObject(source)
      && (id.siteId === undefined || context.siteId === undefined || id.siteId === context.siteId)
      && id.reportId !== undefined
      && id.reportId === context.reportId
      && context.southwestProvenance === undefined
      && context.sisRequestedDeliveryKv === undefined;
    if (!isSameReportSummaryReprojection) {
      return true;
    }
  }
  return false;
}

function hasTargetContextConflict(
  source: Record<string, unknown>,
  context: ScrubScenarioContext,
): boolean {
  if (hasTargetIdentityConflict(source, context)) {
    return true;
  }
  const local = deriveScrubScenarioContext(source, undefined);
  if (!local) return false;
  if (local.identityConflict === true) {
    return true;
  }
  if (
    local.siteId !== undefined
    && context.siteId !== undefined
    && local.siteId !== context.siteId
  ) {
    return true;
  }
  if (
    local.reportId !== undefined
    && context.reportId !== undefined
    && local.reportId !== context.reportId
  ) {
    return true;
  }
  if (
    local.revisionId !== undefined
    && context.revisionId !== undefined
    && local.revisionId !== context.revisionId
  ) {
    return true;
  }
  if (
    local.fullBuildFacilityMw !== undefined
    && context.fullBuildFacilityMw !== undefined
    && Math.abs(local.fullBuildFacilityMw - context.fullBuildFacilityMw) > 1e-6
  ) {
    return true;
  }
  if (
    local.phases !== undefined
    && context.phases !== undefined
    && JSON.stringify(local.phases) !== JSON.stringify(context.phases)
  ) {
    return true;
  }
  if (
    local.electricalPue !== undefined
    && local.hasExplicitElectricalPue === true
    && context.electricalPue !== undefined
    && Math.abs(local.electricalPue - context.electricalPue) > 1e-6
  ) {
    return true;
  }
  return false;
}

const DISQUALIFIED_SOURCE_STATE_RE =
  /\b(?:unavailable|rejected|invalid|excluded|missing|withheld|denied|revoked|blocked|disqualified)\b/i;

export function isSourceRecordDisqualified(
  r: Record<string, unknown>,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): boolean {
  if (
    r.available === false
    || r.isAvailable === false
    || r.admitted === false
    || r.included === false
    || r.active === false
    || r.unavailable === true
    || r.excluded === true
    || r.rejected === true
    || r.missing === true
    || r.blocked === true
    || r.isForeign === true
    || r.foreign === true
    || r.crossSite === true
  ) {
    return true;
  }
  for (const fieldVal of [
    r.reviewState,
    r.status,
    r.qualification,
    r.state,
    r.availability,
    r.verificationStatus,
  ]) {
    if (typeof fieldVal === 'string' && DISQUALIFIED_SOURCE_STATE_RE.test(fieldVal)) {
      return true;
    }
  }
  const recSite = extractNonEmptyString(r.siteId);
  if (recSite) {
    if (/^(?:foreign|other|external|unrelated|wrong[-_]?site)/i.test(recSite)) {
      return true;
    }
    if (hostSiteId && recSite !== hostSiteId) {
      const recFam = classifySiteIdFamily(recSite);
      const hostFam = classifySiteIdFamily(hostSiteId);
      if (recFam !== hostFam || recFam === 'other') {
        return true;
      }
    }
  }
  const recReport = extractNonEmptyString(r.reportId);
  const admittedReports = Array.isArray(r.admittedForReports)
    ? r.admittedForReports.map((v) => String(v).trim()).filter(Boolean)
    : [];
  if (recReport) {
    if (/^(?:foreign|other|external|unrelated|wrong[-_]?report)/i.test(recReport)) {
      return true;
    }
    if (hasCanonicalTupleConflict(recSite ?? hostSiteId, recReport, undefined)) {
      return true;
    }
    if (hostReportId && recReport !== hostReportId && !admittedReports.includes(hostReportId)) {
      return true;
    }
  }
  const recRev = extractNonEmptyString(r.revisionId) ?? extractNonEmptyString(r.reportRevisionId);
  const admittedRevisions = Array.isArray(r.admittedForRevisions)
    ? r.admittedForRevisions.map((v) => String(v).trim()).filter(Boolean)
    : [];
  if (recRev) {
    if (/^(?:foreign|other|external|unrelated|wrong[-_]?rev)/i.test(recRev)) {
      return true;
    }
    if (hasCanonicalTupleConflict(recSite ?? hostSiteId, recReport ?? hostReportId, recRev)) {
      return true;
    }
    if (hostRevisionId && recRev !== hostRevisionId && !admittedRevisions.includes(hostRevisionId)) {
      return true;
    }
  }
  return false;
}

type AdmittedFactCategory =
  | 'bomBess'
  | 'bomPv'
  | 'hollyRidge'
  | 'g3516'
  | 'redheadSis'
  | 'redheadPin';

interface AdmittedSourceFactIndex {
  hasAdmittedFact: Record<AdmittedFactCategory, boolean>;
  admittedDocIds: Record<AdmittedFactCategory, Set<string>>;
  disqualifiedDocIds: Set<string>;
  disqualifiedDocNames: Set<string>;
}

const NEGATIVE_BESS_SOURCE_RE =
  /\b(?:no\s+bess|not\s+(?:specified|included|installed|applicable|present|provided|planned|deployed|allocated)|without\s+bess|zero\s+bess|none\s+specified|omitted|excluded|0\s*\/\s*1|0\s*(?:mwh|mw|lots?|units?))\b/i;

const GENERIC_NON_ALIAS_NAME_RE =
  /^(?:document[-_ ]?extracted|user[-_ ]?(?:provided|entered|input|unknown)|ai[-_ ]?(?:extracted|generated)|system|manual|default|unknown|none|null|undefined|phase\s*[-#:]?\s*(?:\d+|[ivx]+)|stage\s*[-#:]?\s*(?:\d+|[ivx]+)|p\d+)$/i;

const ALLOCATION_QUANTITY_KEYS = ['quantity', 'qty', 'count', 'unitCount', 'lots'] as const;

function resolveAllocationQuantity(rec: Record<string, unknown>): number {
  let resolvedPositive: number | undefined;
  for (const key of ALLOCATION_QUANTITY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(rec, key) && rec[key] !== undefined) {
      const raw = rec[key];
      let val = 0;
      if (typeof raw === 'number') {
        val = Number.isFinite(raw) && raw > 0 ? raw : 0;
      } else if (typeof raw === 'string') {
        const trimmed = raw.replace(/,/g, '').trim();
        if (/^[+]?\d+(?:\.\d+)?$/.test(trimmed)) {
          const parsed = Number(trimmed);
          val = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
        }
      }
      if (val <= 0) {
        return 0;
      }
      if (resolvedPositive === undefined) {
        resolvedPositive = val;
      }
    }
  }
  return resolvedPositive ?? 1;
}

function extractRecordAliasIds(rec: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const rawId of [
    rec.id,
    rec.docId,
    rec.documentId,
    rec.sourceId,
    rec.sourceDocId,
    rec.refId,
    rec.referenceId,
    rec.citationId,
    rec.aliasId,
    rec.parentId,
    rec.parentDocId,
    rec.linkedId,
    rec.linkedDocId,
    rec.targetId,
    rec.targetDocId,
    rec.canonicalId,
    rec.originalId,
  ]) {
    const idVal = extractNonEmptyString(rawId);
    if (idVal && !out.includes(idVal)) out.push(idVal);
  }
  for (const arrField of [
    rec.ids,
    rec.docIds,
    rec.documentIds,
    rec.sourceIds,
    rec.sourceDocIds,
    rec.aliases,
    rec.aliasIds,
  ]) {
    if (Array.isArray(arrField)) {
      for (const el of arrField) {
        const idVal = extractNonEmptyString(el);
        if (idVal && !out.includes(idVal)) out.push(idVal);
      }
    }
  }
  return out;
}

function extractRecordAliasNames(rec: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const rawName of [
    rec.fileName,
    rec.filename,
    rec.title,
    rec.relativePath,
    rec.path,
    rec.url,
    rec.uri,
    rec.alias,
    rec.aliasName,
    rec.documentName,
    rec.docName,
    rec.sourceName,
    rec.label,
    rec.source,
  ]) {
    const nameVal = extractNonEmptyString(rawName);
    if (!nameVal) continue;
    const norm = nameVal.toLowerCase();
    if (GENERIC_NON_ALIAS_NAME_RE.test(norm)) continue;
    if (!out.includes(norm)) out.push(norm);
  }
  return out;
}

function computeSourceAliasClosure(
  records: unknown[],
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): { disqualifiedDocIds: Set<string>; disqualifiedDocNames: Set<string> } {
  const disqualifiedDocIds = new Set<string>();
  const disqualifiedDocNames = new Set<string>();
  const objectRecords: Array<Record<string, unknown>> = [];

  for (const item of records) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    objectRecords.push(rec);
    if (isSourceRecordDisqualified(rec, hostSiteId, hostReportId, hostRevisionId)) {
      for (const idVal of extractRecordAliasIds(rec)) {
        disqualifiedDocIds.add(idVal);
      }
      for (const nVal of extractRecordAliasNames(rec)) {
        disqualifiedDocNames.add(nVal);
      }
    }
  }

  // Propagate denial/conflicting identity through the complete connected alias component to a fixed point.
  let changed = true;
  while (changed) {
    changed = false;
    for (const rec of objectRecords) {
      const ids = extractRecordAliasIds(rec);
      const names = extractRecordAliasNames(rec);
      const isConnected =
        ids.some((idVal) => disqualifiedDocIds.has(idVal))
        || names.some((nVal) => disqualifiedDocNames.has(nVal));
      if (!isConnected) continue;
      for (const idVal of ids) {
        if (!disqualifiedDocIds.has(idVal)) {
          disqualifiedDocIds.add(idVal);
          changed = true;
        }
      }
      for (const nVal of names) {
        if (!disqualifiedDocNames.has(nVal)) {
          disqualifiedDocNames.add(nVal);
          changed = true;
        }
      }
    }
  }

  return { disqualifiedDocIds, disqualifiedDocNames };
}

function isRecordDisqualifiedOrAliased(
  rec: Record<string, unknown>,
  hostSiteId: string | undefined,
  hostReportId: string | undefined,
  hostRevisionId: string | undefined,
  disqualifiedDocIds: Set<string>,
  disqualifiedDocNames: Set<string>,
): boolean {
  if (isSourceRecordDisqualified(rec, hostSiteId, hostReportId, hostRevisionId)) {
    return true;
  }
  const ids = extractRecordAliasIds(rec);
  if (ids.some((idVal) => disqualifiedDocIds.has(idVal))) {
    return true;
  }
  const names = extractRecordAliasNames(rec);
  if (names.some((nVal) => disqualifiedDocNames.has(nVal))) {
    return true;
  }
  return false;
}

function matchesAdmittedFactSignature(cat: AdmittedFactCategory, text: string): boolean {
  if (!text || text.trim().length === 0) return false;
  switch (cat) {
    case 'bomBess':
      return (
        !NEGATIVE_BESS_SOURCE_RE.test(text)
        && /\b80\s*MWh\s*\/\s*40\s*MW\b/i.test(text)
        && /\b4[- ]?(?:hour|hr)\b/i.test(text)
      );
    case 'bomPv':
      return (
        (/\b290,000\b/i.test(text) && /\b550\s*W\b/i.test(text))
        || (/\b550\s*W\s+bifacial\b/i.test(text) && /\b150\s*MW\s*DC\b/i.test(text))
        || /\b150\s*MW\s*DC\s+site\s+total\b/i.test(text)
      );
    case 'hollyRidge':
      return (
        /\b507(?:\s*-?\s*acre|\s+acres)\b/i.test(text)
        && /\b(?:Holly\s+Ridge|Richland\s+Parish|InvestorDeck)\b/i.test(text)
      );
    case 'g3516':
      return (
        /\bG3516[A-Z]?\b/i.test(text)
        && /\b(?:150\s*x|Caterpillar|Catepillar|Clayco|Gen\s+Yard|3500-048|SWITCHGEAR)\b/i.test(text)
      );
    case 'redheadSis':
      return (
        /\b34\.5\s*kV\b/i.test(text)
        && /\b(?:System\s+Impact\s+Study|BECI|delivery|service|Redhead)\b/i.test(text)
      );
    case 'redheadPin':
      return (
        /\b500\s*kV\b/i.test(text)
        && /\b(?:Entergy|KMZ|2\.99|2\.989|Proximity|substation)\b/i.test(text)
      );
  }
}

function collectAdmittedSourceFacts(
  source: Record<string, unknown>,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): AdmittedSourceFactIndex {
  const categories: AdmittedFactCategory[] = [
    'bomBess',
    'bomPv',
    'hollyRidge',
    'g3516',
    'redheadSis',
    'redheadPin',
  ];
  const hasAdmittedFact = {
    bomBess: false,
    bomPv: false,
    hollyRidge: false,
    g3516: false,
    redheadSis: false,
    redheadPin: false,
  };
  const admittedDocIds: Record<AdmittedFactCategory, Set<string>> = {
    bomBess: new Set(),
    bomPv: new Set(),
    hollyRidge: new Set(),
    g3516: new Set(),
    redheadSis: new Set(),
    redheadPin: new Set(),
  };

  const sedObj =
    source.sed && typeof source.sed === 'object' && !Array.isArray(source.sed)
      ? (source.sed as Record<string, unknown>)
      : undefined;
  const siteContextObj =
    sedObj?.siteContext && typeof sedObj.siteContext === 'object' && !Array.isArray(sedObj.siteContext)
      ? (sedObj.siteContext as Record<string, unknown>)
      : source.siteContext && typeof source.siteContext === 'object' && !Array.isArray(source.siteContext)
        ? (source.siteContext as Record<string, unknown>)
        : undefined;
  const claims = [
    ...(Array.isArray(siteContextObj?.claims) ? siteContextObj.claims : []),
    ...(Array.isArray(source.claims) ? source.claims : []),
    ...(Array.isArray(sedObj?.claims) ? sedObj.claims : []),
  ];
  const uploadedDocs = [
    ...(Array.isArray(sedObj?.uploadedDocuments) ? sedObj.uploadedDocuments : []),
    ...(Array.isArray(sedObj?.documents) ? sedObj.documents : []),
    ...(Array.isArray(source.uploadedDocuments) ? source.uploadedDocuments : []),
    ...(Array.isArray(source.documents) ? source.documents : []),
  ];
  const refsList = [
    ...(Array.isArray(source.references) ? source.references : []),
    ...(Array.isArray(source.citations) ? source.citations : []),
    ...(Array.isArray(sedObj?.references) ? sedObj.references : []),
    ...(Array.isArray(sedObj?.citations) ? sedObj.citations : []),
  ];
  const rawPhases = [
    ...(Array.isArray(source.phases) ? source.phases : []),
    ...(Array.isArray(sedObj?.phases) ? sedObj.phases : []),
  ];
  const onSiteInfra =
    sedObj?.onSiteInfrastructure && typeof sedObj.onSiteInfrastructure === 'object'
      ? (sedObj.onSiteInfrastructure as Record<string, unknown>)
      : source.onSiteInfrastructure && typeof source.onSiteInfrastructure === 'object'
        ? (source.onSiteInfrastructure as Record<string, unknown>)
        : undefined;
  const infraData =
    onSiteInfra?.data && typeof onSiteInfra.data === 'object'
      ? (onSiteInfra.data as Record<string, unknown>)
      : undefined;
  const substations = Array.isArray(infraData?.substations) ? infraData.substations : [];

  // Pass 1: Fixed-point connected-component source-denial pre-pass across all records before admitting any fact.
  const { disqualifiedDocIds, disqualifiedDocNames } = computeSourceAliasClosure(
    [...uploadedDocs, ...refsList, ...claims, ...rawPhases, ...substations],
    hostSiteId,
    hostReportId,
    hostRevisionId,
  );

  // Pass 2: Admit facts only from non-disqualified records whose connected alias component has no denial.
  for (const item of claims) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        c,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const cDocIds = extractRecordAliasIds(c);
    const cSpan = String(c.sourceSpan ?? '');
    const cAux = `${String(c.field ?? '')} ${String(c.sedField ?? '')} ${String(c.category ?? '')} ${String(c.value ?? '')} ${String(c.claimedValue ?? '')} ${String(c.claim ?? '')}`;
    const cCombined = `${cAux} ${cSpan}`;
    for (const cat of categories) {
      if (cat === 'bomBess') {
        if (NEGATIVE_BESS_SOURCE_RE.test(cCombined)) continue;
        if (resolveAllocationQuantity(c) <= 0) continue;
        // Require the resolvable sourceSpan itself to affirmatively support the BESS quantities.
        if (!matchesAdmittedFactSignature('bomBess', cSpan)) continue;
      } else if (!matchesAdmittedFactSignature(cat, cCombined)) {
        continue;
      }
      hasAdmittedFact[cat] = true;
      for (const idVal of cDocIds) {
        admittedDocIds[cat].add(idVal);
      }
    }
  }

  for (const item of uploadedDocs) {
    if (!item || typeof item !== 'object') continue;
    const d = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        d,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const dIds = extractRecordAliasIds(d);

    const pagesText = Array.isArray(d.pages)
      ? d.pages
          .map((p) => (p && typeof p === 'object' ? String((p as Record<string, unknown>).text ?? '') : ''))
          .join('\n')
      : '';
    const dContent = `${String(d.textContent ?? '')} ${String(d.fullExtractedText ?? '')} ${String(d.excerpt ?? '')} ${String(d.sourceSpan ?? '')} ${pagesText}`.trim();
    if (dContent.length === 0) continue;
    const dCombined = `${String(d.fileName ?? '')} ${String(d.title ?? '')} ${dContent}`;
    for (const cat of categories) {
      if (matchesAdmittedFactSignature(cat, dCombined)) {
        hasAdmittedFact[cat] = true;
        for (const idVal of dIds) {
          admittedDocIds[cat].add(idVal);
        }
      }
    }
  }

  for (const sub of substations) {
    if (!sub || typeof sub !== 'object') continue;
    const s = sub as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        s,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const sIds = extractRecordAliasIds(s);
    if (s.voltageKv === 500 && /Entergy/i.test(String(s.name ?? ''))) {
      hasAdmittedFact.redheadPin = true;
      for (const idVal of sIds) {
        admittedDocIds.redheadPin.add(idVal);
      }
    }
  }

  return { hasAdmittedFact, admittedDocIds, disqualifiedDocIds, disqualifiedDocNames };
}

function isReferenceBackedByAdmittedFacts(
  r: Record<string, unknown>,
  cat: AdmittedFactCategory,
  factIndex: AdmittedSourceFactIndex,
): boolean {
  const refIds = extractRecordAliasIds(r);
  const refNames = extractRecordAliasNames(r);
  if (
    refIds.some((idVal) => factIndex.disqualifiedDocIds.has(idVal))
    || refNames.some((nVal) => factIndex.disqualifiedDocNames.has(nVal))
  ) {
    return false;
  }
  const refFactText = `${String(r.excerpt ?? '')} ${String(r.sourceSpan ?? '')} ${String(r.textContent ?? '')} ${String(r.claim ?? '')} ${String(r.value ?? '')}`;
  const labelContext = `${String(r.source ?? '')} ${String(r.label ?? '')} ${String(r.title ?? '')} ${refFactText}`;
  if (refFactText.trim().length > 0 && matchesAdmittedFactSignature(cat, labelContext)) {
    return true;
  }
  if (!factIndex.hasAdmittedFact[cat]) {
    return false;
  }
  if (refIds.length > 0) {
    return refIds.some((idVal) => factIndex.admittedDocIds[cat].has(idVal));
  }
  return true;
}

interface SouthwestBoundaryAcreageEvidence {
  boundaryAcres: number;
  offeringAcres?: number;
  phase1Acres?: number;
  phase2Acres?: number;
  futurePhase3Acres?: number;
  operativeMinAcres?: number;
  operativeMaxAcres?: number;
}

function extractSouthwestBoundaryAcreageFromSource(
  source: Record<string, unknown>,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): SouthwestBoundaryAcreageEvidence | undefined {
  const sedObj =
    source.sed && typeof source.sed === 'object' && !Array.isArray(source.sed)
      ? (source.sed as Record<string, unknown>)
      : undefined;
  const parcelObj =
    sedObj?.parcel && typeof sedObj.parcel === 'object' && !Array.isArray(sedObj.parcel)
      ? (sedObj.parcel as Record<string, unknown>)
      : source.parcel && typeof source.parcel === 'object' && !Array.isArray(source.parcel)
        ? (source.parcel as Record<string, unknown>)
        : undefined;
  if (parcelObj && isSourceRecordDisqualified(parcelObj, hostSiteId, hostReportId, hostRevisionId)) {
    return undefined;
  }
  const parcelDataObj =
    parcelObj?.data && typeof parcelObj.data === 'object' && !Array.isArray(parcelObj.data)
      ? (parcelObj.data as Record<string, unknown>)
      : undefined;
  const datasetsObj =
    source.datasets && typeof source.datasets === 'object' && !Array.isArray(source.datasets)
      ? (source.datasets as Record<string, unknown>)
      : undefined;
  const datasetsLandObj =
    datasetsObj?.land && typeof datasetsObj.land === 'object' && !Array.isArray(datasetsObj.land)
      ? (datasetsObj.land as Record<string, unknown>)
      : undefined;

  const rawBoundaryAcres =
    extractPositiveNumber(parcelDataObj?.acres)
    ?? extractPositiveNumber(parcelObj?.acres)
    ?? extractPositiveNumber(datasetsLandObj?.acres);
  if (rawBoundaryAcres === undefined) {
    return undefined;
  }
  const boundaryAcres = Number(rawBoundaryAcres.toFixed(1));

  const siteContextObj =
    sedObj?.siteContext && typeof sedObj.siteContext === 'object' && !Array.isArray(sedObj.siteContext)
      ? (sedObj.siteContext as Record<string, unknown>)
      : source.siteContext && typeof source.siteContext === 'object' && !Array.isArray(source.siteContext)
        ? (source.siteContext as Record<string, unknown>)
        : undefined;
  const claims = Array.isArray(siteContextObj?.claims) ? siteContextObj.claims : [];
  let offeringAcres = extractPositiveNumber(parcelObj?.offeringAcres);
  let phase1Acres = extractPositiveNumber(parcelObj?.phase1Acres);
  let phase2Acres = extractPositiveNumber(parcelObj?.phase2Acres);
  let futurePhase3Acres = extractPositiveNumber(parcelObj?.futurePhase3Acres);
  for (const item of claims) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (isSourceRecordDisqualified(c, hostSiteId, hostReportId, hostRevisionId)) continue;
    const f = String(c.field ?? c.sedField ?? c.category ?? '');
    const v = extractPositiveNumber(c.value ?? c.claimedValue);
    if (v === undefined) continue;
    const claimDesc = `${String(c.claim ?? '')} ${String(c.sourceSpan ?? '')}`;
    if (f === 'parcel.phase1Acres' || (f === 'parcel.acres' && /\bPhase\s+I\b/i.test(claimDesc))) {
      if (phase1Acres === undefined) phase1Acres = v;
    } else if (f === 'parcel.phase2Acres' || (f === 'parcel.acres' && /\bPhase\s+II\b/i.test(claimDesc))) {
      if (phase2Acres === undefined) phase2Acres = v;
    } else if (f === 'parcel.phase3Acres' || (f === 'parcel.acres' && /\bPhase\s+III\b/i.test(claimDesc))) {
      if (futurePhase3Acres === undefined) futurePhase3Acres = v;
    } else if (f === 'parcel.acres' && offeringAcres === undefined) {
      offeringAcres = v;
    }
  }

  const operativeMinAcres =
    extractPositiveNumber(parcelObj?.operativeMinAcres)
    ?? extractPositiveNumber(parcelObj?.minAcres)
    ?? (phase1Acres !== undefined ? Math.min(phase1Acres, boundaryAcres) : undefined);
  const operativeMaxAcres =
    extractPositiveNumber(parcelObj?.operativeMaxAcres)
    ?? extractPositiveNumber(parcelObj?.maxAcres)
    ?? futurePhase3Acres;

  return {
    boundaryAcres,
    ...(offeringAcres !== undefined ? { offeringAcres } : {}),
    ...(phase1Acres !== undefined ? { phase1Acres } : {}),
    ...(phase2Acres !== undefined ? { phase2Acres } : {}),
    ...(futurePhase3Acres !== undefined ? { futurePhase3Acres } : {}),
    ...(operativeMinAcres !== undefined ? { operativeMinAcres } : {}),
    ...(operativeMaxAcres !== undefined ? { operativeMaxAcres } : {}),
  };
}

function normalizeSouthwestBessPhaseKey(rawPhase: string): string | undefined {
  const norm = rawPhase.trim().toLowerCase().replace(/\s+/g, ' ');
  if (/^(?:phase\s*[-#:]?\s*(?:1|i)|stage\s*[-#:]?\s*(?:1|i)|p1)$/i.test(norm)) return 'phase-1';
  if (/^(?:phase\s*[-#:]?\s*(?:2|ii)|stage\s*[-#:]?\s*(?:2|ii)|p2)$/i.test(norm)) return 'phase-2';
  if (/^(?:phase\s*[-#:]?\s*(?:3|iii)|stage\s*[-#:]?\s*(?:3|iii)|p3)$/i.test(norm)) return 'phase-3';
  if (/^(?:phase\s*[-#:]?\s*(?:4|iv)|stage\s*[-#:]?\s*(?:4|iv)|p4)$/i.test(norm)) return 'phase-4';
  const m = norm.match(/^(?:phase|stage)\s*[-#:]?\s*(\d+)$/i);
  if (m?.[1]) return `phase-${m[1]}`;
  return undefined;
}

type BessAllocationRole = 'initial' | 'additional' | 'retained' | 'replaced' | 'unknown';

interface BessAllocationCandidate {
  sourceKind: 'csv' | 'claim' | 'phase';
  recordId?: string;
  sourceDocIds: string[];
  sourceDocNames: string[];
  explicitPhaseRaw?: string;
  explicitUnitRaw?: string;
  explicitRoleRaw?: string;
  sourceSpan: string;
  auxiliaryText: string;
  quantity: number;
}

const BESS_UNIT_NUMBER_WORD_MAP: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  i: '1',
  ii: '2',
  iii: '3',
  iv: '4',
  v: '5',
};

function normalizeBessUnitToken(raw: string): string | undefined {
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(
      /^(?:bess[-_\s]*)?(?:storage[-_\s]*)?(?:unit|block|asset|equip(?:ment)?|alloc(?:ation)?)\s*[-_#:=]?\s*/i,
      '',
    )
    .replace(/\s+/g, '-');
  if (!cleaned) return undefined;
  const mapped = BESS_UNIT_NUMBER_WORD_MAP[cleaned] ?? cleaned;
  if (
    /^(?:lot|system|bess|phase|mw|mwh|hour|hr|duration|lfp|none|null|undefined|initial|additional|retained|replaced|expansion)$/i.test(
      mapped,
    )
  ) {
    return undefined;
  }
  return `unit:${mapped}`;
}

function extractBessUnitKey(explicitUnitRaw?: string, textForUnit?: string): string | undefined {
  if (explicitUnitRaw) {
    const fromExplicit = normalizeBessUnitToken(explicitUnitRaw);
    if (fromExplicit) return fromExplicit;
  }
  if (textForUnit) {
    const kvMatch = textForUnit.match(
      /\b(?:unitId|equipmentId|assetId|allocationId)\s*[:=]\s*([A-Za-z0-9_-]+)/i,
    );
    if (kvMatch?.[1]) {
      const fromKv = normalizeBessUnitToken(kvMatch[1]);
      if (fromKv) return fromKv;
    }
    const phraseMatch = textForUnit.match(
      /\b(?:bess\s+unit|storage\s+unit|unit|asset|allocation)\s*[-_#:]?\s*(one|two|three|four|five|six|i|ii|iii|iv|\d+|[a-z]+\d+|\d+[a-z]+)\b/i,
    );
    if (phraseMatch?.[1]) {
      const fromPhrase = normalizeBessUnitToken(phraseMatch[1]);
      if (fromPhrase) return fromPhrase;
    }
  }
  return undefined;
}

function classifyBessAllocationRole(
  explicitRoleRaw?: string,
  sourceSpan?: string,
  auxiliaryText?: string,
): BessAllocationRole {
  const combined = `${explicitRoleRaw ?? ''} ${sourceSpan ?? ''} ${auxiliaryText ?? ''}`;
  if (
    /\b(?:replaced|replaces|replacing|replacement|supersede[ds]?|swap(?:ped|s)?|retire[ds]?|decommission(?:ed|s)?|substitut(?:ed|es|ion))\b/i.test(
      combined,
    )
  ) {
    return 'replaced';
  }
  if (
    /\b(?:retained|retain|retaining|existing\s+(?:unit|bess|system|storage|asset|allocation)|continues?\s+unchanged|continued\s+unchanged|unchanged|carr(?:y|ied|ies)\s+over|carryover|same\s+(?:unit|bess|system|asset|allocation)|already\s+installed|prior[- ]phase\s+(?:unit|bess)|cumulative|relocated)\b/i.test(
      combined,
    )
  ) {
    return 'retained';
  }
  if (explicitRoleRaw) {
    const normRole = explicitRoleRaw.trim().toLowerCase();
    if (/^(?:initial|baseline|new[-_ ]?initial|primary|first)$/i.test(normRole)) {
      return 'initial';
    }
    if (/^(?:additional|addition|add(?:ed)?|incremental|supplemental|second(?:ary)?|new[-_ ]?addition)$/i.test(normRole)) {
      return 'additional';
    }
  }
  if (
    /\b(?:additional\s+(?:bess|unit|storage|lot|allocation)|new\s+additional|second\s+unit|unit\s*[-#:]?\s*(?:2|two|ii)\s*(?:added|additional)|\(\s*additional\s*\)|\brole\s*[:=]\s*additional\b)/i.test(
      combined,
    )
  ) {
    return 'additional';
  }
  if (
    /\b(?:initial\s+(?:bess|unit|storage|lot|allocation)|baseline\s+unit|first\s+unit|unit\s*[-#:]?\s*(?:1|one|i)\s*(?:initial|baseline)|\(\s*initial\s*\)|\brole\s*[:=]\s*initial\b)/i.test(
      combined,
    )
  ) {
    return 'initial';
  }
  return 'unknown';
}

function isBessOrBomRelatedRecord(rec: Record<string, unknown>): boolean {
  const text = [
    rec.id,
    rec.docId,
    rec.documentId,
    rec.sourceId,
    rec.sourceDocId,
    rec.fileName,
    rec.title,
    rec.relativePath,
    rec.source,
    rec.label,
    rec.field,
    rec.sedField,
    rec.category,
    rec.claim,
    rec.value,
    rec.claimedValue,
    rec.sourceSpan,
    rec.excerpt,
    rec.textContent,
    rec.fullExtractedText,
    rec.bessSpec,
    rec.bess,
  ]
    .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : ''))
    .join(' ');
  return (
    /\b(?:southwest[_ -]?crossing[_ -]?bom|generation\s*&\s*impact\s*bom|energy-storage|generation\.bess|power\.btm|80\s*MWh\s*\/\s*40\s*MW|\bbess\b)\b/i.test(
      text,
    )
    || extractPositiveNumber(rec.bessMwh) !== undefined
    || extractPositiveNumber(rec.bessMw) !== undefined
  );
}

export function extractSouthwestBessPhaseCountFromSource(
  source: Record<string, unknown>,
  _phases?: ScrubPhaseInfo[] | undefined,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): number | undefined {
  const sedObj =
    source.sed && typeof source.sed === 'object' && !Array.isArray(source.sed)
      ? (source.sed as Record<string, unknown>)
      : undefined;

  const uploadedDocs = [
    ...(Array.isArray(sedObj?.uploadedDocuments) ? sedObj.uploadedDocuments : []),
    ...(Array.isArray(sedObj?.documents) ? sedObj.documents : []),
    ...(Array.isArray(source.uploadedDocuments) ? source.uploadedDocuments : []),
    ...(Array.isArray(source.documents) ? source.documents : []),
  ];
  const refsList = [
    ...(Array.isArray(source.references) ? source.references : []),
    ...(Array.isArray(source.citations) ? source.citations : []),
    ...(Array.isArray(sedObj?.references) ? sedObj.references : []),
    ...(Array.isArray(sedObj?.citations) ? sedObj.citations : []),
  ];
  const siteContextObj =
    sedObj?.siteContext && typeof sedObj.siteContext === 'object' && !Array.isArray(sedObj.siteContext)
      ? (sedObj.siteContext as Record<string, unknown>)
      : source.siteContext && typeof source.siteContext === 'object' && !Array.isArray(source.siteContext)
        ? (source.siteContext as Record<string, unknown>)
        : undefined;
  const claims = [
    ...(Array.isArray(siteContextObj?.claims) ? siteContextObj.claims : []),
    ...(Array.isArray(source.claims) ? source.claims : []),
    ...(Array.isArray(sedObj?.claims) ? sedObj.claims : []),
  ];
  const rawPhases = [
    ...(Array.isArray(source.phases) ? source.phases : []),
    ...(Array.isArray(sedObj?.phases) ? sedObj.phases : []),
  ];

  // Pass 1: Fixed-point connected-component source-denial pre-pass across all source collections and aliases before any allocation admission.
  const allSourceRecords = [...uploadedDocs, ...refsList, ...claims, ...rawPhases];
  const { disqualifiedDocIds, disqualifiedDocNames } = computeSourceAliasClosure(
    allSourceRecords,
    hostSiteId,
    hostReportId,
    hostRevisionId,
  );
  const negatedPhaseKeys = new Set<string>();
  const nonAdditivePhaseKeys = new Set<string>();
  const nonAdditiveUnitKeys = new Set<string>();
  let hasDisqualifiedBessOrBomRecord = false;

  for (const item of allSourceRecords) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        rec,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
      && isBessOrBomRelatedRecord(rec)
    ) {
      hasDisqualifiedBessOrBomRecord = true;
    }

    const recPhaseRaw =
      extractNonEmptyString(rec.phaseId)
      ?? extractNonEmptyString(rec.phase)
      ?? extractNonEmptyString(rec.label);
    const recPhaseKey = recPhaseRaw ? normalizeSouthwestBessPhaseKey(recPhaseRaw) : undefined;
    const recText = `${String(rec.sourceSpan ?? '')} ${String(rec.excerpt ?? '')} ${String(rec.claim ?? '')} ${String(rec.value ?? '')} ${String(rec.claimedValue ?? '')} ${String(rec.bessSpec ?? '')} ${String(rec.bess ?? '')}`;
    if (
      recPhaseKey
      && (
        NEGATIVE_BESS_SOURCE_RE.test(recText)
        || (isBessOrBomRelatedRecord(rec) && resolveAllocationQuantity(rec) <= 0)
      )
    ) {
      negatedPhaseKeys.add(recPhaseKey);
    }
  }

  if (hasDisqualifiedBessOrBomRecord) {
    return undefined;
  }

  // Pass 2: Normalize candidates from CSV rows, claims, and structured phases into one shared allocation contract.
  const candidates: BessAllocationCandidate[] = [];

  for (const item of uploadedDocs) {
    if (!item || typeof item !== 'object') continue;
    const d = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        d,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const dIds = extractRecordAliasIds(d);
    const dNames = extractRecordAliasNames(d);
    const textContent = String(d.textContent ?? d.fullExtractedText ?? '');
    if (textContent.length === 0) continue;
    const bomPhaseRowRe =
      /^(Phase\s+(?:\d+|[IVX]+))\s*,\s*energy-storage\s*,\s*([^,\n]*)\s*,\s*"([^"\n]*)"\s*,\s*([^,\n]*)\s*,\s*([^,\n]*)(?:,\s*([^\n]*))?$/gim;
    let rowIdx = 0;
    for (const m of textContent.matchAll(bomPhaseRowRe)) {
      rowIdx += 1;
      const phaseCol = m[1]?.trim();
      const itemCol = m[2]?.trim() ?? '';
      const specCol = m[3]?.trim() ?? '';
      const qtyRaw = m[4]?.trim() ?? '';
      const qtyVal = resolveAllocationQuantity({ quantity: qtyRaw });
      const unitCol = m[5]?.trim() ?? '';
      const extraColsRaw = m[6]?.trim() ?? '';
      const extraTokens = extraColsRaw
        .split(',')
        .map((t) => t.trim().replace(/^"|"$/g, ''))
        .filter((t) => t.length > 0);
      let explicitUnitRaw: string | undefined;
      let explicitRoleRaw: string | undefined;
      for (const tok of extraTokens) {
        if (!explicitRoleRaw && /^(?:initial|additional|retained|replaced|baseline|incremental|replacement)$/i.test(tok)) {
          explicitRoleRaw = tok;
        } else if (!explicitUnitRaw && normalizeBessUnitToken(tok) !== undefined) {
          explicitUnitRaw = tok;
        }
      }
      candidates.push({
        sourceKind: 'csv',
        recordId: `${dIds[0] ?? dNames[0] ?? 'csv'}:row-${rowIdx}`,
        sourceDocIds: dIds,
        sourceDocNames: dNames,
        explicitPhaseRaw: phaseCol,
        explicitUnitRaw,
        explicitRoleRaw,
        sourceSpan: specCol,
        auxiliaryText: `${itemCol} ${unitCol} ${extraColsRaw}`,
        quantity: qtyVal,
      });
    }
  }

  for (const item of claims) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        c,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const f = String(c.field ?? c.sedField ?? c.category ?? '');
    if (!/^(?:generation\.bess(?:Capacity)?|power\.btm)$/i.test(f)) continue;
    const cDocIds = [c.sourceDocId, c.docId, c.documentId, c.sourceId]
      .map(extractNonEmptyString)
      .filter((v): v is string => v !== undefined);
    const cDocNames = extractRecordAliasNames(c);
    const explicitPhaseRaw = extractNonEmptyString(c.phaseId) ?? extractNonEmptyString(c.phase);
    const explicitUnitRaw =
      extractNonEmptyString(c.unitId)
      ?? extractNonEmptyString(c.equipmentId)
      ?? extractNonEmptyString(c.assetId)
      ?? extractNonEmptyString(c.allocationId)
      ?? extractNonEmptyString(c.bessUnitId);
    const explicitRoleRaw =
      extractNonEmptyString(c.allocationRole)
      ?? extractNonEmptyString(c.role)
      ?? extractNonEmptyString(c.disposition)
      ?? extractNonEmptyString(c.action);
    const sourceSpan = extractNonEmptyString(c.sourceSpan) ?? '';
    const qtyVal = resolveAllocationQuantity(c);
    candidates.push({
      sourceKind: 'claim',
      recordId: extractNonEmptyString(c.id),
      sourceDocIds: cDocIds,
      sourceDocNames: cDocNames,
      explicitPhaseRaw,
      explicitUnitRaw,
      explicitRoleRaw,
      sourceSpan,
      auxiliaryText: `${String(c.claim ?? '')} ${String(c.value ?? '')} ${String(c.claimedValue ?? '')}`,
      quantity: qtyVal,
    });
  }

  for (const item of rawPhases) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    if (
      isRecordDisqualifiedOrAliased(
        rec,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        disqualifiedDocIds,
        disqualifiedDocNames,
      )
    ) {
      continue;
    }
    const pDocIds = [rec.sourceDocId, rec.docId, rec.documentId, rec.sourceId]
      .map(extractNonEmptyString)
      .filter((v): v is string => v !== undefined);
    const pDocNames = extractRecordAliasNames(rec);
    const explicitPhaseRaw =
      extractNonEmptyString(rec.phaseId)
      ?? extractNonEmptyString(rec.phase)
      ?? extractNonEmptyString(rec.id)
      ?? extractNonEmptyString(rec.label);
    const explicitUnitRaw =
      extractNonEmptyString(rec.unitId)
      ?? extractNonEmptyString(rec.equipmentId)
      ?? extractNonEmptyString(rec.assetId)
      ?? extractNonEmptyString(rec.allocationId)
      ?? extractNonEmptyString(rec.bessUnitId);
    const explicitRoleRaw =
      extractNonEmptyString(rec.allocationRole)
      ?? extractNonEmptyString(rec.role)
      ?? extractNonEmptyString(rec.disposition)
      ?? extractNonEmptyString(rec.action);
    const hasExplicitSpanField = rec.sourceSpan !== undefined || rec.excerpt !== undefined;
    const sourceSpan = hasExplicitSpanField
      ? (extractNonEmptyString(rec.sourceSpan) ?? extractNonEmptyString(rec.excerpt) ?? '')
      : (extractNonEmptyString(rec.bessSpec) ?? extractNonEmptyString(rec.bess) ?? '');
    const qtyVal = resolveAllocationQuantity(rec);
    candidates.push({
      sourceKind: 'phase',
      recordId: extractNonEmptyString(rec.id),
      sourceDocIds: pDocIds,
      sourceDocNames: pDocNames,
      explicitPhaseRaw,
      explicitUnitRaw,
      explicitRoleRaw,
      sourceSpan,
      auxiliaryText: `${String(rec.bessSpec ?? '')} ${String(rec.bess ?? '')} ${String(rec.description ?? '')}`,
      quantity: qtyVal,
    });
  }

  // Evaluate all normalized candidates under one shared source-admission and equipment-allocation contract.
  const seenRecordIds = new Set<string>();
  const seenCandidateSignatures = new Set<string>();
  const unitPhases = new Map<string, Set<string>>();
  const admittedAllocations: Array<{ phaseKey: string; unitKey: string; role: 'initial' | 'additional' }> = [];

  for (const cand of candidates) {
    if (cand.sourceDocIds.length === 0) continue;
    if (
      cand.sourceDocIds.some((idVal) => disqualifiedDocIds.has(idVal))
      || cand.sourceDocNames.some((nVal) => disqualifiedDocNames.has(nVal.toLowerCase()))
    ) {
      continue;
    }
    const phaseKey = cand.explicitPhaseRaw
      ? normalizeSouthwestBessPhaseKey(cand.explicitPhaseRaw)
      : undefined;
    const unitKey = extractBessUnitKey(
      cand.explicitUnitRaw,
      `${cand.sourceSpan} ${cand.auxiliaryText}`,
    );
    const role = classifyBessAllocationRole(
      cand.explicitRoleRaw,
      cand.sourceSpan,
      cand.auxiliaryText,
    );
    const fullText = `${cand.sourceSpan} ${cand.auxiliaryText} ${cand.explicitRoleRaw ?? ''}`;

    if (NEGATIVE_BESS_SOURCE_RE.test(fullText) || !(cand.quantity > 0)) {
      if (phaseKey) negatedPhaseKeys.add(phaseKey);
      if (unitKey) nonAdditiveUnitKeys.add(unitKey);
      continue;
    }

    if (role === 'retained' || role === 'replaced') {
      if (phaseKey) nonAdditivePhaseKeys.add(phaseKey);
      if (unitKey) nonAdditiveUnitKeys.add(unitKey);
      continue;
    }

    // Require the resolvable admitted sourceSpan itself to affirmatively support 80 MWh / 40 MW and 4-hour/4-hr.
    if (
      !cand.sourceSpan
      || NEGATIVE_BESS_SOURCE_RE.test(cand.sourceSpan)
      || !/\b80\s*MWh\s*\/\s*40\s*MW\b/i.test(cand.sourceSpan)
      || !/\b4[- ]?(?:hour|hr)\b/i.test(cand.sourceSpan)
    ) {
      continue;
    }

    // Require explicit phase binding, unique unit identity, and explicit initial/additional role.
    if (!phaseKey || !unitKey || (role !== 'initial' && role !== 'additional')) {
      continue;
    }

    if (cand.recordId) {
      const scopedRecordId = `${cand.sourceKind}:${cand.recordId}`;
      if (seenRecordIds.has(scopedRecordId)) continue;
      seenRecordIds.add(scopedRecordId);
    }

    const sig = `${cand.sourceKind}|${phaseKey}|${unitKey}|${role}|${cand.sourceSpan.toLowerCase().replace(/\s+/g, ' ').trim()}`;
    if (seenCandidateSignatures.has(sig)) continue;
    seenCandidateSignatures.add(sig);

    let phasesForUnit = unitPhases.get(unitKey);
    if (!phasesForUnit) {
      phasesForUnit = new Set<string>();
      unitPhases.set(unitKey, phasesForUnit);
    }
    phasesForUnit.add(phaseKey);

    admittedAllocations.push({ phaseKey, unitKey, role });
  }

  // Any unit appearing across multiple phases is a retained/carryover unit, not distinct additive equipment.
  for (const [uKey, pSet] of unitPhases.entries()) {
    if (pSet.size > 1) {
      nonAdditiveUnitKeys.add(uKey);
      for (const pKey of pSet) {
        nonAdditivePhaseKeys.add(pKey);
      }
    }
  }

  const validPhases = new Set<string>();
  const validUnits = new Set<string>();
  let hasInitialRole = false;
  let hasAdditionalRole = false;

  for (const alloc of admittedAllocations) {
    if (
      negatedPhaseKeys.has(alloc.phaseKey)
      || nonAdditivePhaseKeys.has(alloc.phaseKey)
      || nonAdditiveUnitKeys.has(alloc.unitKey)
    ) {
      continue;
    }
    validPhases.add(alloc.phaseKey);
    validUnits.add(alloc.unitKey);
    if (alloc.role === 'initial') hasInitialRole = true;
    if (alloc.role === 'additional') hasAdditionalRole = true;
  }

  if (
    validPhases.size >= 2
    && validUnits.size >= 2
    && hasInitialRole
    && hasAdditionalRole
  ) {
    return Math.min(validPhases.size, validUnits.size);
  }
  return undefined;
}

function extractCitationOrdinalsFromPdfDataSources(
  source: Record<string, unknown>,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): {
  bomRefOrdinal?: number;
  hollyRidgeRefOrdinal?: number;
  g3516CanonicalRef?: number;
  bomDisqualified: boolean;
  hollyRidgeDisqualified: boolean;
  g3516Disqualified: boolean;
} {
  const factIndex = collectAdmittedSourceFacts(source, hostSiteId, hostReportId, hostRevisionId);
  const sedObj =
    source.sed && typeof source.sed === 'object' && !Array.isArray(source.sed)
      ? (source.sed as Record<string, unknown>)
      : undefined;
  const uploadedDocs = [
    ...(Array.isArray(sedObj?.uploadedDocuments) ? sedObj.uploadedDocuments : []),
    ...(Array.isArray(sedObj?.documents) ? sedObj.documents : []),
    ...(Array.isArray(source.uploadedDocuments) ? source.uploadedDocuments : []),
    ...(Array.isArray(source.documents) ? source.documents : []),
  ];
  let bomDisqualified = false;
  let hollyRidgeDisqualified = false;
  let g3516Disqualified = false;

  for (const item of uploadedDocs) {
    if (!item || typeof item !== 'object') continue;
    const d = item as Record<string, unknown>;
    const docDesc = `${String(d.id ?? '')} ${String(d.fileName ?? '')} ${String(d.relativePath ?? '')} ${String(d.title ?? '')}`;
    const isBom = /southwest-crossing-bom-demo\.csv|Southwest_Crossing_BOM\.xlsx|Generation\s*&\s*Impact\s*BOM/i.test(docDesc);
    const isHollyRidge = /26-0409\s+InvestorDeck\.pptx|Holly\s+Ridge.*Investor\s+Presentation/i.test(docDesc);
    const isG3516 = /Catepillar\s*-\s*Area\s+View\s+Clayco\s+DC\s+150\s*x\s*G3516|Prelim\s+Site\s+Plan\s*&\s*Gen\s+Yard\s+Layout/i.test(docDesc);
    if (!isBom && !isHollyRidge && !isG3516) continue;
    if (
      isRecordDisqualifiedOrAliased(
        d,
        hostSiteId,
        hostReportId,
        hostRevisionId,
        factIndex.disqualifiedDocIds,
        factIndex.disqualifiedDocNames,
      )
    ) {
      if (isBom) bomDisqualified = true;
      if (isHollyRidge) hollyRidgeDisqualified = true;
      if (isG3516) g3516Disqualified = true;
    }
  }

  const hasAdmittedBomDoc = factIndex.hasAdmittedFact.bomBess || factIndex.hasAdmittedFact.bomPv;
  const hasAdmittedHollyRidgeDoc = factIndex.hasAdmittedFact.hollyRidge;
  const hasAdmittedG3516Doc = factIndex.hasAdmittedFact.g3516;

  const pageTextBuffers: string[] = [];
  for (const container of [
    source.extractedPages,
    source.pdfPages,
    source.pages,
    source.dataSources,
    source.dataSourcesText,
  ]) {
    if (typeof container === 'string') {
      pageTextBuffers.push(container);
    } else if (Array.isArray(container)) {
      for (const el of container) {
        if (typeof el === 'string') pageTextBuffers.push(el);
        else if (el && typeof el === 'object') {
          const rec = el as Record<string, unknown>;
          for (const k of ['text', 'label', 'source', 'title', 'description', 'citation', 'value']) {
            if (typeof rec[k] === 'string') pageTextBuffers.push(rec[k] as string);
          }
        }
      }
    } else if (container && typeof container === 'object') {
      for (const val of Object.values(container as Record<string, unknown>)) {
        if (typeof val === 'string') pageTextBuffers.push(val);
      }
    }
  }
  const combinedText = pageTextBuffers.join('\n');
  let bomRefOrdinal: number | undefined;
  let hollyRidgeRefOrdinal: number | undefined;
  let g3516CanonicalRef: number | undefined;
  if (combinedText.length > 0) {
    const citationRe = /Source\s+Citation\s+[—–-]\s+citation\s+ref\s+(\d+)\s+[—–-]\s+Uploaded\s+document:\s*([^·]+)/gi;
    for (const m of combinedText.matchAll(citationRe)) {
      const refNum = parseInt(m[1]!, 10);
      const docLabel = m[2]!.replace(/\s+/g, ' ').trim();
      if (!Number.isFinite(refNum) || refNum <= 0) continue;
      if (
        hasAdmittedBomDoc
        && !bomDisqualified
        && /southwest-crossing-bom-demo\.csv|Southwest_Crossing_BOM\.xlsx/i.test(docLabel)
      ) {
        bomRefOrdinal = refNum;
      }
      if (
        hasAdmittedHollyRidgeDoc
        && !hollyRidgeDisqualified
        && /Holly\s+Ridge.*26-0409\s+InvestorDeck\.pptx/i.test(docLabel)
      ) {
        hollyRidgeRefOrdinal = refNum;
      }
      if (
        hasAdmittedG3516Doc
        && !g3516Disqualified
        && /Catepillar\s*-\s*Area\s+View\s+Clayco\s+DC\s+150\s*x\s*G3516/i.test(docLabel)
      ) {
        g3516CanonicalRef = refNum;
      }
    }
  }
  return {
    ...(bomRefOrdinal !== undefined ? { bomRefOrdinal } : {}),
    ...(hollyRidgeRefOrdinal !== undefined ? { hollyRidgeRefOrdinal } : {}),
    ...(g3516CanonicalRef !== undefined ? { g3516CanonicalRef } : {}),
    bomDisqualified,
    hollyRidgeDisqualified,
    g3516Disqualified,
  };
}

export function bindValidatedScrubContext<T>(
  target: T,
  context: ScrubScenarioContext | undefined | null,
): T {
  if (target === null || typeof target !== 'object') {
    return target;
  }
  if (context === undefined || context === null) {
    BOUND_SCRUB_CONTEXT.delete(target as object);
    return target;
  }
  const source = target as unknown as Record<string, unknown>;
  if (hasTargetContextConflict(source, context)) {
    BOUND_SCRUB_CONTEXT.delete(target as object);
    return target;
  }
  const identity = extractTargetIdentity(source);
  const frozenContext = freezeScrubContext({
    ...(identity.siteId ? { siteId: identity.siteId } : {}),
    ...(identity.reportId ? { reportId: identity.reportId } : {}),
    ...(identity.revisionId ? { revisionId: identity.revisionId } : {}),
    ...context,
  });
  const boundSnapshotFp = computeTargetBindingFingerprint(source);
  BOUND_SCRUB_CONTEXT.set(
    target as object,
    Object.freeze({
      context: frozenContext,
      boundSnapshotFp,
      identity,
    }),
  );
  return target;
}

export function getBoundScrubContext(target: unknown): ScrubScenarioContext | undefined {
  if (target === null || typeof target !== 'object') {
    return undefined;
  }
  const record = BOUND_SCRUB_CONTEXT.get(target as object);
  if (!record) return undefined;
  const source = target as Record<string, unknown>;
  if (
    hasTargetContextConflict(source, record.context)
    || computeTargetBindingFingerprint(source) !== record.boundSnapshotFp
  ) {
    BOUND_SCRUB_CONTEXT.delete(target as object);
    return undefined;
  }
  return record.context;
}

function isScreeningSummaryDocumentObject(source: Record<string, unknown>): boolean {
  if (source.schemaVersion !== 'report-document-v1') return false;
  if (source.title === 'Screening Summary') return true;
  if (Array.isArray(source.sections)) {
    return source.sections.some(
      (sec) =>
        sec != null
        && typeof sec === 'object'
        && (sec as Record<string, unknown>).sectionId === 'summaryCoolingWater',
    );
  }
  return false;
}

const LONGSPUR_SITE_ID = 'site-1783523395000-bczk3g';

function extractPositiveNumber(val: unknown): number | undefined {
  if (typeof val === 'number' && Number.isFinite(val) && val > 0) return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/,/g, '').trim();
    if (/^\d+(?:\.\d+)?(?:\s*MW)?$/i.test(cleaned)) {
      const parsed = parseFloat(cleaned);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return undefined;
}

function normalizePhaseLabel(raw: string, idx: number): string {
  const trimmed = raw.trim();
  const phaseMatch = trimmed.match(/^phase\s+(\d+)\b/i);
  if (phaseMatch) return `Phase ${phaseMatch[1]}`;
  return trimmed || `Phase ${idx + 1}`;
}

function extractPhasesFromArray(rawPhases: unknown[]): ScrubPhaseInfo[] | undefined {
  const extracted: ScrubPhaseInfo[] = [];
  rawPhases.forEach((entry, idx) => {
    if (!entry || typeof entry !== 'object') return;
    const rec = entry as Record<string, unknown>;
    const mw =
      extractPositiveNumber(rec.capacityMW)
      ?? extractPositiveNumber(rec.facilityLoadMW)
      ?? extractPositiveNumber(rec.facilityMW)
      ?? extractPositiveNumber(rec.loadMW)
      ?? extractPositiveNumber(rec.mw);
    if (mw === undefined) return;
    const rawLabel = String(rec.label ?? rec.phaseLabel ?? rec.phase ?? rec.name ?? '');
    extracted.push({
      label: normalizePhaseLabel(rawLabel, idx),
      facilityLoadMw: mw,
    });
  });
  if (extracted.length === 0) return undefined;
  if (extracted.length > 1) {
    extracted[extracted.length - 1]!.isFullBuild = true;
  }
  return extracted;
}

function findEmbeddedIntentObject(source: Record<string, unknown>): Record<string, unknown> | undefined {
  for (const key of ['projectIntentAtGen', 'projectIntent', 'generationImpactScenarioAtGen']) {
    const candidate = source[key];
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate as Record<string, unknown>;
    }
  }
  if (source.input && typeof source.input === 'object' && !Array.isArray(source.input)) {
    const inputRec = source.input as Record<string, unknown>;
    if (inputRec.intent && typeof inputRec.intent === 'object' && !Array.isArray(inputRec.intent)) {
      const intentRec = inputRec.intent as Record<string, unknown>;
      if (intentRec.snapshot && typeof intentRec.snapshot === 'object' && !Array.isArray(intentRec.snapshot)) {
        return intentRec.snapshot as Record<string, unknown>;
      }
    }
  }
  if (Array.isArray(source.sections)) {
    for (const sec of source.sections) {
      if (!sec || typeof sec !== 'object') continue;
      const secRec = sec as Record<string, unknown>;
      if (secRec.sectionId !== 'projectIntentOverview' || !Array.isArray(secRec.blocks)) continue;
      for (const blk of secRec.blocks) {
        if (!blk || typeof blk !== 'object') continue;
        const blkRec = blk as Record<string, unknown>;
        if (
          blkRec.label === 'projectIntentOverview'
          && blkRec.value
          && typeof blkRec.value === 'object'
          && !Array.isArray(blkRec.value)
        ) {
          return blkRec.value as Record<string, unknown>;
        }
      }
    }
  }
  return undefined;
}

const NARRATIVE_BASE_CASE_PUE_RE =
  /\b(?:resolves at a (\d+(?:\.\d+)?)\s*PUE|at the (\d+(?:\.\d+)?)\s*planning PUE default)\b/i;

function extractNarrativeBaseCasePueFromText(text: unknown): number | undefined {
  if (typeof text !== 'string' || text.length === 0) return undefined;
  const m = text.match(NARRATIVE_BASE_CASE_PUE_RE);
  if (!m) return undefined;
  return extractPositiveNumber(m[1] ?? m[2]);
}

function extractNarrativeBaseCasePue(source: Record<string, unknown>): number | undefined {
  const fromOverview = extractNarrativeBaseCasePueFromText(source.projectIntentOverview);
  if (fromOverview !== undefined) return fromOverview;

  if (Array.isArray(source.sections)) {
    for (const sec of source.sections) {
      if (!sec || typeof sec !== 'object') continue;
      const secRec = sec as Record<string, unknown>;
      if (secRec.sectionId !== 'projectIntentOverview') continue;
      const fromText = extractNarrativeBaseCasePueFromText(secRec.content ?? secRec.text);
      if (fromText !== undefined) return fromText;
      if (Array.isArray(secRec.blocks)) {
        for (const blk of secRec.blocks) {
          if (!blk || typeof blk !== 'object') continue;
          const blkRec = blk as Record<string, unknown>;
          const fromBlk = extractNarrativeBaseCasePueFromText(blkRec.text ?? blkRec.value);
          if (fromBlk !== undefined) return fromBlk;
        }
      }
    }
  }

  if (source.semantic && typeof source.semantic === 'object' && !Array.isArray(source.semantic)) {
    const semRec = source.semantic as Record<string, unknown>;
    const fromSem = extractNarrativeBaseCasePue(semRec);
    if (fromSem !== undefined) return fromSem;
  }

  return undefined;
}

function parseMgdBand(text: unknown): { low: number; high: number } | undefined {
  if (typeof text !== 'string') return undefined;
  const m = text.match(/(\d+(?:\.\d+)?)\s*[–-]\s*(\d+(?:\.\d+)?)\s*MGD/i);
  if (!m) return undefined;
  const low = parseFloat(m[1]!);
  const high = parseFloat(m[2]!);
  if (!Number.isFinite(low) || !Number.isFinite(high)) return undefined;
  return { low, high };
}

export function extractWaterScreeningFromSource(source: Record<string, unknown>): ScrubWaterScreeningContext | undefined {
  if (
    source.waterScreening
    && typeof source.waterScreening === 'object'
    && !Array.isArray(source.waterScreening)
  ) {
    return source.waterScreening as ScrubWaterScreeningContext;
  }

  const datasetsClimate =
    source.datasets && typeof source.datasets === 'object'
      ? ((source.datasets as Record<string, unknown>).climate as Record<string, unknown> | undefined)
      : undefined;
  const rawWetBulb = datasetsClimate?.wetBulbTemp ?? datasetsClimate?.avgWetBulb;
  const wetBulbTempC =
    typeof rawWetBulb === 'number' && Number.isFinite(rawWetBulb)
      ? rawWetBulb
      : undefined;

  if (
    source.waterScreeningView
    && typeof source.waterScreeningView === 'object'
    && !Array.isArray(source.waterScreeningView)
  ) {
    const wsv = source.waterScreeningView as Record<string, unknown>;
    const rows = Array.isArray(wsv.rows) ? (wsv.rows as Array<Record<string, unknown>>) : [];
    const dryRow = rows.find((r) => r && r.scenarioId === 'dry-cooling-process-water-v1');
    const wetRow = rows.find((r) => r && r.scenarioId === 'epa-wet-cooling-tower-water-v2-withdrawal');
    const dryAssist =
      dryRow?.assist && typeof dryRow.assist === 'object'
        ? (dryRow.assist as Record<string, unknown>)
        : undefined;
    const dryBand = parseMgdBand(
      dryAssist?.averageDay ?? dryAssist?.waterDemandRange ?? dryRow?.averageDay ?? dryRow?.waterDemandRange,
    );
    const wetBand = parseMgdBand(wetRow?.averageDay ?? wetRow?.waterDemandRange);
    const basisText = Array.isArray(wsv.basis)
      ? wsv.basis.map((b) => String(b ?? '')).join(' ')
      : typeof wsv.basis === 'string'
        ? wsv.basis
        : '';
    const scopeText = typeof wsv.scope === 'string' ? wsv.scope : '';
    const pueMatch = basisText.match(/PUE\s+([\d.]+(?:[–-][\d.]+)?)/i);
    const pueRangeLabel =
      typeof wsv.pueRangeLabel === 'string' && wsv.pueRangeLabel.trim().length > 0
        ? wsv.pueRangeLabel.trim()
        : pueMatch
          ? pueMatch[1]
          : undefined;
    const utilRangeMatch = basisText.match(/(?:annual\s+)?utilization\s+(\d+(?:\.\d+)?[–-]\d+(?:\.\d+)?)%/i);
    const pointUtilMatch =
      basisText.match(/(?:point[- ]utilization|utilization)\s+(\d+(?:\.\d+)?)%(?!\s*[–-])/i)
      ?? basisText.match(/(\d+(?:\.\d+)?)%\s*(?:illustrative\s+)?(?:point[- ]|annual\s+)?utilization/i);
    const pointUtilizationPct =
      typeof wsv.pointUtilizationPct === 'number' && Number.isFinite(wsv.pointUtilizationPct)
        ? wsv.pointUtilizationPct
        : pointUtilMatch
          ? extractPositiveNumber(pointUtilMatch[1])
          : undefined;
    const utilizationRangeLabel =
      typeof wsv.utilizationRangeLabel === 'string' && wsv.utilizationRangeLabel.trim().length > 0
        ? wsv.utilizationRangeLabel.trim()
        : utilRangeMatch
          ? `${utilRangeMatch[1]}%`
          : undefined;
    const loadMatch =
      (basisText || scopeText).match(/facility\s+(?:heat|load)\s+(?:[\d,]+\s*[–-]\s*)?([\d,]+)\s*MW/i)
      ?? scopeText.match(/(?:[\d,]+\s*[–-]\s*)?([\d,]+)\s*MW\s+facility\s+load/i)
      ?? (basisText || scopeText).match(/IT\s+load\s+(?:[\d,]+\s*[–-]\s*)?([\d,]+)\s*MW/i)
      ?? scopeText.match(/(?:[\d,]+\s*[–-]\s*)?([\d,]+)\s*MW\s+IT\s+load/i);
    const facilityLoadMw =
      typeof wsv.facilityLoadMw === 'number' && Number.isFinite(wsv.facilityLoadMw)
        ? wsv.facilityLoadMw
        : loadMatch
          ? extractPositiveNumber(loadMatch[1])
          : undefined;
    const hasCatalogV2Scenarios =
      typeof wsv.hasCatalogV2Scenarios === 'boolean'
        ? wsv.hasCatalogV2Scenarios
        : Boolean(dryRow && wetRow && dryBand && wetBand);
    const has550MwInPathwayOrSections = (() => {
      const devPath = Array.isArray(source.developmentPathway) ? source.developmentPathway : [];
      for (const entry of devPath) {
        if (!entry || typeof entry !== 'object') continue;
        const desc = String((entry as Record<string, unknown>).description ?? '');
        if (/Modeled phase water screening\s*\(\s*550\s*MW/i.test(desc) || /\b550\s*MW\b[^]*?\b3\.82\s*MGD\b/i.test(desc)) {
          return true;
        }
      }
      const secs = Array.isArray(source.sections) ? source.sections : [];
      for (const sec of secs) {
        if (!sec || typeof sec !== 'object') continue;
        const txt = JSON.stringify(sec);
        if (/Modeled phase water screening\s*\(\s*550\s*MW/i.test(txt) || /\b550\s*MW\b[^]*?\b3\.82\s*MGD\b/i.test(txt)) {
          return true;
        }
      }
      return false;
    })();
    const has550MwRows = rows.some((r) => {
      if (!r || typeof r !== 'object') return false;
      const rowLabel = `${String(r.label ?? '')} ${String(r.phaseLabel ?? '')} ${String(r.scope ?? '')}`;
      const is550Row =
        r.phaseLoadMw === 550
        || r.facilityLoadMw === 550
        || /\b550\s*MW\b/i.test(rowLabel);
      const hasRowDemand = Boolean(
        parseMgdBand(r.averageDay ?? r.waterDemandRange)
        || parseMgdBand((r.assist as Record<string, unknown> | undefined)?.averageDay),
      );
      return is550Row && hasRowDemand;
    }) || has550MwInPathwayOrSections;

    const wsvSources = Array.isArray(wsv.sources) ? (wsv.sources as Array<Record<string, unknown>>) : [];
    const wsvSourceUrls = new Set(
      wsvSources
        .map((s) => (typeof s?.url === 'string' ? s.url.trim() : ''))
        .filter(Boolean),
    );
    let refAshraePsychrometrics: number | undefined;
    if (wsvSourceUrls.size > 0) {
      const refsList = Array.isArray(source.references)
        ? source.references
        : Array.isArray(source.citations)
          ? source.citations
          : [];
      for (let i = 0; i < refsList.length; i++) {
        const refItem = refsList[i];
        if (!refItem || typeof refItem !== 'object') continue;
        const r = refItem as Record<string, unknown>;
        const refText = `${String(r.source ?? '')} ${String(r.label ?? '')} ${String(r.field ?? '')}`;
        const refUrl = typeof r.url === 'string' ? r.url.trim() : '';
        if (/\bASHRAE\b/i.test(refText) && /\bPsychrometrics\b/i.test(refText) && refUrl && wsvSourceUrls.has(refUrl)) {
          const num = typeof r.number === 'number' ? r.number : i + 1;
          refAshraePsychrometrics = num;
          break;
        }
      }
    }

    if (dryBand || wetBand || has550MwRows || refAshraePsychrometrics !== undefined) {
      return {
        ...(dryBand ? { dryLowMgd: dryBand.low, dryHighMgd: dryBand.high } : {}),
        ...(wetBand ? { wetLowMgd: wetBand.low, wetHighMgd: wetBand.high } : {}),
        ...(hasCatalogV2Scenarios ? { hasCatalogV2Scenarios: true } : { hasCatalogV2Scenarios: false }),
        ...(facilityLoadMw !== undefined ? { facilityLoadMw } : {}),
        ...(pueRangeLabel ? { pueRangeLabel } : {}),
        ...(pointUtilizationPct !== undefined ? { pointUtilizationPct } : {}),
        ...(utilizationRangeLabel ? { utilizationRangeLabel } : {}),
        ...(wetBulbTempC !== undefined ? { wetBulbTempC } : {}),
        ...(has550MwRows ? { has550MwRows: true } : {}),
        ...(refAshraePsychrometrics !== undefined ? { refAshraePsychrometrics } : {}),
      };
    }
  }

  return undefined;
}

export function deriveScrubScenarioContext(
  source: Record<string, unknown>,
  parent?: ScrubScenarioContext,
): ScrubScenarioContext | undefined {
  const localExplicitSiteId = extractNonEmptyString(source.siteId);
  const sedObjForId =
    source.sed && typeof source.sed === 'object' && !Array.isArray(source.sed)
      ? (source.sed as Record<string, unknown>)
      : undefined;
  const sedSiteId = extractNonEmptyString(sedObjForId?.siteId);
  const localReportId = extractReportIdFromRecord(source);
  const localRevisionId = extractRevisionIdFromRecord(source);
  const reportId = localReportId ?? parent?.reportId;
  const revisionId = localRevisionId ?? parent?.revisionId;
  const hasNestedRevisionBoundary =
    parent !== undefined
    && localRevisionId !== undefined
    && (
      (parent.revisionId !== undefined && localRevisionId !== parent.revisionId)
      || (
        parent.revisionId === undefined
        && (
          parent.southwestProvenance !== undefined
          || parent.phaseWaterMethodologyProvenance !== undefined
          || parent.sisRequestedDeliveryKv !== undefined
        )
      )
    );

  const hasLocalTupleConflict =
    hasCanonicalTupleConflict(
      localExplicitSiteId ?? sedSiteId ?? parent?.siteId,
      reportId,
      revisionId,
    )
    || (
      localExplicitSiteId !== undefined
      && sedSiteId !== undefined
      && localExplicitSiteId !== sedSiteId
      && classifySiteIdFamily(localExplicitSiteId) !== classifySiteIdFamily(sedSiteId)
    )
    || (localExplicitSiteId !== undefined && parent?.siteId !== undefined && localExplicitSiteId !== parent.siteId)
    || (localReportId !== undefined && parent?.reportId !== undefined && localReportId !== parent.reportId)
    || (localRevisionId !== undefined && parent?.revisionId !== undefined && localRevisionId !== parent.revisionId);
  const identityConflict = Boolean(parent?.identityConflict || hasLocalTupleConflict);

  const siteId =
    localExplicitSiteId
    ?? (!identityConflict && sedSiteId ? sedSiteId : undefined)
    ?? (!identityConflict && source.id === 'feasibility-1792773672702-2l6jqi' ? LONGSPUR_SITE_ID : undefined)
    ?? (!identityConflict ? parent?.siteId : undefined);
  const intentObj = findEmbeddedIntentObject(source);
  const hasLocalIntentContainer =
    intentObj !== undefined
    || 'projectIntentAtGen' in source
    || 'projectIntent' in source
    || 'generationImpactScenarioAtGen' in source
    || (source.input != null && typeof source.input === 'object' && !Array.isArray(source.input) && 'intent' in (source.input as Record<string, unknown>));
  const datasetsCooling =
    source.datasets && typeof source.datasets === 'object'
      ? ((source.datasets as Record<string, unknown>).cooling as Record<string, unknown> | undefined)
      : undefined;

  const sourceFieldSource =
    source.fieldSource && typeof source.fieldSource === 'object'
      ? (source.fieldSource as Record<string, unknown>)
      : undefined;
  const intentFieldSource =
    intentObj?.fieldSource && typeof intentObj.fieldSource === 'object'
      ? (intentObj.fieldSource as Record<string, unknown>)
      : undefined;
  const isExplicitUnknownPue =
    sourceFieldSource?.pueValue === 'user-unknown'
    || intentFieldSource?.pueValue === 'user-unknown';

  const hasExplicitPueField =
    'electricalPue' in source
    || 'pueValue' in source
    || 'pue' in source
    || 'pueTarget' in source
    || (intentObj != null && ('electricalPue' in intentObj || 'pueValue' in intentObj || 'pue' in intentObj || 'pueTarget' in intentObj));

  const localNumericPue =
    extractPositiveNumber(source.electricalPue)
    ?? extractPositiveNumber(source.pueValue)
    ?? extractPositiveNumber(source.pue)
    ?? extractPositiveNumber(source.pueTarget)
    ?? (intentObj
      ? extractPositiveNumber(intentObj.electricalPue)
        ?? extractPositiveNumber(intentObj.pueValue)
        ?? extractPositiveNumber(intentObj.pue)
        ?? extractPositiveNumber(intentObj.pueTarget)
      : undefined)
    ?? (datasetsCooling ? extractPositiveNumber(datasetsCooling.resolvedPue) : undefined);

  let electricalPue: number | undefined;
  let hasExplicitElectricalPue: boolean | undefined;
  if (isExplicitUnknownPue || (hasExplicitPueField && localNumericPue === undefined)) {
    electricalPue = undefined;
    hasExplicitElectricalPue = false;
  } else if (localNumericPue !== undefined) {
    electricalPue = localNumericPue;
    hasExplicitElectricalPue = true;
  } else if (parent !== undefined && (hasLocalIntentContainer || hasNestedRevisionBoundary)) {
    // Nested scenario with its own intent object or revision boundary must not inherit parent PUE
    electricalPue = undefined;
    hasExplicitElectricalPue = false;
  } else if (parent?.electricalPue !== undefined) {
    electricalPue = parent.electricalPue;
    hasExplicitElectricalPue = parent.hasExplicitElectricalPue ?? true;
  } else if (parent === undefined) {
    electricalPue = extractNarrativeBaseCasePue(source);
    if (electricalPue !== undefined) {
      hasExplicitElectricalPue = false;
    }
  }

  const powerAnalysis =
    source.powerAnalysis && typeof source.powerAnalysis === 'object'
      ? (source.powerAnalysis as Record<string, unknown>)
      : undefined;
  const deepDive =
    powerAnalysis?.capacityCreditDeepDive && typeof powerAnalysis.capacityCreditDeepDive === 'object'
      ? (powerAnalysis.capacityCreditDeepDive as Record<string, unknown>)
      : undefined;

  const rawPhases =
    (Array.isArray(source.phases) ? source.phases : undefined)
    ?? (intentObj && Array.isArray(intentObj.phases) ? (intentObj.phases as unknown[]) : undefined)
    ?? (deepDive && Array.isArray(deepDive.perPhase) ? (deepDive.perPhase as unknown[]) : undefined);

  const localPhases = rawPhases ? extractPhasesFromArray(rawPhases) : undefined;
  const phases =
    localPhases
    ?? (parent !== undefined && ('phases' in source || hasLocalIntentContainer || hasNestedRevisionBoundary)
      ? undefined
      : parent?.phases);

  const fullBuildFacilityMw =
    extractPositiveNumber(source.fullBuildFacilityMw)
    ?? (localPhases && localPhases.length > 0 ? localPhases[localPhases.length - 1]!.facilityLoadMw : undefined)
    ?? extractPositiveNumber(source.powerRequirementMW)
    ?? (intentObj ? extractPositiveNumber(intentObj.powerRequirementMW) : undefined)
    ?? (parent !== undefined && (hasLocalIntentContainer || hasNestedRevisionBoundary)
      ? undefined
      : parent?.fullBuildFacilityMw);

  const localWaterScreening = extractWaterScreeningFromSource(source);
  const waterScreening =
    localWaterScreening
    ?? (parent !== undefined && (hasLocalIntentContainer || hasNestedRevisionBoundary || 'fullBuildFacilityMw' in source || 'powerRequirementMW' in source)
      ? undefined
      : parent?.waterScreening);

  const resolvedFamily = !identityConflict
    ? classifySiteIdFamily(siteId)
      ?? classifyReportIdFamily(reportId)
      ?? classifyRevisionIdFamily(revisionId)
    : undefined;
  const isRedheadIdentity = resolvedFamily === 'redhead';
  const isLongspurIdentity = resolvedFamily === 'longspur';
  const isSouthwestIdentity = resolvedFamily === 'southwest';

  const factIndex = collectAdmittedSourceFacts(source, siteId, reportId, revisionId);
  const refsList = Array.isArray(source.references)
    ? source.references
    : Array.isArray(source.citations)
      ? source.citations
      : [];
  let sourceSisRefOrdinal: number | undefined;
  let sourcePinRefOrdinal: number | undefined;
  let sourceBomBessRefOrdinal: number | undefined;
  let sourceBomPvRefOrdinal: number | undefined;
  let sourceHollyRidgeRefOrdinal: number | undefined;
  let sourceG3516CanonicalRef: number | undefined;
  let sourceG3516LegacyRef: number | undefined;
  let sourcePhaseWaterMethodologyRef: number | undefined;
  let sisDisqualified = false;
  let pinDisqualified = false;
  let bomDisqualified = false;
  let hollyRidgeDisqualified = false;
  let g3516Disqualified = false;
  let ashraeDisqualified = false;

  for (let i = 0; i < refsList.length; i++) {
    const item = refsList[i];
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const num = typeof r.number === 'number' ? r.number : i + 1;
    const idStr = `${String(r.id ?? '')} ${String(r.documentId ?? '')} ${String(r.sourceId ?? '')}`;
    const labelStr = `${String(r.source ?? '')} ${String(r.label ?? '')} ${String(r.title ?? '')}`;
    const fieldStr = String(r.field ?? '');
    const combinedDesc = `${idStr} ${labelStr}`;
    const isSisMatch = /System\s+Impact\s+Study.*Redhead|BECI.*System\s+Impact\s+Study/i.test(combinedDesc);
    const isPinMatch = /Redhead\s+Infrastructure\s+Proximity/i.test(combinedDesc);
    const isBomMatch = /Generation\s*&\s*Impact\s*BOM|southwest-crossing-bom-demo\.csv|Southwest_Crossing_BOM\.xlsx/i.test(combinedDesc);
    const isHollyRidgeMatch = /Holly\s+Ridge.*Investor\s+Presentation|26-0409\s+InvestorDeck\.pptx/i.test(combinedDesc);
    const isG3516Match = /Prelim\s+Site\s+Plan\s*&\s*Gen\s+Yard\s+Layout|Caterpillar\s+G3516|Catepillar\s*-\s*Area\s+View\s+Clayco\s+DC\s+150\s*x\s*G3516/i.test(combinedDesc);
    const isAshraeMatch =
      /\bASHRAE\b/i.test(labelStr)
      && /\bPsychrometrics\b/i.test(labelStr)
      && (fieldStr === 'economicEvidence.phaseWaterScreening' || isLongspurIdentity);

    if (
      isRecordDisqualifiedOrAliased(
        r,
        siteId,
        reportId,
        revisionId,
        factIndex.disqualifiedDocIds,
        factIndex.disqualifiedDocNames,
      )
    ) {
      if (isSisMatch) sisDisqualified = true;
      if (isPinMatch) pinDisqualified = true;
      if (isBomMatch) bomDisqualified = true;
      if (isHollyRidgeMatch) hollyRidgeDisqualified = true;
      if (isG3516Match) g3516Disqualified = true;
      if (isAshraeMatch) ashraeDisqualified = true;
      continue;
    }

    if (isSisMatch && isReferenceBackedByAdmittedFacts(r, 'redheadSis', factIndex)) {
      sourceSisRefOrdinal = num;
    }
    if (isPinMatch && isReferenceBackedByAdmittedFacts(r, 'redheadPin', factIndex)) {
      sourcePinRefOrdinal = num;
    }
    if (isBomMatch) {
      if (isReferenceBackedByAdmittedFacts(r, 'bomBess', factIndex)) {
        sourceBomBessRefOrdinal = num;
      }
      if (isReferenceBackedByAdmittedFacts(r, 'bomPv', factIndex)) {
        sourceBomPvRefOrdinal = num;
      }
    }
    if (isHollyRidgeMatch && isReferenceBackedByAdmittedFacts(r, 'hollyRidge', factIndex)) {
      sourceHollyRidgeRefOrdinal = num;
    }
    if (isG3516Match && isReferenceBackedByAdmittedFacts(r, 'g3516', factIndex)) {
      sourceG3516CanonicalRef = num;
      if (typeof r.legacyNumber === 'number') {
        sourceG3516LegacyRef = r.legacyNumber;
      }
    }
    if (isAshraeMatch) {
      sourcePhaseWaterMethodologyRef = num;
    }
  }

  const pdfDataSources = extractCitationOrdinalsFromPdfDataSources(source, siteId, reportId, revisionId);
  if (pdfDataSources.bomDisqualified) bomDisqualified = true;
  if (pdfDataSources.hollyRidgeDisqualified) hollyRidgeDisqualified = true;
  if (pdfDataSources.g3516Disqualified) g3516Disqualified = true;

  if (sisDisqualified) sourceSisRefOrdinal = undefined;
  if (pinDisqualified) sourcePinRefOrdinal = undefined;
  if (bomDisqualified) {
    sourceBomBessRefOrdinal = undefined;
    sourceBomPvRefOrdinal = undefined;
  } else {
    if (sourceBomBessRefOrdinal === undefined && factIndex.hasAdmittedFact.bomBess) {
      sourceBomBessRefOrdinal = pdfDataSources.bomRefOrdinal;
    }
    if (sourceBomPvRefOrdinal === undefined && factIndex.hasAdmittedFact.bomPv) {
      sourceBomPvRefOrdinal = pdfDataSources.bomRefOrdinal;
    }
  }
  if (hollyRidgeDisqualified) sourceHollyRidgeRefOrdinal = undefined;
  else if (sourceHollyRidgeRefOrdinal === undefined) sourceHollyRidgeRefOrdinal = pdfDataSources.hollyRidgeRefOrdinal;
  if (g3516Disqualified) {
    sourceG3516CanonicalRef = undefined;
    sourceG3516LegacyRef = undefined;
  } else if (sourceG3516CanonicalRef === undefined) {
    sourceG3516CanonicalRef = pdfDataSources.g3516CanonicalRef;
  }
  if (ashraeDisqualified) sourcePhaseWaterMethodologyRef = undefined;

  let phaseWaterMethodologyProvenance: PhaseWaterMethodologyProvenance | undefined =
    !identityConflict && !hasNestedRevisionBoundary && !ashraeDisqualified
      ? parent?.phaseWaterMethodologyProvenance
      : undefined;
  if (!identityConflict && !ashraeDisqualified && sourcePhaseWaterMethodologyRef !== undefined) {
    const receiptCheck = verifyModelRunReceipt(source, siteId, reportId, revisionId);
    if (receiptCheck.methodologyRef !== undefined) {
      phaseWaterMethodologyProvenance = {
        methodologyRef: receiptCheck.methodologyRef,
        hasExecutedReceipt: receiptCheck.hasExecutedPhaseWaterModelReceipt,
      };
    }
  } else if (
    identityConflict
    || hasNestedRevisionBoundary
    || ashraeDisqualified
    || (siteId && !isLongspurIdentity && sourcePhaseWaterMethodologyRef === undefined && parent?.siteId !== siteId)
  ) {
    phaseWaterMethodologyProvenance = undefined;
  }

  const localBoundaryAcreage = isSouthwestIdentity
    ? extractSouthwestBoundaryAcreageFromSource(source, siteId, reportId, revisionId)
    : undefined;
  const localBessPhaseCount = isSouthwestIdentity
    ? extractSouthwestBessPhaseCountFromSource(source, phases, siteId, reportId, revisionId)
    : undefined;

  let southwestProvenance: SouthwestSupplementalProvenance | undefined;
  if (!identityConflict && isSouthwestIdentity) {
    const parentSw = !hasNestedRevisionBoundary ? parent?.southwestProvenance : undefined;
    const resolvedBomOrd = !bomDisqualified
      ? sourceBomBessRefOrdinal ?? parentSw?.bessBomRefOrdinal
      : undefined;
    const resolvedPvOrd = !bomDisqualified
      ? sourceBomPvRefOrdinal ?? parentSw?.pvBomRefOrdinal
      : undefined;
    const resolvedBessPhases = !bomDisqualified
      ? localBessPhaseCount ?? parentSw?.bessPhaseCount
      : undefined;
    const resolvedHollyRidgeOrd = !hollyRidgeDisqualified
      ? sourceHollyRidgeRefOrdinal ?? parentSw?.foreignAcreage?.foreignRefOrdinal
      : undefined;
    const resolvedG3516CanonOrd = !g3516Disqualified
      ? sourceG3516CanonicalRef ?? parentSw?.g3516CanonicalRefOrdinal
      : undefined;
    const resolvedG3516LegacyOrd = !g3516Disqualified
      ? sourceG3516LegacyRef ?? parentSw?.g3516LegacyRefOrdinal ?? 118
      : undefined;

    const inheritedForeignAcreage = parentSw?.foreignAcreage;
    const resolvedForeignAcreage: SouthwestForeignAcreageProvenance | undefined =
      !hollyRidgeDisqualified && resolvedHollyRidgeOrd !== undefined && (localBoundaryAcreage !== undefined || inheritedForeignAcreage !== undefined)
        ? {
            foreignAcres: 507,
            boundaryAcres: localBoundaryAcreage?.boundaryAcres ?? inheritedForeignAcreage!.boundaryAcres,
            ...(localBoundaryAcreage?.offeringAcres !== undefined
              ? { offeringAcres: localBoundaryAcreage.offeringAcres }
              : inheritedForeignAcreage?.offeringAcres !== undefined
                ? { offeringAcres: inheritedForeignAcreage.offeringAcres }
                : {}),
            ...(localBoundaryAcreage?.phase1Acres !== undefined
              ? { phase1Acres: localBoundaryAcreage.phase1Acres }
              : inheritedForeignAcreage?.phase1Acres !== undefined
                ? { phase1Acres: inheritedForeignAcreage.phase1Acres }
                : {}),
            ...(localBoundaryAcreage?.phase2Acres !== undefined
              ? { phase2Acres: localBoundaryAcreage.phase2Acres }
              : inheritedForeignAcreage?.phase2Acres !== undefined
                ? { phase2Acres: inheritedForeignAcreage.phase2Acres }
                : {}),
            ...(localBoundaryAcreage?.futurePhase3Acres !== undefined
              ? { futurePhase3Acres: localBoundaryAcreage.futurePhase3Acres }
              : inheritedForeignAcreage?.futurePhase3Acres !== undefined
                ? { futurePhase3Acres: inheritedForeignAcreage.futurePhase3Acres }
                : {}),
            ...(localBoundaryAcreage?.operativeMinAcres !== undefined
              ? { operativeMinAcres: localBoundaryAcreage.operativeMinAcres }
              : inheritedForeignAcreage?.operativeMinAcres !== undefined
                ? { operativeMinAcres: inheritedForeignAcreage.operativeMinAcres }
                : {}),
            ...(localBoundaryAcreage?.operativeMaxAcres !== undefined
              ? { operativeMaxAcres: localBoundaryAcreage.operativeMaxAcres }
              : inheritedForeignAcreage?.operativeMaxAcres !== undefined
                ? { operativeMaxAcres: inheritedForeignAcreage.operativeMaxAcres }
                : {}),
            foreignRefOrdinal: resolvedHollyRidgeOrd,
            foreignDeckLabel: inheritedForeignAcreage?.foreignDeckLabel ?? 'Holly Ridge investor deck',
            operativeSiteLabel: inheritedForeignAcreage?.operativeSiteLabel ?? 'Southwest Crossing',
          }
        : undefined;

    if (
      resolvedBomOrd !== undefined
      || resolvedPvOrd !== undefined
      || resolvedForeignAcreage !== undefined
      || resolvedG3516CanonOrd !== undefined
    ) {
      southwestProvenance = {
        ...(resolvedBomOrd !== undefined
          ? {
              bessBomRefOrdinal: resolvedBomOrd,
              ...(resolvedBessPhases !== undefined ? { bessPhaseCount: resolvedBessPhases } : {}),
            }
          : {}),
        ...(resolvedPvOrd !== undefined
          ? {
              pvBomRefOrdinal: resolvedPvOrd,
              pvInverterSummary:
                parentSw?.pvInverterSummary
                ?? '50 × 3.0 MVA = 150 MVA central inverter apparent-power rating [active-power MW AC and verified DC/AC ratio unverified without stated inverter power factor]',
              pvInverterShortSummary:
                parentSw?.pvInverterShortSummary
                ?? '50 × 3.0 MVA = 150 MVA apparent-power inverter rating [active-power MW AC and verified DC/AC ratio unverified without power factor]',
            }
          : {}),
        ...(resolvedForeignAcreage !== undefined ? { foreignAcreage: resolvedForeignAcreage } : {}),
        ...(resolvedG3516CanonOrd !== undefined
          ? {
              g3516LegacyRefOrdinal: resolvedG3516LegacyOrd,
              g3516CanonicalRefOrdinal: resolvedG3516CanonOrd,
            }
          : {}),
      };
    }
  }

  const sisRequestedDeliveryKv =
    !identityConflict && !hasNestedRevisionBoundary && !sisDisqualified
      ? typeof source.sisRequestedDeliveryKv === 'number'
        ? source.sisRequestedDeliveryKv
        : isRedheadIdentity && sourceSisRefOrdinal !== undefined
          ? 34.5
          : parent?.sisRequestedDeliveryKv
      : undefined;
  const sisRequestedDeliveryRefOrdinal =
    !identityConflict && !hasNestedRevisionBoundary && !sisDisqualified
      ? typeof source.sisRequestedDeliveryRefOrdinal === 'number'
        ? source.sisRequestedDeliveryRefOrdinal
        : sourceSisRefOrdinal ?? parent?.sisRequestedDeliveryRefOrdinal
      : undefined;
  const sisNearbyPinRefOrdinal =
    !identityConflict && !hasNestedRevisionBoundary && !pinDisqualified
      ? typeof source.sisNearbyPinRefOrdinal === 'number'
        ? source.sisNearbyPinRefOrdinal
        : sourcePinRefOrdinal ?? parent?.sisNearbyPinRefOrdinal
      : undefined;
  const isSummarySurface = isScreeningSummaryDocumentObject(source) ? true : parent?.isSummarySurface;

  const hasLocalBoundary =
    identityConflict
    || hasNestedRevisionBoundary
    || isExplicitUnknownPue
    || hasExplicitPueField
    || hasLocalIntentContainer
    || 'phases' in source
    || 'fullBuildFacilityMw' in source
    || 'powerRequirementMW' in source
    || localWaterScreening !== undefined
    || 'sisRequestedDeliveryKv' in source
    || isRedheadIdentity
    || isLongspurIdentity
    || isSouthwestIdentity
    || sourcePhaseWaterMethodologyRef !== undefined
    || southwestProvenance !== undefined
    || isSummarySurface !== parent?.isSummarySurface;

  if (
    !hasLocalBoundary
    && !siteId
    && !reportId
    && !revisionId
    && electricalPue === undefined
    && !phases
    && fullBuildFacilityMw === undefined
    && !waterScreening
    && sisRequestedDeliveryKv === undefined
    && isSummarySurface === undefined
    && !phaseWaterMethodologyProvenance
    && !southwestProvenance
  ) {
    return parent;
  }
  return {
    ...(siteId !== undefined ? { siteId } : {}),
    ...(reportId !== undefined ? { reportId } : {}),
    ...(revisionId !== undefined ? { revisionId } : {}),
    ...(identityConflict ? { identityConflict: true } : {}),
    electricalPue,
    ...(hasExplicitElectricalPue !== undefined ? { hasExplicitElectricalPue } : {}),
    fullBuildFacilityMw,
    phases,
    ...(waterScreening ? { waterScreening } : {}),
    ...(sisRequestedDeliveryKv !== undefined ? { sisRequestedDeliveryKv } : {}),
    ...(sisRequestedDeliveryRefOrdinal !== undefined ? { sisRequestedDeliveryRefOrdinal } : {}),
    ...(sisNearbyPinRefOrdinal !== undefined ? { sisNearbyPinRefOrdinal } : {}),
    ...(isSummarySurface !== undefined ? { isSummarySurface } : {}),
    ...(phaseWaterMethodologyProvenance !== undefined ? { phaseWaterMethodologyProvenance } : {}),
    ...(southwestProvenance !== undefined ? { southwestProvenance } : {}),
  };
}

export interface WaterScreeningProjection {
  headlineLabel: string;
  matchedRangeLabel: string;
  crossBoundLabel: string;
  fullDetailParenthetical: string;
  fullShortDetailParenthetical: string;
  summaryClause: string;
  tailClimateClause: string;
  hasVerifiedV2CatalogIdentity: boolean;
}

const WATER_COMPARISON_PARENTHETICAL_RE =
  /\s*\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;|legacy water-screening comparison withheld)[^)]*\)/gi;

const STANDALONE_WATER_COMPARISON_RE =
  /(?:(by over 90%|by ~[\d.]+(?:[–-][\d.]+)?%|by an unverified screening margin)(\s+on a matched model-case basis)?\s*)?\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;[^)]*requires bound site cooling-water model inputs[^)]*|legacy water-screening comparison withheld)[^)]*\)/i;

const STANDALONE_WATER_COMPARISON_GLOBAL_RE =
  /(?:(by over 90%|by ~[\d.]+(?:[–-][\d.]+)?%|by an unverified screening margin)(\s+on a matched model-case basis)?\s*)?\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;[^)]*requires bound site cooling-water model inputs[^)]*|legacy water-screening comparison withheld)[^)]*\)/gi;

const QUALIFIED_DROUGHT_CURTAILMENT_RE =
  /can reduce exposure to regional drought curtailment if a dry or low-water cooling architecture is selected and a verified water supply is secured for(?: the)? modeled adiabatic-assist makeup(?:\s*\([\d.]+[–-][\d.]+\s*MGD annual average(?:\s+at\s+[\d,]+(?:\.\d+)?\s*MW)?\))?,\s*unless full-capacity zero-water dry operation is engineered/g;

const QUALIFIED_WATER_FULL_SENTENCE_RE =
  /Dry cooling with adiabatic assist reduces modeled (?:screening )?cooling-water demand (?:by over 90%|by ~[\d.]+(?:[–-][\d.]+)?%|by an unverified screening margin)(?:\s+on a matched model-case basis)?\s*\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;|legacy water-screening comparison withheld)[^)]*\)\s*(?:relative to evaporative cooling towers|vs\.\s*evaporative towers)\s+(?:in a [-+]?\d+(?:\.\d+)?°C wet-bulb climate\s*\[((?:[a-z_]+\s+)?Ref\s+\d+)\]\s*(?:,\s*|\s+and\s+)5\/5 water-stressed basin\s*\[((?:[a-z_]+\s+)?Ref\s+\d+)\]|(?:in a 5\/5 water-stressed basin\s*)?\[((?:[a-z_]+\s+)?Ref\s+\d+)\],\s*\[((?:[a-z_]+\s+)?Ref\s+\d+)\])(?:,\s*\[(?:[a-z_]+\s+)?Ref\s+\d+\])?\./gi;

const QUALIFIED_WATER_SHORT_CLAUSE_RE =
  /(?:by over 90%|by ~[\d.]+(?:[–-][\d.]+)?%|by an unverified screening margin)(?:\s+on a matched model-case basis)?\s*\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;|legacy water-screening comparison withheld)[^)]*\)\s*(?:relative to evaporative cooling towers|vs\.\s*evaporative towers)/gi;

export function canonicalizeWaterScreeningProse(text: string): string {
  if (!text) return text;
  let next = text;
  if (next.includes('can reduce exposure to regional drought curtailment if a dry or low-water cooling architecture is selected')) {
    next = next.replace(
      QUALIFIED_DROUGHT_CURTAILMENT_RE,
      'insulates continuous AI training operations from regional drought curtailment',
    );
  }
  if (
    next.includes('evaporative cooling towers')
    || next.includes('evaporative towers')
  ) {
    next = next
      .replace(
        QUALIFIED_WATER_FULL_SENTENCE_RE,
        (_m, r1a, r2a, r1b, r2b) =>
          `Dry cooling with adiabatic assist reduces modeled screening cooling-water demand by over 90% relative to evaporative cooling towers in a 5/5 water-stressed basin [${r1a ?? r1b}], [${r2a ?? r2b}].`,
      )
      .replace(
        QUALIFIED_WATER_SHORT_CLAUSE_RE,
        'by over 90% relative to evaporative cooling towers',
      );
  }
  return next;
}

function normalizeDashRange(val: string): string {
  return val.replace(/[\u2012\u2013\u2014\u2212-]/g, '–').replace(/\s+/g, '').trim();
}

function parseRangeEndpoints(val: string): { low: number; high: number } | undefined {
  const norm = normalizeDashRange(val.replace(/%$/, ''));
  const rangeMatch = norm.match(/^(\d+(?:\.\d+)?)–(\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    const low = parseFloat(rangeMatch[1]!);
    const high = parseFloat(rangeMatch[2]!);
    if (Number.isFinite(low) && Number.isFinite(high)) return { low, high };
  }
  const singleMatch = norm.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    const num = parseFloat(singleMatch[1]!);
    if (Number.isFinite(num)) return { low: num, high: num };
  }
  return undefined;
}

export function hasWaterScreeningConflict(
  ws: ScrubWaterScreeningContext | undefined,
  context?: ScrubScenarioContext,
  inTextProse?: string,
): boolean {
  if (!ws) return true;
  if (
    context?.fullBuildFacilityMw !== undefined
    && ws.facilityLoadMw !== undefined
    && Math.abs(context.fullBuildFacilityMw - ws.facilityLoadMw) > 1e-6
  ) {
    return true;
  }
  if (
    context?.electricalPue !== undefined
    && context.hasExplicitElectricalPue !== false
    && typeof ws.pueRangeLabel === 'string'
  ) {
    const pueBounds = parseRangeEndpoints(ws.pueRangeLabel);
    if (
      pueBounds
      && (context.electricalPue < pueBounds.low - 1e-6 || context.electricalPue > pueBounds.high + 1e-6)
    ) {
      return true;
    }
  }
  if (typeof inTextProse === 'string' && inTextProse.length > 0) {
    const surroundingProse = canonicalizeWaterScreeningProse(inTextProse)
      .replace(WATER_COMPARISON_PARENTHETICAL_RE, ' ')
      .replace(/\(modeled\/inferred screening estimate;[^)]*\)/gi, ' ');
    const inTextMwMatches = [...surroundingProse.matchAll(/\b(\d[\d,]*(?:\.\d+)?)\s*MW(?!\s+IT\b)\b/gi)];
    const inTextMwValues = inTextMwMatches
      .map((m) => extractPositiveNumber(m[1]))
      .filter((v): v is number => v !== undefined);
    if (inTextMwValues.length > 0) {
      if (
        ws.facilityLoadMw === undefined
        || !inTextMwValues.some((mw) => Math.abs(mw - ws.facilityLoadMw!) < 1e-6)
      ) {
        return true;
      }
    }
    const pueMatch = surroundingProse.match(/\bPUE\s+([\d.]+(?:[–-][\d.]+)?)/i);
    if (pueMatch) {
      if (!ws.pueRangeLabel || normalizeDashRange(ws.pueRangeLabel) !== normalizeDashRange(pueMatch[1]!)) {
        return true;
      }
    }
    const utilMatch =
      surroundingProse.match(
        /\b(\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?)%\s*(?:illustrative\s+)?(?:point[- ]|annual\s+)?utilization\b/i,
      )
      ?? surroundingProse.match(/\butilization\s+(\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?)%/i);
    if (utilMatch) {
      const inTextUtil = normalizeDashRange(utilMatch[1]!);
      const matchesPoint =
        ws.pointUtilizationPct !== undefined && normalizeDashRange(String(ws.pointUtilizationPct)) === inTextUtil;
      const matchesRange =
        ws.utilizationRangeLabel !== undefined
        && normalizeDashRange(ws.utilizationRangeLabel.replace(/%$/, '')) === inTextUtil;
      if (!matchesPoint && !matchesRange) {
        return true;
      }
    }
    const wbMatch = surroundingProse.match(/([-+]?\d+(?:\.\d+)?)\s*°C\s*(?:average\s+)?wet[- ]bulb/i);
    if (wbMatch) {
      const inTextWb = parseFloat(wbMatch[1]!);
      if (ws.wetBulbTempC !== undefined && Math.abs(ws.wetBulbTempC - inTextWb) > 1e-6) {
        return true;
      }
    }
  }
  return false;
}

export function projectWaterScreeningComparison(
  ws: ScrubWaterScreeningContext | undefined,
  options?: {
    context?: ScrubScenarioContext;
    inTextProse?: string;
    refAshraePsychrometrics?: number;
  },
): WaterScreeningProjection | null {
  if (
    !ws
    || typeof ws.dryLowMgd !== 'number'
    || !Number.isFinite(ws.dryLowMgd)
    || ws.dryLowMgd < 0
    || typeof ws.dryHighMgd !== 'number'
    || !Number.isFinite(ws.dryHighMgd)
    || ws.dryHighMgd < ws.dryLowMgd
    || typeof ws.wetLowMgd !== 'number'
    || !Number.isFinite(ws.wetLowMgd)
    || ws.wetLowMgd <= 0
    || typeof ws.wetHighMgd !== 'number'
    || !Number.isFinite(ws.wetHighMgd)
    || ws.wetHighMgd < ws.wetLowMgd
  ) {
    return null;
  }
  if (hasWaterScreeningConflict(ws, options?.context, options?.inTextProse)) {
    return null;
  }

  const lowToLowPct = (1 - ws.dryLowMgd / ws.wetLowMgd) * 100;
  const highToHighPct = (1 - ws.dryHighMgd / ws.wetHighMgd) * 100;
  const crossBoundPct = (1 - ws.dryHighMgd / ws.wetLowMgd) * 100;
  const minMatchedPct = Math.min(lowToLowPct, highToHighPct);
  const maxMatchedPct = Math.max(lowToLowPct, highToHighPct);
  const matchedRangeLabel = `~${minMatchedPct.toFixed(1)}–${maxMatchedPct.toFixed(1)}%`;
  const crossBoundLabel = `~${crossBoundPct.toFixed(1)}%`;
  const headlineLabel =
    minMatchedPct > 90
      ? 'by over 90%'
      : Math.abs(maxMatchedPct - minMatchedPct) < 0.05
        ? `by ~${minMatchedPct.toFixed(1)}%`
        : `by ~${minMatchedPct.toFixed(1)}–${maxMatchedPct.toFixed(1)}%`;

  const hasVerifiedV2CatalogIdentity = ws.hasCatalogV2Scenarios === true;
  const catalogClause = hasVerifiedV2CatalogIdentity
    ? ' under the deterministic waterScreeningView v2 catalog screen [dry-cooling-process-water-v1 vs epa-wet-cooling-tower-water-v2-withdrawal]'
    : '';
  const shortCatalogClause = hasVerifiedV2CatalogIdentity
    ? ' under the deterministic waterScreeningView v2 catalog screen'
    : '';

  const paramParts: string[] = [];
  const summaryParamParts: string[] = [];
  if (typeof ws.facilityLoadMw === 'number' && Number.isFinite(ws.facilityLoadMw)) {
    const mwFormatted = ws.facilityLoadMw.toLocaleString('en-US');
    paramParts.push(`${mwFormatted} MW facility load`);
    summaryParamParts.push(`${mwFormatted} MW`);
  }
  if (typeof ws.pueRangeLabel === 'string' && ws.pueRangeLabel.trim().length > 0) {
    paramParts.push(`PUE ${ws.pueRangeLabel.trim()}`);
    summaryParamParts.push(`PUE ${ws.pueRangeLabel.trim()}`);
  }
  if (typeof ws.pointUtilizationPct === 'number' && Number.isFinite(ws.pointUtilizationPct)) {
    if (typeof ws.utilizationRangeLabel === 'string' && ws.utilizationRangeLabel.trim().length > 0) {
      paramParts.push(
        `${ws.pointUtilizationPct}% point-utilization reference within the ${ws.utilizationRangeLabel.trim()} annual utilization sensitivity band`,
      );
    } else {
      paramParts.push(`${ws.pointUtilizationPct}% point-utilization reference`);
    }
    summaryParamParts.push(`${ws.pointUtilizationPct}% util.`);
  } else if (typeof ws.utilizationRangeLabel === 'string' && ws.utilizationRangeLabel.trim().length > 0) {
    paramParts.push(`${ws.utilizationRangeLabel.trim()} annual utilization`);
    summaryParamParts.push(`${ws.utilizationRangeLabel.trim()} util.`);
  }

  const paramsClause = paramParts.length > 0 ? ` at ${paramParts.join(', ')}` : '';
  const tailClimateClause =
    typeof ws.wetBulbTempC === 'number' && Number.isFinite(ws.wetBulbTempC)
      ? `in a ${ws.wetBulbTempC}°C wet-bulb climate [$1] and 5/5 water-stressed basin [$2].`
      : 'in a 5/5 water-stressed basin [$1], [$2].';

  const fullDetailParenthetical = `${matchedRangeLabel} comparing low-to-low ${ws.dryLowMgd} vs ${ws.wetLowMgd} MGD or high-to-high ${ws.dryHighMgd} vs ${ws.wetHighMgd} MGD; ${crossBoundLabel} cross-bound comparing high adiabatic ${ws.dryHighMgd} MGD to low evaporative ${ws.wetLowMgd} MGD${catalogClause}${paramsClause} — conditional modeled/inferred water-screen calculation, not verified site savings or an independent model-run artifact`;
  const fullShortDetailParenthetical = `${matchedRangeLabel} comparing low-to-low ${ws.dryLowMgd} vs ${ws.wetLowMgd} MGD or high-to-high ${ws.dryHighMgd} vs ${ws.wetHighMgd} MGD; ${crossBoundLabel} cross-bound comparing high adiabatic ${ws.dryHighMgd} MGD to low evaporative ${ws.wetLowMgd} MGD${shortCatalogClause}${paramsClause} — conditional modeled/inferred water-screen calculation, not verified site savings or an independent model-run artifact`;

  const summaryParamsBracket = summaryParamParts.length > 0 ? ` [${summaryParamParts.join(', ')}]` : '';
  const summaryCatalogSegment = hasVerifiedV2CatalogIdentity
    ? `; deterministic waterScreeningView v2 catalog screen${summaryParamsBracket}`
    : summaryParamParts.length > 0
      ? `; modeled parameters${summaryParamsBracket}`
      : '';
  const resolvedRefAshrae = options?.refAshraePsychrometrics ?? ws.refAshraePsychrometrics;
  const ashraeSegment =
    resolvedRefAshrae && resolvedRefAshrae > 0
      ? `; psychrometric equations methodology [Ref ${resolvedRefAshrae}]`
      : '';
  const summaryClause = ` (${matchedRangeLabel} matched ${ws.dryLowMgd} vs ${ws.wetLowMgd} / ${ws.dryHighMgd} vs ${ws.wetHighMgd} MGD; ${crossBoundLabel} cross-bound ${ws.dryHighMgd} vs ${ws.wetLowMgd} MGD${summaryCatalogSegment}${ashraeSegment} — conditional modeled calculation, not a verified model-run artifact or guaranteed site savings)`;

  return {
    headlineLabel,
    matchedRangeLabel,
    crossBoundLabel,
    fullDetailParenthetical,
    fullShortDetailParenthetical,
    summaryClause,
    tailClimateClause,
    hasVerifiedV2CatalogIdentity,
  };
}

export function projectSummaryWaterRationale(
  text: string,
  waterProjection: WaterScreeningProjection | null,
): string {
  const summaryWaterScreenClause = waterProjection
    ? waterProjection.summaryClause
    : ' (legacy water-screening comparison withheld — requires recalculation for current design inputs)';
  const reconciledRationale = text
    .replace(
      /\b(?:by over 90%|by ~[\d.]+(?:[–-][\d.]+)?%|by an unverified screening margin)(?=\s+(?:on a matched model-case basis|relative to evaporative cooling towers|vs\.\s*evaporative towers|\())/gi,
      waterProjection ? waterProjection.headlineLabel : 'by an unverified screening margin',
    )
    .replace(
      /\s*\((?:~?\d+(?:\.\d+)?(?:[–-]\d+(?:\.\d+)?)?%\s*(?:comparing|matched)|modeled\/inferred screening estimate;|legacy water-screening comparison withheld)[^)]*\)/gi,
      summaryWaterScreenClause,
    );
  return reconciledRationale.length > 260
    ? reconciledRationale
        .replace(/^Our deterministic water-use screen shows that full wet evaporative cooling towers[^.!?]*[.!?]\s*/i, '')
        .replace(/modeled screening cooling-water demand/i, 'modeled cooling-water demand')
        .replace(/in a [\d.+-]+°C wet-bulb climate\s*(\[(?:[a-z_]+\s+)?Ref\s+\d+\])(?:,\s*|\s+and\s+)5\/5 water-stressed basin\s*(\[(?:[a-z_]+\s+)?Ref\s+\d+\])/i, '$1, $2')
        .replace(/relative to evaporative cooling towers in a 5\/5 water-stressed basin/i, 'vs. evaporative towers')
        .replace(/\s*\([\d.]+ MW IT load at PUE ([\d.]+) and (\d+%) utilization\)/i, ' (PUE $1, $2 utilization)')
        .replace(/,\s*avoiding the [\d,]+ to [\d,]+ gal\/day average-day modeled withdrawal sensitivity of wet cooling towers/i, '')
        .replace(/,\s*alongside Total site water[\s\S]*?(?=\s*(?:\[(?:[a-z_]+\s+)?Ref\s+\d+\][,\s]*)+[.!?])/i, '')
        .replace(/\s*Separately,\s*"[^"]+"\s*records a source-stated assumed site water allocation[\s\S]*$/i, '')
    : reconciledRationale;
}

function reconcileCustomerTextLeaf(body: string, context?: ScrubScenarioContext): string {
  if (!body || body.startsWith('STAGE 1 SCREENING CONTRACT')) return body;
  let next = body;

  if (next.includes('UNKNOWN205186') && next.includes('345 kV')) {
    next = next
      .replace(
        /UNKNOWN205186 at 7\.3 km \(345 kV\)/g,
        'UNKNOWN205186 at 7.3 km (station voltage not established in HIFLD; 345 kV reflects the separate proposed MPR Switching Station in the Tri-State study)',
      )
      .replace(
        /UNKNOWN205186 located 7\.3 km away and rated at 345 kV/g,
        'UNKNOWN205186 located 7.3 km away (station voltage not established in HIFLD; 345 kV reflects the separate proposed MPR Switching Station in the Tri-State study)',
      )
      .replace(
        /UNKNOWN205186 at 7\.3 km with an estimated interconnection voltage of 345 kV \(parsed from grid\.voltage\)/g,
        'UNKNOWN205186 at 7.3 km (station voltage not established in HIFLD; 345 kV parsed from the separate proposed MPR Switching Station in the Tri-State study)',
      )
      .replace(
        /HIFLD maps 345 kV substation UNKNOWN205186 at 7\.3 km/g,
        'HIFLD maps substation feature UNKNOWN205186 at 7.3 km (station voltage not established in HIFLD)',
      );
  }

  if (next.includes('36.5 USD/MWh')) {
    next = next.replace(
      /yields an? (?:un)?verified LCOE fuel cost of 36\.5 USD\/MWh/g,
      'yields a modeled fuel-only benchmark of $36.54/MWh at an assumed 9,000 Btu/kWh heat rate (excluding capital, O&M, firming, and lateral charges; not total LCOE)',
    );
  }

  const isLongspur = context?.siteId === LONGSPUR_SITE_ID;
  const ws = context?.waterScreening;
  next = canonicalizeWaterScreeningProse(next);
  const hasBoundDryMakeupRates =
    ws !== undefined
    && typeof ws.dryLowMgd === 'number'
    && typeof ws.dryHighMgd === 'number'
    && !hasWaterScreeningConflict(ws, context, next);

  if (next.includes('insulates continuous AI training operations from regional drought curtailment')) {
    const dryMakeupQualification =
      hasBoundDryMakeupRates && ws
        ? ws.facilityLoadMw !== undefined
          ? ` (${ws.dryLowMgd}–${ws.dryHighMgd} MGD annual average at ${ws.facilityLoadMw.toLocaleString('en-US')} MW)`
          : ` (${ws.dryLowMgd}–${ws.dryHighMgd} MGD annual average)`
        : '';
    next = next.replace(
      /insulates continuous AI training operations from regional drought curtailment/g,
      `can reduce exposure to regional drought curtailment if a dry or low-water cooling architecture is selected and a verified water supply is secured for${dryMakeupQualification ? ' the' : ''} modeled adiabatic-assist makeup${dryMakeupQualification}, unless full-capacity zero-water dry operation is engineered`,
    );
  }

  const hasOver90WaterClaim = next.includes('by over 90% relative to evaporative cooling towers');
  const hasLegacyWaterComparisonParenthetical =
    !hasOver90WaterClaim
    && STANDALONE_WATER_COMPARISON_RE.test(next);

  if (hasOver90WaterClaim) {
    const proj = projectWaterScreeningComparison(ws, { context, inTextProse: next });
    if (proj) {
      next = next
        .replace(
          /Dry cooling with adiabatic assist reduces modeled screening cooling-water demand by over 90% relative to evaporative cooling towers in a 5\/5 water-stressed basin\s*\[((?:verified\s+)?Ref 1)\],\s*\[((?:verified\s+)?Ref 17)\](?:,\s*\[(?:verified\s+|inferred\s+)?Ref 87\])?\./g,
          `Dry cooling with adiabatic assist reduces modeled screening cooling-water demand ${proj.headlineLabel} on a matched model-case basis (${proj.fullDetailParenthetical}) relative to evaporative cooling towers ${proj.tailClimateClause}`,
        )
        .replace(
          /by over 90% relative to evaporative cooling towers/g,
          `${proj.headlineLabel} on a matched model-case basis (${proj.fullShortDetailParenthetical}) relative to evaporative cooling towers`,
        );
    } else {
      next = next.replace(
        /by over 90% relative to evaporative cooling towers/g,
        'by an unverified screening margin (modeled/inferred screening estimate; not verified site savings — requires bound site cooling-water model inputs and low/high operating-bound comparison) relative to evaporative cooling towers',
      );
    }
    if (context?.isSummarySurface) {
      next = projectSummaryWaterRationale(next, proj);
    }
  } else if (hasLegacyWaterComparisonParenthetical) {
    const proj = projectWaterScreeningComparison(ws, { context, inTextProse: next });
    if (context?.isSummarySurface) {
      next = projectSummaryWaterRationale(next, proj);
    } else if (!proj) {
      next = next.replace(
        STANDALONE_WATER_COMPARISON_GLOBAL_RE,
        (_m, headline) =>
          headline
            ? 'by an unverified screening margin (legacy water-screening comparison withheld — requires recalculation for current design inputs)'
            : '(legacy water-screening comparison withheld — requires recalculation for current design inputs)',
      );
    } else {
      next = next.replace(
        STANDALONE_WATER_COMPARISON_GLOBAL_RE,
        (_m, headline, basis) =>
          headline
            ? `${proj.headlineLabel}${basis || ' on a matched model-case basis'} (${proj.fullDetailParenthetical})`
            : `(${proj.fullDetailParenthetical})`,
      );
    }
  }

  if (next.includes('while local data-center land use has no blocker identified (not an approval or legal clearance) and on-site generation land use remains unresolved')) {
    next = next.replace(
      /while local data-center land use has no blocker identified \(not an approval or legal clearance\) and on-site generation land use remains unresolved/g,
      'while local data-center land use remains unresolved (source not admitted as official parcel zoning — developer memo cites county PUD precedent only, with no blocker identified in that precedent memo; not an approval or legal clearance) and on-site generation land use remains unresolved',
    );
  }

  if (next.includes('Confirm Tri-State HIL study participation and $250,000 fee submission under MPR-26-HW05')) {
    next = next.replace(
      /Confirm Tri-State HIL study participation and \$250,000 fee submission under MPR-26-HW05/g,
      'Confirm Tri-State HIL study participation (October 28, 2026 participation deadline) and $250,000 fee submission under MPR-26-HW05',
    );
  }

  if (next.includes('The rack density it implies forces the cooling technology')) {
    next = next.replace(
      'The rack density it implies forces the cooling technology',
      'The indicative rack-density envelope informs candidate cooling architectures (conditional on actual project/OEM thermal design)',
    );
  }

  if (next.includes('Direct-to-chip liquid mandatory') || next.includes('Direct-to-chip liquid required above')) {
    next = next
      .replace(
        /Direct-to-chip liquid mandatory\s*[—–-]\s*air fails >~40[–-]50 kW\/rack;\s*immersion for >150/g,
        'Indicative envelope (conditional on OEM/thermal design; project selection not established): direct-to-chip liquid typically adopted above ~40–50 kW/rack (conventional air practical limit); immersion candidate for >150 kW/rack',
      )
      .replace(
        /Direct-to-chip liquid required above ~40[–-]50 kW\/rack \(conventional air practical limit\);\s*immersion for >150/g,
        'Indicative envelope (conditional on OEM/thermal design; project selection not established): direct-to-chip liquid typically adopted above ~40–50 kW/rack (conventional air practical limit); immersion candidate for >150 kW/rack',
      );
  }

  if (next.includes('528.13 acres (92.2%) in Zone X')) {
    next = next
      .replace(
        /and 528\.13 acres \(92\.2%\) in Zone X,\s*though overlapping FEMA records disagree and negative coverage remains unresolved/g,
        'while the remaining 528.13 acres (92.2%) outside mapped Zone A carry an unverified/disputed source Zone X classification (parcel-complete Zone X is not established because overlapping FEMA records disagree and negative coverage remains unresolved)',
      )
      .replace(
        /and 528\.13 acres \(92\.2%\) in Zone X\s*\(though negative coverage is unresolved due to missing exact-version FEMA context binding\)/g,
        'while the remaining 528.13 acres (92.2%) outside mapped Zone A carry an unverified/disputed source Zone X classification (parcel-complete Zone X is not established because negative coverage and exact-version FEMA context binding remain unresolved)',
      );
  }

  if (next === 'Avg Ambient Temp: 8.8 °C (green). Wet Bulb Temp: 4.1 °C (green). Water Stress Index: High (red)') {
    next = 'Climate opportunity: Avg Ambient Temp: 8.8 °C (green); Wet Bulb Temp: 4.1 °C (green, historical screening). Supply & design readiness: Water Stress Index: High (red) – score 67 blends favorable climate proxy with unverified water-supply & cooling-architecture readiness (prerequisite gate: verify physical water supply/rights and thermal design)';
  }

  if (
    next === 'Nuclear Permitting: Favorable (green). Zoning Alignment: not specified (evidence gap — excluded from scoring). Env. Constraints: Moderate (amber). NRC Reactor Proximity: not specified (evidence gap — excluded from scoring)'
    || next === 'Nuclear Permitting: Favorable (green). Zoning Alignment: not specified (evidence gap – excluded from scoring). Env. Constraints: Moderate (amber). NRC Reactor Proximity: not specified (evidence gap – excluded from scoring)'
  ) {
    next = isLongspur
      ? 'State policy proxy: Nuclear Permitting: Favorable (green, WY geographic proxy only; Tri-State utility study doc-1791195957188-1zhigb establishes WY location, not nuclear licensing or zoning). Readiness gates: Zoning Alignment: not specified (evidence gap – excluded from 73 partial-basis score; prerequisite gate: confirm local zoning); Env. Constraints: Moderate (amber); NRC Reactor Proximity: not specified (evidence gap – excluded from scoring)'
      : 'State policy proxy: Nuclear Permitting: Favorable (green, state-level policy proxy only, not site-specific nuclear licensing or zoning). Readiness gates: Zoning Alignment: not specified (evidence gap – excluded from partial-basis score; prerequisite gate: confirm local zoning); Env. Constraints: Moderate (amber); NRC Reactor Proximity: not specified (evidence gap – excluded from scoring)';
  }

  if (next === 'Nuclear permitting: Favorable state stance') {
    next = isLongspur
      ? 'Nuclear permitting: Favorable state stance (WY geographic policy proxy only; Tri-State utility study doc-1791195957188-1zhigb establishes WY location, not claim-level nuclear licensing verification)'
      : 'Nuclear permitting: Favorable state stance (state-level policy proxy only, not claim-level nuclear licensing verification)';
  }

  if (
    isLongspur
    && (
      next === 'Zoning: not specified — confirm designation with the local jurisdiction'
      || next === 'Zoning: not specified – confirm designation with the local jurisdiction'
    )
  ) {
    next = 'Zoning: not specified – confirm designation with the local jurisdiction (Tri-State utility study doc-1791195957188-1zhigb establishes WY location only, not local zoning verification)';
  }

  if (isLongspur && next.includes('the canonical estimated wind capacity factor and overall capacity factor are not established in the SED')) {
    next = next.replace(
      'the canonical estimated wind capacity factor and overall capacity factor are not established in the SED',
      'the canonical site-specific estimated wind capacity factor and overall capacity factor are not established in the SED (the raw 0.24 / 24% field in datasets.wind is unverified/unknown in the stored SED7 payload because no turbine power curve, hub-height loss model, or derivation is retained)',
    );
  }

  if (next.includes('user-provided site geometry metadata and USGS 3DEP sampling')) {
    next = next.replace(
      /user-provided site geometry metadata and USGS 3DEP sampling/g,
      isLongspur
        ? 'user-provided site geometry metadata and USGS 3DEP 1-arc-second grid summary statistics (refdata-3dep:grid, n=40: min 1,833.1 m, avg 1,856.2 m, max 1,872.6 m, stdev 11.5 m; individual 40-point coordinates/datum and 3% slope-proxy derivation are unverified/not retained in the stored SED7 payload)'
        : 'user-provided site geometry metadata and USGS 3DEP regional grid sampling (regional relief proxy only; individual sample coordinates/datum and local parcel slope derivation are unverified)',
    );
  }

  if (!isLongspur) {
    if (
      next.includes('doc-1791195957188-1zhigb')
      && !/\b(?:rejected|foreign|not\s+a\s+bound|unbound)\b/i.test(next)
    ) {
      next = next
        .replace(
          /\s*\((?:WY geographic(?:\s+policy)? proxy only;\s*)?Tri-State utility study doc-1791195957188-1zhigb establishes WY location(?:\s+only)?,\s*not (?:nuclear licensing or zoning|claim-level nuclear licensing verification|local zoning verification)\)/gi,
          ' (foreign/unbound source doc-1791195957188-1zhigb is not bound to this site; local jurisdiction, licensing, and zoning remain unverified)',
        )
        .replace(
          /Provenance qualification:\s*doc:doc-1791195957188-1zhigb is the Tri-State utility feasibility study[^.]*\./gi,
          'Provenance qualification: doc:doc-1791195957188-1zhigb is a foreign/unbound source record (not bound to this site and excluded as site evidence; local jurisdiction, nuclear licensing, and parcel zoning are not established by it).',
        )
        .replace(
          /\b(doc:doc-1791195957188-1zhigb|doc-1791195957188-1zhigb)\b/g,
          '$1 (foreign/unbound source record — not bound to this site)',
        );
    }
    if (next.includes('1,833.1 m, avg 1,856.2 m, max 1,872.6 m')) {
      next = next.replace(
        /1-arc-second grid summary statistics\s*\(refdata-3dep:grid,\s*n=40:\s*min 1,833\.1 m,\s*avg 1,856\.2 m,\s*max 1,872\.6 m,\s*stdev 11\.5 m;[^)]*\)/gi,
        'regional grid sampling (regional relief proxy only; individual sample coordinates/datum and local parcel slope derivation are unverified)',
      );
    }
    if (/1,833\.1[–-]1,872\.6\s*m/i.test(next)) {
      next = next.replace(
        /1,833\.1[–-]1,872\.6\s*m(?:\s+across\s+40-point\s+grid)?/gi,
        'regional elevation sample (local parcel slope unverified)',
      );
    }
  }

  if (next.includes('*(per "') || /\.(?:pdf|docx)\s+"/i.test(next)) {
    next = next
      .replace(/\*\(\s*per\s+"([^"]+)"(\s*,\s*doc:\/\/[^)\s]+)?\s*\)\*/g, (_, file: string, docRef?: string) => `(per "${file.trim()}"${docRef ?? ''})`)
      .replace(/"([^"\n]+\.(?:pdf|docx|xlsx|csv))\s+"/gi, (_, file: string) => `"${file.trim()}"`);
  }

  if (
    next.includes('Modeled basis: PUE 1.15–1.30 (screening assumption);')
    || next.includes('Modeled basis: PUE 1.15–1.30 (water-screening sensitivity')
  ) {
    const pue = context?.electricalPue;
    if (typeof pue === 'number' && Number.isFinite(pue) && pue > 0) {
      next = next.replace(
        /Modeled basis: PUE 1\.15[–-]1\.30 \((?:screening assumption|water-screening sensitivity(?:;[^)]*)?)\);([^\n]*?\bfacility heat\s+([\d,]+(?:\.\d+)?)\s*MW(?![\s–-]*\d))/g,
        (_match, tail: string, mwRaw: string) => {
          const mw = Number(mwRaw.replace(/,/g, ''));
          if (Number.isFinite(mw) && mw > 0) {
            const itMw = Number((mw / pue).toFixed(1));
            const matchedPhase = context?.phases?.find((p) => Math.abs(p.facilityLoadMw - mw) < 1e-3);
            let scopeLabel = 'electrical base-case';
            if (matchedPhase) {
              if (matchedPhase.isFullBuild && isLongspur) {
                scopeLabel = 'full-build Project Intent electrical base-case';
              } else if (matchedPhase.isFullBuild) {
                scopeLabel = `${matchedPhase.label} full-build electrical base-case`;
              } else {
                scopeLabel = `${matchedPhase.label} electrical base-case`;
              }
            }
            return `Modeled basis: PUE 1.15–1.30 (water-screening sensitivity; vs. ${scopeLabel} PUE ${pue} / ${itMw} MW IT at ${mwRaw} MW facility load);${tail}`;
          }
          return `Modeled basis: PUE 1.15–1.30 (water-screening sensitivity; vs. electrical base-case PUE ${pue});${tail}`;
        },
      );
      if (next.includes('Modeled basis: PUE 1.15–1.30 (screening assumption);')) {
        const fullMw = context?.fullBuildFacilityMw;
        const comparisonClause =
          isLongspur && typeof fullMw === 'number' && Number.isFinite(fullMw) && fullMw > 0
            ? `vs. Project Intent electrical base-case PUE ${pue} / ${Number((fullMw / pue).toFixed(1))} MW IT`
            : `vs. electrical base-case PUE ${pue}`;
        next = next.replace(
          'Modeled basis: PUE 1.15–1.30 (screening assumption);',
          `Modeled basis: PUE 1.15–1.30 (water-screening sensitivity; ${comparisonClause});`,
        );
      }
    }
  }

  if (next === 'CRITICAL: Flood Risk, Flood risk: High (score 20)') {
    next =
      'CRITICAL: Flood Risk — High floodplain hazard requires site-specific hydraulic study, ALTA survey, and jurisdictional wetland delineation to verify net buildable envelope outside mapped flood zones [Role: Civil/Water-Resources Engineer | Deliverable: Hydraulic & ALTA survey | Closure: Verified flood-free buildable pad outside FEMA Zone A & delineated wetlands] (score 20)';
  }

  if (next === 'Site & Land: requires mitigation (score 65)') {
    next = isLongspur
      ? 'Site & Land + Permitting Readiness: requires mitigation — verify net buildable acreage, confirm local zoning for both data-center and on-site generation use, obtain stationary-source air-permit applicability and BTM ownership/sale determinations, and complete Phase I ESA (conditional Phase II if RECs found) plus borehole geotechnical investigation [Role: Civil/Geotech & Permitting Lead | Deliverable: Buildable-area survey, zoning/air/PSC determinations & borehole geotech report | Closure: Verified buildable pad, borehole geotech clearance, and Laramie County data-center/BTM generation zoning, WDEQ air & WY PSC ownership/sale determinations] (score 65)'
      : 'Site & Land + Permitting Readiness: requires mitigation — verify net buildable acreage, confirm local zoning for both data-center and on-site generation use, obtain stationary-source air-permit applicability and BTM ownership/sale determinations, and complete Phase I ESA (conditional Phase II if RECs found) plus borehole geotechnical investigation [Role: Civil/Geotech & Permitting Lead | Deliverable: Buildable-area survey, zoning/air/regulatory determinations & borehole geotech report | Closure: Verified buildable pad, borehole geotech clearance, and local data-center/BTM generation zoning, state/local air-permit & utility/regulatory ownership/sale determinations] (score 65)';
  }

  if (next === 'Grid & Power Infrastructure: requires mitigation (score 50)') {
    next =
      'Grid Interconnection & Firm BTM Fuel Readiness: requires mitigation — advance utility interconnection studies (cost allocation, delivery-point substation scope, and post-agreement schedule) and execute binding firm fuel supply, transport, and lateral construction contracts (a preliminary study or non-binding term sheet does not clear the firm-fuel gate) plus OEM site-rated N-1 dispatchable plant and transient stability design [Role: Power Systems & Commercial Energy Lead | Deliverable: Utility system impact study, executed firm fuel/transport/lateral contracts & site-rated N-1/transient design | Closure: Executed IA with grid-mode/protection clearance and/or binding firm fuel supply, transport & lateral contracts (with verified delivery pressure, fuel quality/heating value [Btu/scf & Wobbe index], hourly/daily deliverability, commissioning schedule & curtailment/outage terms) plus verified OEM site-rated N-1 & transient design] (score 50)';
  }

  if (next === 'Logistics & Access: requires mitigation (score 40)') {
    next =
      'Logistics, Water/Thermal & Carrier Connectivity Readiness: requires mitigation — verify physical water supply/rights and peak active-hour delivery capacity alongside multi-year ASHRAE thermal design (climate screening scores do not establish water-supply or cooling readiness), and confirm diverse carrier fiber routes and optical latency [Role: Thermal/Water Lead & Network Architect | Deliverable: Peak thermal/water-supply study & diverse carrier fiber KMZ/quotes | Closure: Contracted water supply/rights with verified peak active-hour delivery/storage & ASHRAE/OEM peak thermal validation, plus FID-gating diverse carrier fiber & optical latency commitment] (score 40)';
  }

  if (next.includes('queue position MPR-')) {
    next = next.replace(
      /\bqueue position (MPR-[A-Za-z0-9-]+)\b/g,
      'Member Project Request identifier $1 (preliminary utility study request ID, not a verified ranked queue position)',
    );
  }

  if (/^Interconnection Queue \(LGIA\/SGIA\)(\s*\(continued\))?$/.test(next)) {
    next = next.replace(
      /^Interconnection Queue \(LGIA\/SGIA\)(\s*\(continued\))?$/,
      'Load Service and Generation Interconnection Pathways$1',
    );
  }

  if (/^Drought Resilience \(1-in-10 \/ 1-in-100\)(\s*\(continued\))?$/.test(next)) {
    next = next.replace(
      /^Drought Resilience \(1-in-10 \/ 1-in-100\)(\s*\(continued\))?$/,
      'Drought Exposure & Water-Supply Resilience (Qualitative Basin Screen)$1',
    );
  }

  if (next.includes('multi-year 1-in-10 or 1-in-100 drought events qualitatively elevate')) {
    next = next.replace(
      /multi-year 1-in-10 or 1-in-100 drought events qualitatively elevate/g,
      'multi-year regional drought episodes (qualitative basin exposure screen; site-specific 1-in-10 / 1-in-100 return-period hydrology is not modeled in the SED) elevate',
    );
  }

  if (next.includes('exposes junior rights to senior-right curtailment during 1-in-10 or 1-in-100 droughts')) {
    next = next.replace(
      /exposes junior rights to senior-right curtailment during 1-in-10 or 1-in-100 droughts/g,
      'exposes junior rights to senior-right curtailment during severe regional droughts (qualitative prior-appropriation exposure only; site-specific 1-in-10 or 1-in-100 return-period drought hydrology is not modeled)',
    );
  }

  if (/with on-site water storage buffered for the \d[\d,]*\s*-\s*\d[\d,]* modeled active hours\/year of adiabatic assist/.test(next)) {
    next = next.replace(
      /with on-site water storage buffered for the (\d[\d,]*)\s*-\s*(\d[\d,]*) modeled active hours\/year of adiabatic assist(\s*\[[^\]]+\](?:,\s*\[[^\]]+\])*)?/g,
      (_m, lowHrs: string, highHrs: string, refs?: string) => {
        const refSuffix = refs ?? '';
        const peakNote =
          isLongspur || (lowHrs === '293' && highHrs === '575')
            ? ', Phase 2 peak active-hour makeup reaches 372,548–1,061,939 gal/hour [~6,209–17,699 gpm],'
            : '';
        return `with engineered peak-day delivery and on-site storage sized via hourly thermal-balance, refill-rate, and consecutive hot-day analysis rather than cumulative annual active hours (${lowHrs}–${highHrs} active hours/year is not a contiguous outage duration or tank-sizing basis${peakNote} and 0 gal/day dry cooling-process water does not mean zero total-site water${refSuffix})`;
      },
    );
  }

  if (next.includes('simple-cycle or reciprocating combustion turbine')) {
    const elevationQual = isLongspur ? 'at 1,856.2 m elevation' : 'at site elevation';
    next = next.replace(
      /simple-cycle or reciprocating combustion turbine net shaft output unless compensated in OEM sizing/g,
      `simple-cycle gas turbine or reciprocating internal-combustion engine net shaft output (requiring OEM site-rated performance curves ${elevationQual} rather than generic altitude derating rules of thumb)`,
    );
  }

  if (
    next.includes('Phase II subsurface geotechnical investigation with boreholes')
    || next.includes('Phase II borehole geotechnical investigation')
  ) {
    next = next
      .replace(
        /a Phase I Environmental Site Assessment and a Phase II subsurface geotechnical investigation with boreholes/g,
        'a Phase I Environmental Site Assessment (with a conditional Phase II environmental subsurface investigation only if Recognized Environmental Conditions are identified) and a separate geotechnical subsurface investigation with soil borings and laboratory testing',
      )
      .replace(
        /a Phase I ESA and Phase II subsurface geotechnical investigation with boreholes/g,
        'a Phase I ESA (with conditional Phase II environmental sampling if RECs are found) and a separate geotechnical subsurface investigation with boreholes',
      )
      .replace(
        /Phase I ESA,\s*and Phase II borehole geotechnical investigation/g,
        'Phase I ESA (with conditional Phase II environmental sampling only if RECs are identified), and a separate geotechnical subsurface investigation with soil borings',
      )
      .replace(
        /a Phase II borehole geotechnical investigation/g,
        'a separate geotechnical subsurface investigation with soil borings',
      );
  }

  if (next.includes('protects the 2-5 year target timeline while preserving long-term 345 kV grid parallel operation')) {
    next = next.replace(
      /protects the 2-5 year target timeline while preserving long-term 345 kV grid parallel operation/g,
      isLongspur
        ? 'mitigates exposure to the 48 to 72 month transmission buildout while preserving long-term 345 kV grid parallel operation (though it does not automatically meet the developer-noted Q4 2028 Phase 1 target because the indicative Tallgrass lateral is non-binding and targets Q1 2029 in-service)'
        : 'mitigates exposure to multi-year transmission buildouts while preserving long-term grid parallel operation (subject to contracted fuel lateral delivery, air permitting, and OEM queue schedules)',
    );
  }

  if (next.includes('execute a binding lateral study with Tallgrass or Black Hills to clear the lateral-required fuel gate')) {
    next = next.replace(
      /execute a binding lateral study with Tallgrass or Black Hills to clear the lateral-required fuel gate/g,
      'advance from non-binding lateral studies toward executed firm pipeline construction, interconnect, and gas transportation/supply agreements with Tallgrass or Black Hills (a lateral study alone does not clear the firm-fuel gate, which requires contracted hourly/daily deliverability, verified 450–650 psig vs. 660 psig delivery pressure, fuel quality/heating-value [Btu/scf & Wobbe index] specification, completion/commissioning schedule, and curtailment/outage terms; note Tallgrass 200,000 dth/day is a minimum-volume commitment rather than guaranteed maximum hourly deliverability)',
    );
  }

  if (next.includes('Compare the EIA Wyoming industrial retail electricity average of 8.65 ¢/kWh')) {
    next = next.replace(
      /Compare the EIA Wyoming industrial retail electricity average of 8\.65 ¢\/kWh \(\$86\.50\/MWh context-only state average, not a site tariff\)\s*(\[(?:verified\s+)?Ref 23\])\s*against the EIA Wyoming industrial natural gas fuel benchmark of \$4\.06\/MMBtu \(\$36\.54\/MWh fuel-only cost at 9,000 Btu\/kWh, excluding capital, O&M, and lateral charges\)\s*(\[(?:verified\s+)?Ref 24\])/g,
      'Build a matched-boundary, same-service all-in served-load cost model rather than directly comparing the EIA Wyoming industrial retail electricity average of 8.65 ¢/kWh ($86.50/MWh context-only state average, not a site tariff) $1 against the EIA Wyoming industrial natural gas fuel-only benchmark of $4.06/MMBtu ($36.54/MWh fuel component only at 9,000 Btu/kWh, excluding generation capex, fixed/variable O&M, lateral reservation [$8.03M/yr conditional on 200,000 dth/day × $0.110/dth/day over a 20-year term], N-1 reserve/firming, and network/standby charges — do not infer electricity rate savings from fuel-only vs. retail benchmarks) $2',
    );
  }

  if (next.includes('provided the developer pursues behind-the-meter (BTM) or hybrid power generation and dry or adiabatic-assist heat rejection to address transmission schedule delays and severe regional water stress')) {
    next = next.replace(
      'provided the developer pursues behind-the-meter (BTM) or hybrid power generation and dry or adiabatic-assist heat rejection to address transmission schedule delays and severe regional water stress',
      isLongspur
        ? 'subject to completing dual-track engineering and clearing prerequisite gates for behind-the-meter (BTM) or hybrid power generation (uncontracted gas lateral with indicative Tallgrass Q1 2029 in-service after the developer-stated Q4 2028 target, plus unresolved air and land-use permits) and dry or adiabatic-assist heat rejection (unverified water supply and OEM peak thermal duty) to address transmission schedule delays and severe regional water stress'
        : 'subject to completing dual-track engineering and clearing prerequisite gates for behind-the-meter (BTM) or hybrid power generation (uncontracted fuel delivery, unresolved air and land-use permits, and OEM queue timing) and dry or adiabatic-assist heat rejection (unverified water supply and OEM peak thermal duty) to address transmission schedule delays and severe regional water stress',
    );
  }

  if (next.includes('to decouple initial data center energization from 345 kV transmission construction')) {
    next = next.replace(
      /to decouple initial data center energization from 345 kV transmission construction/g,
      isLongspur
        ? 'to evaluate whether Phase 1 energization can proceed ahead of 345 kV transmission construction (contingent on contracted gas lateral delivery, air permitting, and OEM queue timing; indicative Tallgrass lateral in-service is Q1 2029 vs. developer-noted Q4 2028 request)'
        : 'to evaluate whether Phase 1 energization can proceed ahead of 345 kV transmission construction (contingent on contracted fuel delivery, air permitting, and OEM queue timing)',
    );
  }

  if (next === 'Select Dry Coolers with Adiabatic Assist and Verify Water Supply Source') {
    next = 'Evaluate Dry Coolers with Adiabatic Assist as Candidate Architecture and Verify Water Supply Source';
  }

  if (next.includes('Adopt closed-loop direct-to-chip liquid cooling at the racks paired with dry fluid coolers and seasonal adiabatic assist')) {
    next = next.replace(
      /Adopt closed-loop direct-to-chip liquid cooling at the racks paired with dry fluid coolers and seasonal adiabatic assist/g,
      'Evaluate closed-loop direct-to-chip liquid cooling at the racks paired with dry fluid coolers and seasonal adiabatic assist as a candidate architecture (deferring final selection until ASHRAE peak thermal duty, OEM derating, and peak active-hour water-supply capacity are verified)',
    );
  }

  if (next.includes('Select dry or adiabatic-assist heat rejection (')) {
    next = next.replace(
      /Select dry or adiabatic-assist heat rejection \(/g,
      'Evaluate dry or adiabatic-assist heat rejection as a candidate architecture (deferring final selection until peak thermal duty, OEM derating, and physical water-supply capacity are verified; ',
    );
  }

  if (next.includes('Specify closed-loop rack liquid cooling with dry fluid coolers and seasonal adiabatic assist')) {
    next = next.replace(
      /Specify closed-loop rack liquid cooling with dry fluid coolers and seasonal adiabatic assist/g,
      'Evaluate closed-loop rack liquid cooling with dry fluid coolers and seasonal adiabatic assist as a candidate architecture (deferring final selection until ASHRAE peak thermal duty, OEM derating, and peak active-hour water-supply capacity are verified)',
    );
  }

  if (next.includes('44.65 acres (7.8%) of the 572.78-acre parcel intersect high-risk FEMA Zone A (100-year SFHA, with partial/disagreeing FEMA coverage and upstream dams noted in cross-validation)')) {
    next = next.replace(
      /44\.65 acres \(7\.8%\) of the 572\.78-acre parcel intersect high-risk FEMA Zone A \(100-year SFHA, with partial\/disagreeing FEMA coverage and upstream dams noted in cross-validation\)\s*(\[(?:verified\s+)?Ref 14\]),\s*and 153\.2 acres of riverine wetlands are mapped in the full NWI source-feature inventory \(not parcel overlap acreage\)\s*(\[(?:verified\s+)?Ref 15\])\./g,
      '44.65 acres (7.8%) of the 572.78-acre parcel intersect high-risk FEMA Zone A (100-year SFHA, driving the CRITICAL / High 20/100 site flood-hazard score, with partial/disagreeing FEMA coverage and upstream dams noted in cross-validation; residual execution risk is rated Medium only if all structures avoid Zone A after hydraulic verification, while the remaining 528.13 acres [92.2%] carry an unverified/disputed Zone X label and are not established as flood-free buildable land) $1, and 153.2 acres of riverine wetlands are mapped in the full NWI source-feature inventory (regional feature total, not a parcel overlap deduction) $2.',
    );
  }

  if (next.includes('requires a $250,000 study fee and capacity security')) {
    next = next.replace(
      /requires a \$250,000 study fee and capacity security/g,
      'requires a $250,000 HIL study fee (distinct from separate capacity-security collateral; October 28, 2026 deposit deadline) and separate capacity security',
    );
  }

  if (/^≈60% \(CCGT\)(\s*\[Ref\s+\d+\])?$/.test(next)) {
    next = next.replace(
      /^≈60% \(CCGT\)(\s*\[Ref\s+\d+\])?$/,
      '≈60% fleet avg CF (CCGT dispatch avg; distinct from unit availability; site N-1/ELCC design required)$1',
    );
  }

  if (next === 'Months–~2 yr (fast)' || next === 'Months-~2 yr (fast)') {
    next = '~18–36+ mo typical (OEM queue + lateral + air permit dependent; not a site commitment)';
  }

  if (next === 'Firm (fast to deploy)') {
    next = 'Dispatchable (subject to firm fuel lateral, air permit & OEM queue)';
  }

  if (next === 'Interconnection queue (often >2,100 days)') {
    next = 'Gen. queue benchmark often >2,100 days (LBNL 2023 gen. context; load service per utility study)';
  }

  if (next.includes('Needs FIRM dispatchable power at high load factor: gas now (CF ≈60%, fast)')) {
    next = next.replace(
      /Needs FIRM dispatchable power at high load factor: gas now \(CF ≈60%, fast\)/g,
      'Needs dispatchable power at high load factor: gas (≈60% historical fleet CF, distinct from dedicated unit availability; site N-1/ELCC design, firm fuel lateral & air permit required)',
    );
  }

  if (/^Sustained AI training & colocation, now(\s*\[Ref\s+\d+\])?$/.test(next)) {
    next = next.replace(
      /^Sustained AI training & colocation, now(\s*\[Ref\s+\d+\])?$/,
      'Sustained AI training & colocation (subject to site N-1, fuel lateral & air permit)$1',
    );
  }

  if (/^Risk sub-score \d+ \(from \d+ project-specific risks\)\. Confirmed Red\/High risk in pool [—–-] capped$/.test(next)) {
    next = next.replace(
      /^Risk sub-score (\d+) \(from (\d+) project-specific risks\)\. Confirmed Red\/High risk in pool ([—–-]) capped$/,
      'Risk sub-score $1 (computed across $2 assessed project-specific risks; unscored evidence-gap rows are listed separately below). Confirmed Red/High risk in pool $3 capped',
    );
  }

  if (next === 'Severity-weighted across project-specific risks') {
    next = isLongspur
      ? 'Severity-weighted across 5 assessed project-specific risks (excluding unscored evidence-gap rows)'
      : 'Severity-weighted across assessed project-specific risks (excluding unscored evidence-gap rows)';
  }

  if (next.includes('Connectivity is advisory and does not gate the go/no-go call.')) {
    next = next.replace(
      /Connectivity is advisory and does not gate the go\/no-go call\./g,
      'Connectivity is excluded from the numerical screening score as an unverified evidence gap, but remains a required commercial/engineering confirmation gate before final investment decision.',
    );
  }

  if (next.includes('connectivity is advisory, not a go/no-go input')) {
    next = next.replace(
      /connectivity is advisory,\s*not a go\/no-go input/g,
      'connectivity is excluded from the numerical screening score as an unverified evidence gap, but remains a required confirmation gate before final investment decision',
    );
  }

  if (/^AQI \d+ \([^)]+\)\. Primary pollutant: .+\.$/.test(next)) {
    next = next.replace(
      /^AQI (\d+) \(([^)]+)\)\. Primary pollutant: (.+)\.$/,
      'Ambient context: AQI $1 ($2; $3) — ambient baseline only, not stationary-source BTM air-permit clearance.',
    );
  }

  if (/^Air Quality \(AQI\): AQI \d+ \([^)]+\)\. Primary pollutant: .+\. \(\d+\/100\)$/.test(next)) {
    next = next.replace(
      /^Air Quality \(AQI\): AQI (\d+) \(([^)]+)\)\. Primary pollutant: (.+)\. \((\d+\/100)\)$/,
      'Ambient Air Quality (AQI): AQI $1 ($2, $3; ambient baseline only, not BTM air-permit clearance) ($4)',
    );
  }

  if (next.includes('BESS/supercap firming required')) {
    next = next.replace(
      /Sustained high load factor, but synchronized GPU swings drive high-frequency volatility\s*→\s*BESS\/supercap firming required/g,
      'Sustained high load factor; synchronized GPU swings drive high-frequency volatility (evaluate BESS/supercapacitor smoothing in transient study)',
    );
  }

  if (next.includes('essential for training')) {
    next = next.replace(
      /All AI workloads\s*[—–-]\s*essential for training/g,
      'AI training & intermittent-source firming (subject to transient study)',
    );
  }

  if (next.startsWith('≈$89') && !next.includes('lacks table/page')) {
    next = next.replace(
      /^≈\$89[–-]102\/MWh(?:\s*\(FOAK ref\.;\s*~\$65\/MWh NOAK target\))?(\s*\[Ref\s+\d+\])?$/,
      'Omitted (secondary blog ≈$89–102/MWh FOAK / ~$65/MWh NOAK lacks table/page, dollar year & site boundary)$1',
    );
  }

  if (next.startsWith('≈$26') && !next.includes('lacks table/page')) {
    next = next.replace(
      /^≈\$26[–-]50\/MWh\s*\(pre-firming(?:;\s*excludes 24\/7 AI baseload overbuild\/firming)?\)(\s*\[Ref\s+\d+\])?$/,
      'Omitted (secondary blog ≈$26–50/MWh pre-firming lacks table/page, dollar year & 24/7 firming boundary)$1',
    );
  }

  if (next === 'SMR capacity factor 95%+, LCOE ≈$89–102/MWh, generally post-2030; renewables LCOE ≈$26–50/MWh; interconnection queues averaging >2,100 days.') {
    next =
      'SMR capacity factor 95%+, generally post-2030; interconnection queues averaging >2,100 days (secondary blog LCOE ranges ≈$89–102/MWh SMR and ≈$26–50/MWh pre-firming renewables are omitted from site economic basis pending primary-source table/page, dollar year, and 24/7 firming/interconnection boundary).';
  }

  if (next.includes('a generic provider heuristic estimates interconnection cost at $3-8M and context-only duration at 18-36 months, which does not establish site connection cost, load-service timing, or a utility offer')) {
    next = next.replace(
      /a generic provider heuristic estimates interconnection cost at \$3-8M and context-only duration at 18-36 months, which does not establish site connection cost, load-service timing, or a utility offer/g,
      isLongspur
        ? 'a generic provider heuristic lists an unverified $3–8M / 18–36 month placeholder (omitted from site cost/schedule basis: lacks source/table/page, dollar year, and 345 kV network boundary; superseded by the site-specific $133.7M / 48–72 month Class 5 study estimate)'
        : 'a generic provider heuristic lists an unverified $3–8M / 18–36 month placeholder (omitted from site cost/schedule basis: lacks source/table/page, dollar year, and site-specific utility network boundary; does not establish site connection cost, load-service timing, or a utility offer)',
    );
  }

  // Redhead & multi-site presentation reconciliations (#240 #issuecomment-6494607656, #issuecomment-6539365508, #issuecomment-6540682710 & #issuecomment-6541820632)
  const hasIdentityConflict = Boolean(
    context?.identityConflict
    || hasCanonicalTupleConflict(context?.siteId, context?.reportId, context?.revisionId),
  );
  const isRedheadSiteContext =
    !hasIdentityConflict
    && (
      context?.siteId === 'site-1780556191149-rab796'
      || context?.siteId === '1780556191149-rab796'
      || context?.siteId === 'site-1774871397240-x5n10s'
      || context?.reportId === 'feasibility-1792061492807-ypx22n'
      || context?.reportId === 'feasibility-1792048588890-gi9x5c'
    );
  const isLongspurSiteContext =
    !hasIdentityConflict
    && (isLongspur || context?.reportId === 'feasibility-1792773672702-2l6jqi');
  const isSouthwestSiteContext =
    !hasIdentityConflict
    && (
      context?.siteId === 'site-1779699961476-s2nxki'
      || context?.reportId === 'feasibility-1793052047639-xhvekb'
    );
  const isForeignSiteForRedheadNichols =
    !hasIdentityConflict
    && typeof context?.siteId === 'string'
    && context.siteId.trim().length > 0
    && !isRedheadSiteContext;

  if (
    isForeignSiteForRedheadNichols
    && /\b(?:Nichols\s+(?:Purchase\s+Option|POA|Contract)|Little\s+Indian\s+Bayou|Book\s+4504,\s*Page\s+792|File\s*#?3465900|doc-1780556635705-vv7y7f)\b/i.test(next)
    && !next.includes('[CROSS-SITE / WRONG-PARTY CLAUSE CONTAMINATION:')
  ) {
    next = `${next} [CROSS-SITE / WRONG-PARTY CLAUSE CONTAMINATION: Nichols POA (doc-1780556635705-vv7y7f [Ref 6] / Book 4504, Page 792, File #3465900) binds Seller (Kenneth Howard Nichols, Caroline Boese Nichols, and Little Indian Bayou, LLC) and Purchaser (Redhead Industrial Park, LLC) on up to 1,167.99 acres in Calcasieu Parish, LA (site-1780556191149-rab796) and is excluded from this site's operative property/title obligations]`;
  } else if (
    isRedheadSiteContext
    && /Nichols|Option fails to be exercised|Seller'?s mineral|Book\s+4504,\s*Page\s+792|File\s*#?3465900/i.test(next)
    && /mineral|surface[- ]use/i.test(next)
    && !next.includes('(source-transcription note:')
    && !next.includes('conditioning Seller\'s mineral surface use on non-exercise')
    && !next.includes('Seller-retained mineral reservation/surface-use clause unless extinguished via Purchaser mineral option')
    && !next.includes('Nichols Purchase Option Agreement (doc-1780556635705-vv7y7f')
  ) {
    next = next
      .replace(
        /\b(mineral\s+surface[- ]use\s+(?:waiver|restriction)s?|surface[- ]use\s+waiver\s+gate)\b/i,
        '$1 (source-transcription note: under Nichols Purchase Option Agreement doc-1780556635705-vv7y7f [Ref 6] §§2(C) and 6(i)–(j) between Seller [Kenneth Howard Nichols, Caroline Boese Nichols, and Little Indian Bayou, LLC] and Purchaser [Redhead Industrial Park, LLC] for up to 1,167.99 acres in T8S R9W, Calcasieu Parish, LA, the 500-ft surface/subsurface prohibition binds Seller if the $750/acre Mineral Purchase Option is not exercised, while Book 4504, Page 792, File #3465900 is an Exhibit A vesting Judgment of Possession and Preliminary Title Report doc-1780556655925-b1qlh2 notes prior third-party mineral severances — a transcription ambiguity, not an unconditional project surface-use waiver or legal title conclusion; legal counsel must verify mineral title and execute a binding surface-use waiver for the exercised purchase)',
      )
      .replace(
        /\bSeller has the right to use the surface for mineral extraction unless Purchaser exercises the mineral purchase option\b/i,
        'Seller-retained mineral reservation/surface-use clause unless extinguished via Purchaser mineral option (source-transcription note: Nichols Contract §29(c) / §§2(C) & 6(i)–(j) conditions Seller surface-use terms on non-exercise of the Option — verify mineral title and execute a binding surface-use waiver)',
      );
    if (
      /\b(?:Book\s+4504,\s*Page\s+792|File\s*#?3465900)\b/i.test(next)
      && !next.includes('(source-transcription note:')
      && !next.includes('Nichols Purchase Option Agreement (doc-1780556635705-vv7y7f')
    ) {
      next = `${next} (source-transcription note: Nichols Purchase Option Agreement doc-1780556635705-vv7y7f [Ref 6] §§2(C) and 6(i)–(j) between Seller [Kenneth Howard Nichols, Caroline Boese Nichols, and Little Indian Bayou, LLC] and Purchaser [Redhead Industrial Park, LLC] for up to 1,167.99 acres in T8S R9W, Calcasieu Parish, LA prohibits Seller from surface use or subsurface disturbance within 500 ft if the $750/acre Mineral Purchase Option is not exercised; Book 4504, Page 792, File #3465900 is an Exhibit A vesting Judgment of Possession, and Preliminary Title Report doc-1780556655925-b1qlh2 notes prior third-party mineral severances requiring title/survey verification)`;
    }
  }
  // Repair any already-persisted duplicate `(source-transcription note: ...)` repetitions (`DC-REG-004`).
  next = next.replace(
    /(\(source-transcription note:[^)]+\))(?:\s*\(source-transcription note:[^)]+\))+/gi,
    '$1',
  );

  const effectiveSisDeliveryKv = !hasIdentityConflict
    ? context?.sisRequestedDeliveryKv
    : undefined;
  const sisRefOrd = context?.sisRequestedDeliveryRefOrdinal ?? 8;
  const pinRefOrd = context?.sisNearbyPinRefOrdinal ?? 4;

  if (
    effectiveSisDeliveryKv === 34.5
    && /\b500\s*kV\b/i.test(next)
    && !/\b(?:rejected|not\s+the\s+adopted|abandoned|dropped|superseded|historical)\b[^.!?\n]*\b34\.5\s*kV\b|\b34\.5\s*kV\b[^.!?\n]*\b(?:rejected|not\s+the\s+adopted|abandoned|dropped|superseded|historical)\b/i.test(next)
    && !next.includes('requested 34.5 kV customer delivery')
    && !next.includes('distinct from requested 34.5 kV SIS interconnection delivery')
    && !next.includes('nearby infrastructure only; not a committed connection')
  ) {
    next = next
      .replace(
        /Canonical facts establish a 500\s*kV interconnection voltage from the on-site KMZ substation/gi,
        `Canonical facts record an unverified nearby Entergy 500 kV KMZ pin at 2.99 km [Ref ${pinRefOrd}] (nearby infrastructure only; not a committed connection, capacity allocation, or the requested 34.5 kV customer delivery voltage per BECI System Impact Study doc-1782033270857-mg0e65 [Ref ${sisRefOrd}] p3–8)`,
      )
      .replace(
        /\(which sets the canonical 500\s*kV interconnection voltage\)/gi,
        `(nearby third-party infrastructure only; not a committed connection, capacity allocation, or the requested 34.5 kV customer delivery voltage per BECI SIS doc-1782033270857-mg0e65 [Ref ${sisRefOrd}])`,
      )
      .replace(
        /\b500\s*kV\s+interconnection\s+asset\b/gi,
        `nearby 500 kV transmission pin [Ref ${pinRefOrd}] (nearby infrastructure only; not a committed connection, capacity allocation, or the requested 34.5 kV customer delivery voltage per BECI SIS [Ref ${sisRefOrd}])`,
      )
      .replace(
        /\b500\s*kV\s+(substation|transmission|point of interconnection|POI|infrastructure|station|pin)/i,
        '500 kV $1 (regional HIFLD proximity / upstream 500/230 kV SIS reinforcement context only; the frozen System Impact Study specifies requested 34.5 kV customer delivery via initial 69/34.5 kV and later 230/34.5 kV transformation, not a verified 500 kV customer service connection)',
      );
  }

  const hasBoundPhase1_100_Phase2_350 =
    !hasIdentityConflict
    && context?.phases !== undefined
    && context.phases.length >= 2
    && Math.abs(context.phases[0]!.facilityLoadMw - 100) < 1e-6
    && Math.abs(context.phases[1]!.facilityLoadMw - 350) < 1e-6;

  if (
    hasBoundPhase1_100_Phase2_350
    && /\bincremental\s+350\s*MW\b|\b350\s*MW\s+incremental\b|\bPhase\s+2\s+adds\s+350\s*MW\b/i.test(next)
  ) {
    next = next
      .replace(
        /\ban\s+incremental\s+350\s*MW(?:\s+expansion)?\b/gi,
        'a cumulative 350 MW Phase 2 target (total Phase 2 capacity, not an incremental 350 MW addition on top of nonzero Phase 1 load)',
      )
      .replace(
        /\b350\s*MW\s+incremental\b/gi,
        '350 MW cumulative Phase 2 (total Phase 2 capacity, not an incremental 350 MW addition)',
      )
      .replace(
        /\bPhase\s+2\s+adds\s+350\s*MW\b/gi,
        'Phase 2 reaches 350 MW cumulative (adds 250 MW to Phase 1 100 MW)',
      );
  } else if (
    isRedheadSiteContext
    && !isForeignSiteForRedheadNichols
    && /\b350\s*MW\b/i.test(next)
  ) {
    next = next
      .replace(
        /350\s*MW\s+CUMULATIVE\s+IT\s+LOAD\s*\(\s*350\s*MW\s+INCREMENTAL\s+IT\s+ADDITION\s+OVER\s+THE\s+UNDECLARED\s+EXACT\s+PHASE\s+1\s+POINT\s*\)/gi,
        '350 MW CUMULATIVE IT LOAD (+300–340 MW INCREMENTAL IT ADDITION OVER PHASE 1 10–50 MW RANGE; +300 MW OVER 50 MW PHASE 1 GRID TARGET — NOT +350 MW ADDITIONAL)',
      )
      .replace(
        /\bPhase\s+2\s+adds\s+350\s*MW\s+of\s+IT\s+load\s*\(\s*413\s*MW\s+total\s+facility\s+load\s+at\s+a\s+1\.18\s+PUE\s*\)/gi,
        'Phase 2 reaches 350 MW cumulative IT load (+300–340 MW incremental IT addition over Phase 1 10–50 MW range, or +300 MW over the 50 MW Phase 1 grid target; 413 MW cumulative facility load at a 1.18 PUE, not an additional 350 MW)',
      )
      .replace(
        /\b350\s*MW\s+incremental\s+IT\s+addition\s+over\s+the\s+undeclared\s+exact\s+Phase\s+1\s+point\b/gi,
        '+300–340 MW incremental IT addition over Phase 1 (10–50 MW range; +300 MW over 50 MW Phase 1 grid target) to reach 350 MW cumulative IT load',
      );
  }

  if (
    !hasIdentityConflict
    && ws?.has550MwRows === true
    && !isForeignSiteForRedheadNichols
    && /\b550\s*MW\b/i.test(next)
    && !/\bno\s+water[- ]screening\s+rows\s+are\s+available\b/i.test(next)
    && /\b(?:outside\s+(?:the\s+)?(?:current\s+)?water[- ]screening\s+scope|requires?\s+a\s+water[- ]screen\s+rerun|water\s+demand\s+is\s+omitted\s+from\s+the\s+table|does\s+not\s+cover\s+the\s+350\s*MW\s+Phase\s+2\s*\(\s*413\s*MW\s+facility\s*\)\s+or\s+550\s*MW\s+Phase\s+3)\b/i.test(next)
    && !next.includes('deterministic screening water-demand rows for 550 MW')
    && !next.includes('unmodeled in the Phases 1–2 table')
    && !next.includes('per-phase N1 point-load estimates for Phase 2 (350 MW IT / 413 MW facility: 2.43 MGD wet')
  ) {
    next = next
      .replace(
        /Because this screen is bounded at 500\s*MW\s*IT\s*\(\s*590\s*MW\s+facility\s*\),\s*it does not cover the 350\s*MW\s+Phase\s+2\s*\(\s*413\s*MW\s+facility\s*\)\s+or\s+550\s*MW\s+Phase\s+3\s*\(\s*649\s*MW\s+facility\s*\)\s+point loads,\s*which require a water-screen rerun/gi,
        'Because the primary N0 bucket screen (sed.economicEvidence.data.waterUseScreening) is bounded to the declared 200–500 MW IT range (236–590 MW facility at PUE 1.18: 1.39–3.48 MGD wet, 0.153–0.382 MGD hybrid, 0.065–0.163 MGD dry), per-phase N1 point-load estimates for Phase 2 (350 MW IT / 413 MW facility: 2.43 MGD wet, 0.268 MGD hybrid, 0.114 MGD dry) and Phase 3 (550 MW IT / 649 MW facility: 3.82 MGD wet, 0.421 MGD hybrid, 0.179 MGD dry) are reported separately in the N1 phase scenario screens (sed.economicEvidence.data.waterUseScenarioScreens; Phase 1 10–50 MW IT exact point remains undeclared, and physical supply capacity for 550 MW requires dedicated utility/hydrogeological study)',
      )
      .replace(
        /\b(outside\s+(?:the\s+)?(?:current\s+)?water[- ]screening\s+scope|requires\s+a\s+water[- ]screen\s+rerun)\b/i,
        '$1 (deterministic screening water-demand rows for 550 MW are tabulated in the water screening view, while physical supply capacity and thermal engineering for 550 MW remain unverified and require dedicated study)',
      )
      .replace(
        /\bPhase\s+3\s*\(\s*550\s*MW\s*\)\s+water\s+demand\s+is\s+omitted\s+from\s+the\s+table\b/i,
        'Phase 3 (550 MW) water demand is unmodeled in the Phases 1–2 table (deterministic 550 MW rows appear in the water screening view, while physical 550 MW supply and thermal engineering require dedicated study)',
      );
  }

  // DC-REG-010 — Longspur methodology citation (`[Ref 87]` / ASHRAE Psychrometrics) vs executed model run receipt.
  // Strictly gated by verified site/report/source provenance (`phaseWaterMethodologyProvenance`) and `hasExecutedReceipt === false`.
  // Never fall back to hardcoded Ref 87 from `siteId` alone (#issuecomment-6541820632).
  const effectivePhaseWaterMeth: PhaseWaterMethodologyProvenance | undefined =
    !hasIdentityConflict ? context?.phaseWaterMethodologyProvenance : undefined;
  if (
    !context?.isSummarySurface
    && effectivePhaseWaterMeth !== undefined
    && effectivePhaseWaterMeth.hasExecutedReceipt === false
    && typeof effectivePhaseWaterMeth.methodologyRef === 'number'
    && new RegExp(`\\[\\s*(?:(?:verified|inferred|unverified)\\s+)?Ref\\s+${effectivePhaseWaterMeth.methodologyRef}\\s*\\]`, 'i').test(next)
    && /\b(?:ASHRAE|psychrometric|adiabatic|water|cooling|wet[- ]bulb|dry[- ]bulb)\b/i.test(next)
    && !next.includes('methodology reference only; no standalone phaseWaterScreening or OEM curve execution receipt')
    && !next.includes('conditional on site dry-bulb/wet-bulb hours')
  ) {
    next = `${next} (methodology reference only; no standalone phaseWaterScreening or OEM curve execution receipt exists in the frozen evidence — conditional on site dry-bulb/wet-bulb hours, trim threshold, and OEM curves; not an executed model run, verified site savings, or guaranteed performance)`;
  }

  // DC-ART-016 / DC-REPORT-010 — Supplemental Southwest Crossing v5 A4 reconciliations (#issuecomment-6539390149, #issuecomment-6540682710 & #issuecomment-6541820632).
  // Strictly gated by affirmative bound source provenance (`southwestProvenance`) without identity conflict; never fall back to hardcoded defaults from `siteId` alone.
  const effectiveSwProv: SouthwestSupplementalProvenance | undefined =
    !hasIdentityConflict ? context?.southwestProvenance : undefined;

  if (effectiveSwProv) {
    // (1) BESS 80 MWh / 40 MW labelled 4-hour (nominal energy / power = 2.0 hours)
    if (
      typeof effectiveSwProv.bessBomRefOrdinal === 'number'
      && /\b80\s*MWh\s*\/\s*40\s*MW\b/i.test(next)
      && /\b4[- ]hour\b/i.test(next)
      && !next.includes('nominal single-unit energy/power ratio is 80 MWh ÷ 40 MW = 2.0 hours')
      && !next.includes('nominal 80 MWh ÷ 40 MW = 2.0 hours')
    ) {
      const bessRef = effectiveSwProv.bessBomRefOrdinal;
      const bessPhases =
        typeof effectiveSwProv.bessPhaseCount === 'number' && effectiveSwProv.bessPhaseCount >= 2
          ? effectiveSwProv.bessPhaseCount
          : undefined;
      if (/\b80\s*MWh\s*\/\s*40\s*MW\s*\(\s*4[- ]hour\s+LFP\s*\)\s*BESS\b/i.test(next)) {
        const phaseClause =
          bessPhases !== undefined
            ? ` per phase across ${bessPhases} bound phases; nominal single-unit energy/power ratio is 80 MWh ÷ 40 MW = 2.0 hours [or ${80 * bessPhases} MWh across ${bessPhases} phases = ${((80 * bessPhases) / 40).toFixed(1)} hours at 40 MW / 20 MW continuous for 4.0 hours at 80 MWh]`
            : `; nominal single-unit energy/power ratio is 80 MWh ÷ 40 MW = 2.0 hours [or 20 MW continuous for 4.0 hours at 80 MWh; multi-phase aggregate energy/duration and phase count unverified in source]`;
        next = next.replace(
          /\b80\s*MWh\s*\/\s*40\s*MW\s*\(\s*4[- ]hour\s+LFP\s*\)\s*BESS\b/gi,
          `80 MWh / 40 MW BESS (source BOM [Ref ${bessRef}] labels "80 MWh / 40 MW, 4-hour duration"${phaseClause}; usable duration basis requires OEM/schedule reconciliation)`,
        );
      } else if (/\b80\s*MWh\s*\/\s*40\s*MW,\s*4[- ]hour\s+duration\b/i.test(next)) {
        const phaseNote =
          bessPhases !== undefined
            ? ` per phase; ${80 * bessPhases} MWh across ${bessPhases} bound phases = ${((80 * bessPhases) / 40).toFixed(1)} hours at 40 MW`
            : ` at 40 MW [or 20 MW continuous for 4.0 hours at 80 MWh; multi-phase aggregate energy/duration and phase count unverified in source]`;
        next = next.replace(
          /\b80\s*MWh\s*\/\s*40\s*MW,\s*4[- ]hour\s+duration\b/gi,
          `80 MWh / 40 MW, 4-hour duration [reconciliation note: nominal 80 MWh ÷ 40 MW = 2.0 hours${phaseNote}]`,
        );
      }
    }
    // (2) PV 290,000 modules × 550 W = 159.5 MW DC vs 150 MW DC stated in BOM note
    if (
      typeof effectiveSwProv.pvBomRefOrdinal === 'number'
      && !next.includes('159.5 MW DC nominal module total')
    ) {
      const pvRef = effectiveSwProv.pvBomRefOrdinal;
      const pvInvSummary =
        effectiveSwProv.pvInverterSummary
        ?? '50 × 3.0 MVA = 150 MVA central inverter apparent-power rating [active-power MW AC and verified DC/AC ratio unverified without stated inverter power factor]';
      const pvInvShort =
        effectiveSwProv.pvInverterShortSummary
        ?? '50 × 3.0 MVA = 150 MVA central inverter apparent-power rating [active-power MW AC and verified DC/AC ratio unverified without stated inverter power factor]';
      if (/\b290,000\s+bifacial\s+550\s*W\s+solar\s+PV\s+modules\s*\(\s*150\s*MW\s*DC\s+total\b/i.test(next)) {
        next = next.replace(
          /\b290,000\s+bifacial\s+550\s*W\s+solar\s+PV\s+modules\s*\(\s*150\s*MW\s*DC\s+total\b/gi,
          `290,000 bifacial 550 W solar PV modules (159.5 MW DC nominal module total = 290,000 × 550 W; source BOM [Ref ${pvRef}] notes "150 MW DC site total" alongside ${pvInvSummary}`,
        );
      } else if (/\b550\s*W\s+bifacial,\s*1\.38\s*DC\/AC\s+ratio\s*\|\s*150\s*MW\s*DC\s+site\s+total\b/i.test(next)) {
        next = next.replace(
          /\b550\s*W\s+bifacial,\s*1\.38\s*DC\/AC\s+ratio\s*\|\s*150\s*MW\s*DC\s+site\s+total\b/gi,
          `550 W bifacial, 1.38 DC/AC ratio | 150 MW DC site total [reconciliation note: 290,000 modules × 550 W = 159.5 MW DC nominal module total vs 150 MW DC BOM note and ${pvInvShort}]`,
        );
      }
    }
    // (3) Southwest 507-acre discrepancy citing foreign Holly Ridge investor deck (`Ref 25`)
    if (
      effectiveSwProv.foreignAcreage !== undefined
      && /\b507\s*(?:acres|-acre|,?\s+and\s+640\s+acres|\s+to\s+930\s+acres)|\b276\.3,\s*273,\s*121,\s*115,\s*152,\s*507\b/i.test(next)
      && !next.includes(`foreign-site ${effectiveSwProv.foreignAcreage.foreignDeckLabel} [Ref ${effectiveSwProv.foreignAcreage.foreignRefOrdinal}]`)
      && !next.includes(`excluding foreign-site ${effectiveSwProv.foreignAcreage.foreignAcres}-acre figure`)
      && !/\bRichland\s+Parish\b/i.test(next)
    ) {
      const fa = effectiveSwProv.foreignAcreage;
      const operativeBoundaryDesc =
        typeof fa.boundaryAcres === 'number' && typeof fa.offeringAcres === 'number'
          ? `${fa.boundaryAcres} acres user-provided geometry [not survey/title/registry proof] (${fa.offeringAcres} acres in Offering Memorandum across Phase I ${fa.phase1Acres ?? 121} acres + Phase II ${fa.phase2Acres ?? 152} acres${typeof fa.futurePhase3Acres === 'number' ? `, plus ${fa.futurePhase3Acres} acres Future Phase III` : ''})`
          : typeof fa.boundaryAcres === 'number'
            ? `${fa.boundaryAcres} acres user-provided geometry [not survey/title/registry proof]`
            : `${fa.operativeSiteLabel} operative parcel boundary`;
      const operativeRangeClause =
        typeof fa.boundaryAcres === 'number' && typeof fa.offeringAcres === 'number'
          ? `is ${fa.boundaryAcres} acres per user-provided geometry [not survey/title/registry proof] (${fa.offeringAcres} acres in the Offering Memorandum across Phase I ${fa.phase1Acres ?? 121} acres + Phase II ${fa.phase2Acres ?? 152} acres${typeof fa.futurePhase3Acres === 'number' ? `, with ${fa.futurePhase3Acres} acres Future Phase III` : ''}) for ${fa.operativeSiteLabel}`
          : typeof fa.boundaryAcres === 'number'
            ? `is ${fa.boundaryAcres} acres per user-provided geometry [not survey/title/registry proof] for ${fa.operativeSiteLabel}`
            : typeof fa.operativeMinAcres === 'number' && typeof fa.operativeMaxAcres === 'number'
              ? `ranges from ${fa.operativeMinAcres} to ${fa.operativeMaxAcres} acres for ${fa.operativeSiteLabel}`
              : `is governed by the verified ${fa.operativeSiteLabel} boundary`;
      next = next
        .replace(
          /\b507\s+acres\s+in\s+an\s+uploaded\s+investor\s+deck\b/gi,
          `${fa.foreignAcres} acres in foreign-site ${fa.foreignDeckLabel} [Ref ${fa.foreignRefOrdinal}] (Holly Ridge - Richland Parish Data Center, LA — cross-site upload excluded from ${fa.operativeSiteLabel} operative parcel acreage: ${operativeBoundaryDesc})`,
        )
        .replace(
          /(,\s*)507(?:\s+acres)?,\s*and\s*640\s+acres\b/gi,
          `$1and 640 acres (excluding foreign-site ${fa.foreignAcres}-acre figure from ${fa.foreignDeckLabel} [Ref ${fa.foreignRefOrdinal}], Richland Parish, LA; ${fa.operativeSiteLabel} operative boundary is ${operativeBoundaryDesc})`,
        )
        .replace(
          /\(\s*507(?:\s+acres)?,\s*and\s*640\s+acres\s*\)/gi,
          `(640 acres Future Phase III; excluding foreign-site ${fa.foreignAcres}-acre figure from ${fa.foreignDeckLabel} [Ref ${fa.foreignRefOrdinal}], Richland Parish, LA; ${fa.operativeSiteLabel} operative boundary is ${operativeBoundaryDesc})`,
        )
        .replace(
          /(276\.3,\s*273,\s*121,\s*115,\s*152,\s*)507\b/gi,
          `$1excluding foreign-site ${fa.foreignAcres}-acre ${fa.foreignDeckLabel} [Ref ${fa.foreignRefOrdinal}]`,
        )
        .replace(
          /\branges\s+from\s+507\s+to\s+930\s+acres\b/gi,
          `${operativeRangeClause} (excluding foreign-site ${fa.foreignAcres}-acre figure from ${fa.foreignDeckLabel} [Ref ${fa.foreignRefOrdinal}], Richland Parish, LA)`,
        );
    }
    // (4) G3516 `Ref 118` vs drawing citation `[38]`
    if (typeof effectiveSwProv.g3516CanonicalRefOrdinal === 'number') {
      const legacyRef = effectiveSwProv.g3516LegacyRefOrdinal ?? 118;
      const canonRef = effectiveSwProv.g3516CanonicalRefOrdinal;
      const g3516Re = new RegExp(`\\b150\\s*x\\s*Caterpillar\\s+G3516\\s+layout\\s+in\\s+Ref\\s+${legacyRef}\\b`, 'gi');
      if (g3516Re.test(next)) {
        next = next.replace(g3516Re, `150 x Caterpillar G3516 layout in [Ref ${canonRef}]`);
      }
    }
  }

  if (
    isRedheadSiteContext
    && !isForeignSiteForRedheadNichols
    && /\b10[–-]50\s*MW\b/i.test(next)
    && !next.includes('67.85 MW')
    && !next.includes('10 MW lower and 50 MW upper bounds are not separately sized')
  ) {
    if (/\b48\.5\s*MW\b/i.test(next)) {
      next = next.replace(
        /\b(10[–-]50\s*MW)\b/,
        '$1 (Phase 1 adequacy against 48.5 MW modeled firm is tested at the 30 MW IT / 35.4 MW facility bucket midpoint [40.7 MW required at 1.18 PUE and 15% reserve]; the 50 MW IT upper endpoint requires 59.0 MW facility / 67.85 MW with 15% reserve and exceeds 48.5 MW modeled firm)',
      );
    } else if (/evaluated at 30\s*MW\s*IT\s*\(35\.4\s*MW\s*facility\)/i.test(next)) {
      next = next.replace(
        /evaluated at 30\s*MW\s*IT\s*\(35\.4\s*MW\s*facility\)/i,
        'evaluated at the 30 MW IT / 35.4 MW facility midpoint only; 10 MW lower and 50 MW upper bounds are not separately sized',
      );
    }
  }

  if (
    /\bpermitted\s+(?:water\s+)?supply\s+(?:is\s+ready|readiness\s+is\s+confirmed)\b/i.test(next)
    && /\b(?:no\s+(?:site-specific\s+)?water\s+permit|unverified\s+water)\b/i.test(next)
  ) {
    next = next.replace(
      /\bpermitted\s+(?:water\s+)?supply\s+(?:is\s+ready|readiness\s+is\s+confirmed)\b/gi,
      'permitted water-supply readiness is unverified (no site-specific water permit or allocation is confirmed on file)',
    );
  }

  if (
    /\bde-risked\s+water(?:\s+supply|\s+profile)?\b/i.test(next)
    && /\b(?:unverified|water\s+stress|no\s+water\s+permit)\b/i.test(next)
  ) {
    next = next.replace(
      /\bde-risked\s+water(?:\s+supply|\s+profile)?\b/gi,
      'climate-favorable thermal baseline (physical water supply and water rights remain unverified)',
    );
  }

  if (
    /\bmandates\s+strict\s+compliance\s+with\s+the\s+local\s+data\s+center\s+ordinance\b/i.test(next)
    && /\b(?:draft|tier[- ]?3|unverified)\b/i.test(next)
  ) {
    next = next.replace(
      /\bmandates\s+strict\s+compliance\s+with\s+the\s+local\s+data\s+center\s+ordinance\b/gi,
      'references the uploaded local data-center ordinance text (unverified draft/tier-3 document — confirm official adoption and parcel applicability)',
    );
  }

  if (/\$61[–-]72\/MWh[^.;\n]*?\babove\s+(?:the\s+)?\$64\/MWh\b/i.test(next)) {
    next = next.replace(
      /(\$61[–-]72\/MWh[^.;\n]*?)\babove\s+(the\s+)?(\$64\/MWh)\b/gi,
      '$1straddling/overlapping $2$3',
    );
  }

  if (
    /\bexecuted\s+fuel\s+supply\s+agreements?\s+and\s+guaranteed\s+pipeline\s+deliverability\b/i.test(next)
    && /\b(?:non-binding|RFQ|request\s+for\s+quotation|indicative\s+term\s+sheet)\b/i.test(next)
    && !/\b(?:supersedes?|superseded|replaced?|historical|former|prior|rejected|instead\s+of|rather\s+than)\b/i.test(next)
  ) {
    next = next.replace(
      /[^.!?\n]*\bexecuted\s+fuel\s+supply\s+agreements?\s+and\s+guaranteed\s+pipeline\s+deliverability\b[^.!?\n]*/gi,
      (sentence) => {
        if (
          !/\b(?:non-binding|RFQ|request\s+for\s+quotation|indicative\s+term\s+sheet)\b/i.test(sentence)
          || /\b(?:supersedes?|superseded|replaced?|historical|former|prior|rejected|instead\s+of|rather\s+than)\b/i.test(sentence)
        ) {
          return sentence;
        }
        return sentence.replace(
          /\bexecuted\s+fuel\s+supply\s+agreements?\s+and\s+guaranteed\s+pipeline\s+deliverability\b/gi,
          'a conditional, non-binding RFQ response (no executed fuel supply agreement or guaranteed pipeline deliverability is on file)',
        );
      },
    );
  }

  if (/\b(?:dry-only|zero-water\s+dry)\s+cooling\s+with\s+adiabatic\s+assist\b/i.test(next)) {
    next = next.replace(
      /\b(?:dry-only|zero-water\s+dry)\s+cooling\s+with\s+adiabatic\s+assist\b/gi,
      'hybrid dry/adiabatic-assist cooling (near-zero cooling water during dry-bulb operation, but requiring peak-period adiabatic makeup water)',
    );
  }

  if (
    /\bno\s+battery\s+storage\s+is\s+recorded\s+in\s+the\s+project\s+documentation\b/i.test(next)
    && /\bBESS\b/i.test(next)
  ) {
    next = next.replace(
      /\bno\s+battery\s+storage\s+is\s+recorded\s+in\s+the\s+project\s+documentation\b/gi,
      'battery storage is not confirmed as an adopted baseline specification (though an indicative BESS proposal is noted in project files)',
    );
  }

  if (
    /\bimmediately\s+initiate\s+equipment\s+procurement\s+and\s+construction\s+contracting\b/i.test(next)
    && /\b(?:proceed\s+with\s+caution|conditional|pending\s+interconnection|prerequisite\s+gate)\b/i.test(next)
  ) {
    next = next.replace(
      /\bimmediately\s+initiate\s+equipment\s+procurement\s+and\s+construction\s+contracting\b/gi,
      'defer equipment procurement and construction contracting until binding utility interconnection, water-supply, and environmental prerequisite gates are cleared',
    );
  }

  return next;
}

/**
 * Scrub one string.
 *   - A body that is ONLY sentinel text (plus whitespace / markdown chrome)
 *     becomes the withheld-narrative notice: the designed whole-leaf
 *     treatment.
 *   - A sentinel EMBEDDED in other content is replaced IN PLACE by a short
 *     bracketed notice naming what is missing. Surrounding content (other
 *     sentences, other table rows and cells) is never discarded.
 */
export function scrubSentinelBody(body: string, context?: ScrubScenarioContext): string {
  if (!containsInternalSentinel(body)) return reconcileCustomerTextLeaf(body, context);

  const replaced = replaceSentinels(body)
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Whole-leaf: nothing but the notice(s) and markdown/whitespace chrome.
  const remainder = replaced.replace(INLINE_NOTICE_RE, '').replace(/[#>\-*|:\s]/g, '');
  if (remainder.length === 0) return WITHHELD_NARRATIVE_NOTICE;
  return reconcileCustomerTextLeaf(replaced, context);
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

function scrubValue(
  value: unknown,
  context?: ScrubScenarioContext | null,
  isRoot = true,
  suppressContextDerivation = false,
): unknown {
  if (typeof value === 'string') return scrubSentinelBody(value, context ?? undefined);
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const scrubbed = scrubValue(item, context, false, suppressContextDerivation);
      if (scrubbed !== item) changed = true;
      return scrubbed;
    });
    return changed ? next : value;
  }
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    let changed = false;
    const source = value as Record<string, unknown>;
    let nextContext: ScrubScenarioContext | undefined;
    let hadValidBoundContext = false;
    const isSummaryDoc = isScreeningSummaryDocumentObject(source);
    const docSiteId =
      typeof source.siteId === 'string' && source.siteId.trim().length > 0
        ? source.siteId.trim()
        : undefined;
    if (isRoot && context === null) {
      suppressContextDerivation = true;
      nextContext = isSummaryDoc ? { isSummarySurface: true } : undefined;
    } else if (suppressContextDerivation) {
      nextContext = context ?? undefined;
    } else if (isRoot && context !== undefined && context !== null) {
      const rootIdentityConflict = hasTargetIdentityConflict(source, context);
      if (rootIdentityConflict) {
        suppressContextDerivation = true;
        nextContext = {
          identityConflict: true,
          ...(isSummaryDoc && context.isSummarySurface === undefined
            ? { isSummarySurface: true }
            : context.isSummarySurface !== undefined
              ? { isSummarySurface: context.isSummarySurface }
              : {}),
        };
      } else {
        const derivedProvenance = !hasTargetContextConflict(source, context)
          ? deriveScrubScenarioContext(source, {
              ...(context.siteId !== undefined ? { siteId: context.siteId } : {}),
              ...(context.reportId !== undefined ? { reportId: context.reportId } : {}),
              ...(context.revisionId !== undefined ? { revisionId: context.revisionId } : {}),
            })
          : undefined;
        if (derivedProvenance?.identityConflict) {
          suppressContextDerivation = true;
          nextContext = {
            identityConflict: true,
            ...(isSummaryDoc && context.isSummarySurface === undefined
              ? { isSummarySurface: true }
              : context.isSummarySurface !== undefined
                ? { isSummarySurface: context.isSummarySurface }
                : {}),
          };
        } else {
          nextContext = {
            ...(docSiteId && context.siteId === undefined ? { siteId: docSiteId } : {}),
            ...(derivedProvenance?.reportId !== undefined && context.reportId === undefined
              ? { reportId: derivedProvenance.reportId }
              : {}),
            ...(derivedProvenance?.revisionId !== undefined && context.revisionId === undefined
              ? { revisionId: derivedProvenance.revisionId }
              : {}),
            ...(derivedProvenance?.sisRequestedDeliveryKv !== undefined
              ? { sisRequestedDeliveryKv: derivedProvenance.sisRequestedDeliveryKv }
              : {}),
            ...(derivedProvenance?.sisRequestedDeliveryRefOrdinal !== undefined
              ? { sisRequestedDeliveryRefOrdinal: derivedProvenance.sisRequestedDeliveryRefOrdinal }
              : {}),
            ...(derivedProvenance?.sisNearbyPinRefOrdinal !== undefined
              ? { sisNearbyPinRefOrdinal: derivedProvenance.sisNearbyPinRefOrdinal }
              : {}),
            ...(derivedProvenance?.phaseWaterMethodologyProvenance !== undefined
              ? { phaseWaterMethodologyProvenance: derivedProvenance.phaseWaterMethodologyProvenance }
              : {}),
            ...(derivedProvenance?.southwestProvenance !== undefined
              ? { southwestProvenance: derivedProvenance.southwestProvenance }
              : {}),
            ...context,
            ...(isSummaryDoc && context.isSummarySurface === undefined ? { isSummarySurface: true } : {}),
          };
        }
      }
    } else if (isRoot && context === undefined) {
      const bound = getBoundScrubContext(source);
      if (bound !== undefined) {
        hadValidBoundContext = true;
        nextContext = {
          ...(docSiteId && !bound.siteId ? { siteId: docSiteId } : {}),
          ...bound,
          ...(isSummaryDoc ? { isSummarySurface: true } : {}),
        };
      } else {
        nextContext = deriveScrubScenarioContext(source, undefined);
      }
    } else {
      nextContext = deriveScrubScenarioContext(source, context ?? undefined);
    }
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      const current = source[key];
      const scrubbed = scrubValue(current, nextContext, false, suppressContextDerivation);
      if (scrubbed !== current) changed = true;
      next[key] = scrubbed;
    }
    const scrubbedPower =
      next.powerAnalysis && typeof next.powerAnalysis === 'object'
        ? (next.powerAnalysis as Record<string, unknown>)
        : undefined;
    const scrubbedWater =
      next.waterAnalysis && typeof next.waterAnalysis === 'object'
        ? (next.waterAnalysis as Record<string, unknown>)
        : undefined;
    const needsQueueOverride =
      typeof scrubbedPower?.interconnectionQueue === 'string'
      && /Member Project Request\b/i.test(scrubbedPower.interconnectionQueue);
    const needsDroughtOverride =
      typeof scrubbedWater?.droughtResilience === 'string'
      && scrubbedWater.droughtResilience.includes('qualitative basin exposure screen');
    if (needsQueueOverride || needsDroughtOverride) {
      const existingOverrides =
        next.headingOverrides && typeof next.headingOverrides === 'object' && !Array.isArray(next.headingOverrides)
          ? (next.headingOverrides as Record<string, string>)
          : {};
      const nextOverrides: Record<string, string> = { ...existingOverrides };
      let overridesChanged = false;
      if (needsQueueOverride && !nextOverrides.interconnectionQueue) {
        nextOverrides.interconnectionQueue = 'Load Service and Generation Interconnection Pathways';
        overridesChanged = true;
      }
      if (needsDroughtOverride && !nextOverrides.droughtResilience) {
        nextOverrides.droughtResilience = 'Drought Exposure & Water-Supply Resilience (Qualitative Basin Screen)';
        overridesChanged = true;
      }
      if (overridesChanged) {
        next.headingOverrides = nextOverrides;
        changed = true;
      }
    }
    if (changed && isRoot && hadValidBoundContext && nextContext) {
      bindValidatedScrubContext(next, nextContext);
    }
    return changed ? next : value;
  }
  return value;
}

/**
 * Deep, REFERENCE-PRESERVING scrub of every string leaf in `value` (plain
 * objects and arrays at any depth: report bodies, frozen document models,
 * decision-analysis snapshots). Returns the SAME reference when nothing
 * matched, so clean inputs stay byte- and memo-stable.
 *
 * The result is a presentation copy: a hash-bound envelope (document model,
 * analysis snapshot) that was scrubbed no longer matches its recorded content
 * hash and must never be re-verified or persisted as the source.
 */
export function scrubInternalSentinelsDeep<T>(value: T, context?: ScrubScenarioContext | null): T {
  return scrubValue(value, context, true) as T;
}

/** True when any string leaf of `value` contains an internal sentinel. */
export function containsInternalSentinelDeep(value: unknown): boolean {
  if (typeof value === 'string') return containsInternalSentinel(value);
  if (Array.isArray(value)) return value.some(containsInternalSentinelDeep);
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    return Object.values(value as Record<string, unknown>).some(containsInternalSentinelDeep);
  }
  return false;
}

/**
 * Render-time copy of a report with every internal failure sentinel replaced
 * or removed, across EVERY string leaf: top-level fields, nested typed
 * containers (`waterAnalysis.*`, `developmentPathway[].*`, …), `sections[]`,
 * `sectionContent`, and `decisionAnalysis.semantic` blocks.
 *
 * Does NOT mutate the input. Idempotent; returns the input reference when
 * nothing matched.
 */
export function scrubInternalSentinels(report: SiteRagReport): SiteRagReport {
  return scrubInternalSentinelsDeep(report);
}

/* ── Walkthrough Batch 2 Historical Accuracy & Supplemental Ledgers (#240) ── */

export interface SellerMineralClauseAttributionResult {
  originalTextPreserved: string;
  reconciledDraftText: string;
  isWrongSitePropagation: boolean;
  isWrongPartyPropagation: boolean;
  tracedDocumentId: string;
  tracedDocumentName: string;
  tracedSellerParty: string;
  tracedPurchaserParty: string;
  tracedPropertyDescription: string;
  tracedClauses: string[];
  assertsUnqualifiedLegalConclusion: false;
}

export function verifySellerMineralClauseAttribution(
  rawText: string,
  options?: { siteId?: string; attributedParty?: 'seller' | 'purchaser' | 'third-party' },
): SellerMineralClauseAttributionResult {
  const siteId = options?.siteId?.trim() ?? 'site-1780556191149-rab796';
  const isRedhead = ['site-1780556191149-rab796', '1780556191149-rab796', 'site-1774871397240-x5n10s'].includes(siteId);
  const isWrongSitePropagation = !isRedhead && /\b(?:Nichols|Little\s+Indian\s+Bayou|Book\s+4504,\s*Page\s+792|File\s*#?3465900)\b/i.test(rawText);
  const isWrongPartyPropagation =
    options?.attributedParty === 'purchaser'
    || /\bPurchaser\s+is\s+prohibited\s+from\s+surface\s+use\s+under\s+Section\s+6\(i\)/i.test(rawText)
    || /\brestricting\s+the\s+surface\s+developer\s+under\s+Book\s+4504,\s*Page\s+792\b/i.test(rawText);
  let reconciledDraftText = scrubSentinelBody(rawText, { siteId });
  if (isWrongPartyPropagation && !reconciledDraftText.includes('[WRONG-PARTY ATTRIBUTION:')) {
    reconciledDraftText = `${reconciledDraftText} [WRONG-PARTY ATTRIBUTION: Nichols POA doc-1780556635705-vv7y7f [Ref 6] §§2(C) and 6(i)–(j) prohibit Seller (Kenneth Howard Nichols, Caroline Boese Nichols, and Little Indian Bayou, LLC) — not Purchaser (Redhead Industrial Park, LLC) — from surface use or subsurface disturbance within 500 ft if the $750/acre Mineral Purchase Option is not exercised; Book 4504, Page 792, File #3465900 is an Exhibit A vesting Judgment of Possession]`;
  }
  return {
    originalTextPreserved: rawText,
    reconciledDraftText,
    isWrongSitePropagation,
    isWrongPartyPropagation,
    tracedDocumentId: 'doc-1780556635705-vv7y7f',
    tracedDocumentName: 'Redhead Industrial Park - Nichols POA (fully ex) - 2026.4.20.pdf',
    tracedSellerParty: 'Kenneth Howard Nichols, Caroline Boese Nichols, and Little Indian Bayou, LLC',
    tracedPurchaserParty: 'Redhead Industrial Park, LLC',
    tracedPropertyDescription: 'Up to 1,167.99 acres in Township 8 South, Range 9 West (Sections 11, 13, 14, 23, and 24), Calcasieu Parish, Louisiana (Exhibit A includes Judgment of Possession recorded April 5, 2022, Book 4504, Page 792, File #3465900)',
    tracedClauses: [
      'Section 2(C) Mineral Purchase Option ($750.00/acre × Seller % ownership)',
      'Section 6(i)–(j) Seller mineral surface-use and 500-ft subsurface prohibition if Option is not exercised',
      'Preliminary Title Report (doc-1780556655925-b1qlh2) Page 1 prior third-party mineral severances (e.g., Joshua Timberlands LLC in Sections 13–14)',
    ],
    assertsUnqualifiedLegalConclusion: false,
  };
}

export interface RedheadPhaseWaterTerrainLedger {
  pue: number;
  n0: {
    declaredItBucket: string;
    declaredItMinMw: number;
    declaredItMaxMw: number;
    facilityMinMw: number;
    facilityMaxMw: number;
    waterUseScreeningMgd: {
      wet: { min: number; max: number };
      hybrid: { min: number; max: number };
      dry: { min: number; max: number };
    };
  };
  n1Phases: Array<{
    phaseId: string;
    sequence: number;
    cumulativeItMw: number | null;
    cumulativeItBucket: string | null;
    componentGridTargetMw: number | null;
    incrementalItMw: number | null;
    incrementalItRangeMw: { min: number; max: number } | null;
    cumulativeFacilityMw: number | null;
    cumulativeFacilityRangeMw: { min: number; max: number } | null;
    waterScenarioScreenMgd: { wet: number; hybrid: number; dry: number } | null;
    retainedUnknowns: string[];
  }>;
  terrain: {
    elevationPointsSampled: number;
    minElevationM: number;
    maxElevationM: number;
    rawSlopePercent: number;
    elevationSource: string;
    mainBodyAndSummaryStatus: 'amber-qualified-excluded-from-scoring';
    appendixRawHistoricalPreserved: {
      rawScore: number;
      rawStatus: 'green';
      hasQualificationLabel: boolean;
    };
  };
  independentArithmeticVerified: boolean;
  noBlanketNumericReplacement: boolean;
}

export function buildRedheadPhaseWaterTerrainLedger(): RedheadPhaseWaterTerrainLedger {
  const pue = 1.18;
  const n0ItMin = 200;
  const n0ItMax = 500;
  const n0FacMin = Number((n0ItMin * pue).toFixed(1)); // 236.0
  const n0FacMax = Number((n0ItMax * pue).toFixed(1)); // 590.0
  const p2It = 350;
  const p2Fac = Number((p2It * pue).toFixed(1)); // 413.0
  const p3It = 550;
  const p3Fac = Number((p3It * pue).toFixed(1)); // 649.0
  const arithmeticOk =
    n0FacMin === 236 &&
    n0FacMax === 590 &&
    p2Fac === 413 &&
    p3Fac === 649;

  return {
    pue,
    n0: {
      declaredItBucket: '200-500mw',
      declaredItMinMw: n0ItMin,
      declaredItMaxMw: n0ItMax,
      facilityMinMw: n0FacMin,
      facilityMaxMw: n0FacMax,
      waterUseScreeningMgd: {
        wet: { min: 1.39, max: 3.48 },
        hybrid: { min: 0.153, max: 0.382 },
        dry: { min: 0.065, max: 0.163 },
      },
    },
    n1Phases: [
      {
        phaseId: 'phase-1',
        sequence: 1,
        cumulativeItMw: null,
        cumulativeItBucket: '10-50mw',
        componentGridTargetMw: 50,
        incrementalItMw: null,
        incrementalItRangeMw: { min: 10, max: 50 },
        cumulativeFacilityMw: null,
        cumulativeFacilityRangeMw: {
          min: Number((10 * pue).toFixed(1)),
          max: Number((50 * pue).toFixed(1)),
        },
        waterScenarioScreenMgd: null,
        retainedUnknowns: [
          'phase-1.capacityMW is null (range bucket 10-50mw; exact Phase 1 IT load point undeclared)',
          'sed.economicEvidence.data.waterUseScenarioScreens has no Phase 1 point-load screen',
        ],
      },
      {
        phaseId: 'phase-2',
        sequence: 2,
        cumulativeItMw: p2It,
        cumulativeItBucket: null,
        componentGridTargetMw: 350,
        incrementalItMw: 300, // over 50 MW Phase 1 grid target (never 350 MW incremental)
        incrementalItRangeMw: { min: 300, max: 340 },
        cumulativeFacilityMw: p2Fac,
        cumulativeFacilityRangeMw: null,
        waterScenarioScreenMgd: { wet: 2.43, hybrid: 0.268, dry: 0.114 },
        retainedUnknowns: [
          'Exact Phase 1->2 increment is +300 to +340 MW IT (+300 MW over 50 MW Phase 1 grid target) because Phase 1 exact IT point is undeclared',
        ],
      },
      {
        phaseId: 'phase-3',
        sequence: 3,
        cumulativeItMw: p3It,
        cumulativeItBucket: null,
        componentGridTargetMw: 350,
        incrementalItMw: 200, // 550 - 350 = +200 MW IT
        incrementalItRangeMw: { min: 200, max: 200 },
        cumulativeFacilityMw: p3Fac,
        cumulativeFacilityRangeMw: null,
        waterScenarioScreenMgd: { wet: 3.82, hybrid: 0.421, dry: 0.179 },
        retainedUnknowns: [
          '550 MW IT (649 MW facility) exceeds the N0 200–500 MW IT (236–590 MW facility) primary bucket screen and is covered only by the N1 phase scenario screen; physical water supply capacity is unverified',
        ],
      },
    ],
    terrain: {
      elevationPointsSampled: 1,
      minElevationM: 6.9,
      maxElevationM: 6.9,
      rawSlopePercent: 0,
      elevationSource: 'usgs-epqs',
      mainBodyAndSummaryStatus: 'amber-qualified-excluded-from-scoring',
      appendixRawHistoricalPreserved: {
        rawScore: 95,
        rawStatus: 'green',
        hasQualificationLabel: true,
      },
    },
    independentArithmeticVerified: arithmeticOk,
    noBlanketNumericReplacement: true,
  };
}

export interface ModelRunReceiptVerification {
  methodologyRef: number | undefined;
  methodologyCitationLabel: string | undefined;
  methodologyField: string | undefined;
  hasMethodologyCitation: boolean;
  hasExecutedPhaseWaterModelReceipt: boolean;
  phaseWaterScreeningDataKeys: string[];
  catalogScreenReceipt: {
    modelVersion: string;
    sourceCatalogVersion: string;
    modelInputHash: string;
    facilityLoadMw: number;
    pueRange: [number, number];
    utilizationRangePct: [number, number];
    dryHybridMgdRange: [number, number];
    wetTowerMgdRange: [number, number];
  } | null;
  claimQualificationStatus: 'VERIFIED_MODEL_RUN' | 'QUALIFIED_METHODOLOGY_ONLY_UNVERIFIED';
}

export function verifyModelRunReceipt(
  source: Record<string, unknown>,
  hostSiteId?: string,
  hostReportId?: string,
  hostRevisionId?: string,
): ModelRunReceiptVerification {
  const targetSiteId = hostSiteId ?? extractNonEmptyString(source.siteId);
  const targetReportId = hostReportId ?? extractReportIdFromRecord(source);
  const targetRevisionId = hostRevisionId ?? extractRevisionIdFromRecord(source);
  const refs = Array.isArray(source.references)
    ? source.references
    : Array.isArray(source.citations)
      ? source.citations
      : [];
  let methodologyRef: number | undefined;
  let methodologyCitationLabel: string | undefined;
  let methodologyField: string | undefined;
  let ashraeDisqualified = false;
  for (let i = 0; i < refs.length; i++) {
    const item = refs[i];
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const label = String(r.source ?? r.label ?? '');
    const field = String(r.field ?? '');
    if (/\bASHRAE\b/i.test(label) && /\bPsychrometrics\b/i.test(label)) {
      if (isSourceRecordDisqualified(r, targetSiteId, targetReportId, targetRevisionId)) {
        ashraeDisqualified = true;
        methodologyRef = undefined;
        methodologyCitationLabel = undefined;
        methodologyField = undefined;
        continue;
      }
      if (!ashraeDisqualified && methodologyRef === undefined) {
        methodologyRef = typeof r.number === 'number' ? r.number : i + 1;
        methodologyCitationLabel = label;
        methodologyField = field || undefined;
      }
    }
  }

  const sed = (source.sed && typeof source.sed === 'object' ? source.sed : source) as Record<string, unknown>;
  const econData =
    sed.economicEvidence && typeof sed.economicEvidence === 'object'
      ? ((sed.economicEvidence as Record<string, unknown>).data as Record<string, unknown> | undefined)
      : undefined;
  const phaseWaterRaw =
    econData?.phaseWaterScreening && typeof econData.phaseWaterScreening === 'object'
      ? (econData.phaseWaterScreening as Record<string, unknown>)
      : {};
  const phaseWaterKeys = Object.keys(phaseWaterRaw);
  const hasExecutedPhaseWaterModelReceipt = Boolean(
    typeof phaseWaterRaw.modelVersion === 'string' &&
    phaseWaterRaw.modelVersion.trim().length > 0 &&
    typeof (phaseWaterRaw.modelInputHash ?? phaseWaterRaw.inputHash) === 'string' &&
    typeof (phaseWaterRaw.runId ?? phaseWaterRaw.executedAt) === 'string' &&
    phaseWaterRaw.outputs != null,
  );

  const waterUseRaw =
    econData?.waterUseScreening && typeof econData.waterUseScreening === 'object'
      ? (econData.waterUseScreening as Record<string, unknown>)
      : undefined;
  let catalogScreenReceipt: ModelRunReceiptVerification['catalogScreenReceipt'] = null;
  if (
    waterUseRaw &&
    typeof waterUseRaw.modelVersion === 'string' &&
    typeof waterUseRaw.sourceCatalogVersion === 'string' &&
    typeof waterUseRaw.modelInputHash === 'string'
  ) {
    catalogScreenReceipt = {
      modelVersion: waterUseRaw.modelVersion,
      sourceCatalogVersion: waterUseRaw.sourceCatalogVersion,
      modelInputHash: waterUseRaw.modelInputHash,
      facilityLoadMw: typeof waterUseRaw.facilityLoadMw === 'number' ? waterUseRaw.facilityLoadMw : 1000,
      pueRange: [1.15, 1.3],
      utilizationRangePct: [50, 100],
      dryHybridMgdRange: [0.24, 1.21],
      wetTowerMgdRange: [6.14, 14.74],
    };
  }

  return {
    methodologyRef,
    methodologyCitationLabel,
    methodologyField,
    hasMethodologyCitation: methodologyRef !== undefined,
    hasExecutedPhaseWaterModelReceipt,
    phaseWaterScreeningDataKeys: phaseWaterKeys,
    catalogScreenReceipt,
    claimQualificationStatus: hasExecutedPhaseWaterModelReceipt
      ? 'VERIFIED_MODEL_RUN'
      : 'QUALIFIED_METHODOLOGY_ONLY_UNVERIFIED',
  };
}

